/**
 * voice-v8 — Push-to-Talk Recorder + Sender (pre.144).
 *
 * MediaRecorder hält das Mic offen während der User den PTT-Button drückt.
 * Beim Loslassen wird der Audio-Blob an POST /api/voice/command geschickt
 * (Whisper-STT + LLM-Tool-Loop server-seitig). Result wird im Chat als
 * User/Bot-Bubble + Tool-Cards gerendert.
 *
 * Min-Aufnahme 300ms (Schutz gegen versehentliche Klicks). Max 15s gegen
 * runaway-records (Daumen am Display vergessen).
 */

import { meldeMikroAnlass } from "./mikro-spur.js";
import {
  erzeugeAufnahmeWachter,
  type AufnahmeWachter,
  type AusfallInfo,
  type RueckkehrInfo,
} from "./ptt-wachter.js";
import { beobachteSpurEnde, installiereRouteMessung } from "./route-messung.js";
import { wiedergabeJetzt } from "./wiedergabe-stand.js";
import { empfaengerWahl } from "./empfaenger-wahl.js";
import { ausklangBeenden } from "./tts-pcm.js";
import { letzterVorstartGrund, merkeMikroOeffnung } from "./mikro-vorstart.js";
import { meldeRecBereit, recBereitZuruecksetzen } from "./rec-bereit.js";
// 30.09.2026 (Ticket 2634): der Sende-Hänger — Nachfrage, kurzer Wachhund, Messzeile.
import { ankerAus, fragVerlauf, starteSendeWache, UNTERBROCHEN_TEXT_GESPEICHERT } from "./ptt-haenger.js";
import { TAKT_MIN_ANTEIL, TAKT_STEHT_AB_MS, type TaktProbe } from "./rechentakt.js";
import {
  befundTeil,
  istStummeQuelle,
  istToterStrom,
  sollLeitungWechseln,
  STUMM_PRUEFEN_NACH_MS,
  stromMeldung,
  stummImDruck,
  verworfenMessung,
  WECHSEL_NACH_MS,
  type LeitungImDruck,
  type LeitungsWeg,
  type StromBefund,
} from "./strom-pegel.js";
// 24.09.2026 (Ticket 2536): geteilt mit dem Satz nach dem Wach-Wort.
import { base64ZuBlob } from "./base64-blob.js";
import type { EndwortAngabe } from "../../shared/sende-wort.js";
import type { FortsetzungAngabe } from "../../shared/fortsetzung.js";
import { neueAnfrageKennung } from "./fortsetzung.js";
// 29.09.2026 (Ticket 2618): lange Diktate in Teilen.
import { verbindeTeile } from "../../shared/diktat-teile.js";
import { GERAET_KOPF } from "../../shared/geraet-kennung.js";
import { liveStandAlsFeld, type LiveStand } from "../../shared/live-stand.js";
import { EIGENES_GERAET } from "./geraet-kennung.js";
import { DiktatVorlauf, TeilNichtErkannt, vorlaufVon, type TeilErkenner } from "./diktat-teile.js";
/*
 * ⚠️ 20.09.2026 (Ticket 2396) — HIER STAND EINE EIGENE KOPIE.
 *
 *    `isNativePlatform()` war eine private Funktion in dieser Datei mit zwoelf
 *    Nutzern, waehrend native-voice.ts dieselbe Frage seit jeher exportiert.
 *    Die Karten brauchen die Pruefung jetzt auch — und eine zweite Kopie
 *    daneben waere das Muster gewesen, das in diesem Projekt schon oft
 *    genug schiefgegangen ist.
 */
import { isNativePlatform } from "./native-voice.js";
// 24.09.2026 (Ticket 2202): die Regel für nativ oder Web, und für wen.
import { NATIV_SCHALTER, wegWahl, type WegGrund } from "./ptt-weg.js";
import { istHauptnutzer } from "./hauptnutzer.js";
import { loescheFertig, merkeSchmiedFolgt, merkeZug, zugBeginnt, zugZuEnde } from "./zug-ende.js";
import { meldeSchmiedAngesprochen } from "./schmied-warten.js";
import { meldeZusatzAngesprochen } from "./adressat-vermerk.js";
/*
 * ⚠️ `wiedergabeHaltStand` ist der LESENDE Zugriff auf den Ton-Stand und
 *    fasst nichts an.
 *
 * ⚠️ 23.09.2026 (Ticket 2524) — dazu genau EIN schreibender Aufruf:
 *    `setSeitenAufnahme`. Hier stand, ein schreibender Import zurück wäre ein
 *    Zirkel. Ein Zirkel der MODULE ist es nicht (playback-hifi.ts importiert
 *    diese Datei nicht); der Kreis liegt in den Ereignissen: playback-hifi.ts
 *    sendet `voice-v8:playback-start/-end`, und diese Datei hört darauf. Das
 *    Lösen der Sperre kann so ein Ereignis SYNCHRON auslösen — darum steht
 *    es im Aufräumen erst NACH der Entscheidung über das warme Mikro.
 *    Warum es den Aufruf überhaupt braucht: nur diese Datei weiß, wann die
 *    Seite aufnimmt, und 12:57:20 wurde die Tonsitzung mitten darin
 *    umgeschaltet (54 s ohne Ton).
 */
import {
  letzterWiedergabeWeg,
  setSeitenAufnahme,
  warteAufAufnahmeSitzung,
  wiedergabeHaltStand,
} from "./playback-hifi.js";
import { aufnahmeKennung } from "./aufnahme-kennung.js";

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
] as const;

// Pre.489: 15s → 5min für echte Sprachnachrichten. Server-Limit ist 25 MB,
// das sind bei 64 kbps Opus ca. 50 Minuten — 5min ist gemütlich darunter.
// User-Beschwerde 2026-05-28: "Geht nur ein paar Sekunden, dann hängt's."
// Plus: Modal hört jetzt auf onAutoStop-Event statt blind weiterzulaufen.
//
// Ticket 2263 (14.09.2026, Martin früh): 5min brach ihm zweimal hintereinander
// mitten im Satz ab, ohne jede Vorwarnung. 5min → 15min:
// - Grössenlimit (25 MB / 64 kbps) erlaubt weiterhin ~50min — bleibt gemütlich
//   drunter.
// - Die einzige andere gemessene Grenze (07.09.: Soniox live-WS deckelt bei
//   180s ohne Fehlermeldung) betrifft NUR die live mitlesende Vorschau
//   (live-dictation.ts) — das ENDGÜLTIGE Transkript kommt serverseitig aus
//   der ganzen Audiodatei (Groq Whisper, siehe voice-command.ts), unabhängig
//   von dieser 180s-Eigenheit. Eine laengere Aufnahme verliert also KEINEN
//   Text — nur die live sichtbare Mitschrift kann nach 180s stehen bleiben.
// - 15min matcht TURN_HARD_CAP_MS (claude-sdk-session.ts) — bereits an
//   anderer Stelle als vernünftige Obergrenze etabliert.
// Dazu: Vorwarnung 30s vor Schluss (siehe ptt-modal.ts, PTT_WARNUNG_VOR_MS)
// und ein sanfteres Auto-Stop-Ende statt der bisherigen stillen zwei
// Haptik-Tipser.
//
// ⚠️ 25.09.2026 (Ticket 2618, Albert im Auto): 15 → 25 min. Dreimal an einem
// Nachmittag (15:44, 16:02, 16:38) endete sein Diktat genau bei 900 s, und er
// sprach weiter — trotz Vorwarnung und Ton. Die „~50 min" oben stimmen nicht:
// gemessen kommen 15 min als 11,1 MB an (≈ 99 kbit/s, nicht 64). 25 min sind
// ≈ 18,6 MB, unter der Server-Grenze von 25 MB (MAX_AUDIO_BYTES) mit Luft;
// 30 min wären ≈ 22 MB und zu knapp.
//
// ⚠️ 29.09.2026 (Ticket 2618): Seitdem ist das die Länge EINES TEILS, kein
// Ende mehr. Am 28.09. schnitt die Grenze wieder mitten im Reden (1.527 s).
// Höher geht es nicht: vor dem Server nimmt nginx je Anfrage nur 20 MB an
// (SERVER_ANNAHME_MB). Darum beginnt an der Grenze nahtlos ein neuer Teil
// auf derselben Leitung, und alle Teile landen als EINE Nachricht
// (lib/diktat-teile.ts). Ein Ende bleibt nur, wo kein Wechsel geht — auf
// dem nativen Weg oder wenn auch der letzte Versuch keinen Rekorder bekam,
// der liefert (PttRecorder.wechsleTeil).
export const PTT_MAX_MS = 25 * 60_000;
/**
 * Ticket 2618: so lange vor der Höchstdauer eines Teils sagt die Karte,
 * dass gleich ein neuer Teil beginnt („z. B. ab 23 min").
 */
export const PTT_TEIL_HINWEIS_VOR_MS = 2 * 60_000;
/**
 * Ticket 2618 (Nachbesserung 29.09.): so lange VOR der Höchstdauer beginnt
 * der Wechsel in den neuen Teil — Zeit für weitere Versuche, falls der
 * neue Rekorder nicht liefert, und zuletzt für den Versuch nacheinander.
 */
export const PTT_TEIL_WECHSEL_VOR_MS = 60_000;
/** Ticket 2618: Abstand der Versuche, solange der neue Rekorder nicht lieferte. */
const TEIL_WIEDER_MS = 20_000;
/** Ticket 2618: so lange hat ein neuer Rekorder, um zu zeigen, dass er liefert. */
const TEIL_ANLAUF_MS = 3_000;
/** Ticket 2618: wie oft währenddessen nachgesehen wird. */
const TEIL_PRUEF_TAKT_MS = 100;
/**
 * Ticket 2618: so viele Zeitscheiben mit Inhalt belegen, dass der neue
 * Rekorder wirklich mitschneidet — die erste allein könnte nur der
 * Dateikopf sein.
 */
const TEIL_BELEG_SCHEIBEN = 2;
/**
 * Ticket 2618: so lange nehmen alter und neuer Rekorder an der Teilgrenze
 * mindestens GEMEINSAM auf. Der neue braucht einen Augenblick, bis er
 * wirklich mitschneidet; eine Lücke verlöre Worte, die Überlappung doppelt
 * höchstens eine Silbe am Schnitt.
 */
const TEIL_UEBERLAPPUNG_MS = 500;

/** Ticket 2618: ein Rekorder, der läuft, aber noch nicht der Aufnahme gehört. */
interface NeuerRekorder {
  rec: MediaRecorder;
  /** SEINE Liste — nur Zeitscheiben mit Inhalt. */
  teile: Blob[];
  /** Wann er gestartet wurde. */
  seit: number;
}

/** Ticket 2618: ein gelungener Wechsel in einen neuen Teil. */
interface TeilWechsel {
  /** Der fertige Teil — null, wenn er nichts trug. */
  teil: Blob | null;
  art: "neben" | "nacheinander";
  /** Nacheinander: wie lange zwischen dem Ende des alten und dem Start des neuen lag. */
  luecke: number;
}

/**
 * Ticket 2618 (Nachbesserung 29.09.): der laufende Teil, wie die Karte ihn
 * braucht (PttRecorder.teilLage).
 */
export interface TeilLage {
  /** Geht die Aufnahme an der Höchstdauer in einen neuen Teil über? */
  teilt: boolean;
  /** Der wievielte Teil läuft (ab 1). */
  nr: number;
  /** Seit wann DIESER Teil läuft — die Höchstdauer gilt je Teil. */
  seitMs: number;
}
/** Ticket 2263: ab wann der Timer im Modal vorwarnt (dezenter Amber-Puls,
 *  kein Ton per Default — dasselbe Muster wie .voice-live-timer.timer-
 *  refresh-soon). */
export const PTT_WARNUNG_VOR_MS = 30_000;

export interface PttToolCall {
  tool: string;
  callId: string;
  isError: boolean;
  input: Record<string, unknown>;
  result: unknown;
}

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  for (const c of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported?.(c)) return c;
    } catch {
      /* fall through */
    }
  }
  return "audio/webm";
}

/**
 * Der Name des Mikrofons, kurz und ohne Leerzeichen. (10.09.2026)
 *
 * ⚠️ Leer ist ein gueltiges Ergebnis: Ohne erteilte Berechtigung geben
 *    Browser keinen Namen heraus. Dann steht `mik=?` da — das ist ehrlicher
 *    als eine erfundene Angabe.
 */
function geraeteName(stream: MediaStream | null): string {
  const roh = stream?.getAudioTracks()[0]?.label ?? "";
  const kurz = roh.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "");
  return kurz ? kurz.slice(0, 24) : "?";
}

// Pre.834: geteilter warmer Mikro-Stream (siehe oben).
let warmStream: MediaStream | null = null;
let warmStreamTimer: number | null = null;
/*
 * ⚠️ 14.09.2026 (Ticket 2202) — WARUM war das Mikro kalt?
 *
 * An 244 gemessenen Starts belegt:
 *     kalt  n=207  Median 355 ms  Max 2806 ms
 *     warm  n= 27  Median  79 ms  Max  182 ms
 * ALLE 17 langsamen Starts (>=800 ms) waren kalt. KEIN einziger warmer Start
 * war je langsam. Das Geraet ist nicht die Frage — der kalte Pfad ist es.
 * (Heute um 14:37 und 14:38 kamen zwei langsame Starts im Abstand von 77
 * Sekunden auf ZWEI VERSCHIEDENEN Geraeten, iPhone-Mikrofon und FORD-AUDIO.
 * Damit ist die Bluetooth-Vermutung vom Vormittag widerlegt.)
 *
 * Warm greift nur bei 11 % der Starts. Warum nicht oefter, sagt die Messzeile
 * bisher NICHT — sie kennt nur "cold". Drei Bedingungen kommen in Frage
 * (native App, keine Tagesaufnahme, keine Wiedergabe), dazu das 45-Sekunden-
 * Fenster. Welche davon greift, ist der Unterschied zwischen einer Reparatur
 * und einem Schuss ins Blaue.
 *
 * Deshalb merkt sich das Modul, warum das warme Mikro zuletzt weg war, und
 * die Messzeile traegt es mit (`kalt=`, `kaltSeit=`, `nativ=`).
 */
let warmGrund = "nie-gewaermt";
let warmGrundAt = 0;
function merkeWarmGrund(grund: string): void {
  warmGrund = grund;
  warmGrundAt = typeof performance !== "undefined" ? performance.now() : 0;
}

// 2026-07-09 (Audit-Verdikt, Telemetrie): Zeitmarken je PTT-Startphase.
// EINE kompakte [ptt-t]-Zeile pro Druck — Konsole UND Beacon an den Server
// (journalctl), damit beweisbar ist, welche Phase die Zeit frisst.
export const pttTiming = {
  t0: 0,
  marks: [] as Array<[string, number]>,
  extra: {} as Record<string, string>,
  /**
   * @param ausloeserZeit performance.now() beim EINTRITT in den Handler.
   *
   * ⚠️ 10.09.2026 (Ticket 2202) — der Nullpunkt bleibt, wo er war.
   *
   * Die Gegenprobe zur Mikrofon-Untersuchung zeigte: Zwischen Martins Finger
   * und dieser Zeile liegen ungemessene Schritte (haengende Anruf-Flags
   * heilen, Einstellungen lesen, die laufende Sprachausgabe wirklich
   * stoppen). Den Nullpunkt zu verschieben waere die naheliegende Antwort —
   * sie macht aber jede Zahl der letzten zwei Monate unvergleichbar.
   *
   * Also steht der Vorlauf DANEBEN, als eigene Angabe `vor`. Die Reihe
   * bleibt lesbar, und die Luecke ist zum ersten Mal sichtbar.
   */
  begin(ausloeserZeit?: number): void {
    this.t0 = performance.now();
    this.marks = [];
    this.extra = {};
    if (typeof ausloeserZeit === "number" && ausloeserZeit > 0) {
      this.extra.vor = `+${Math.round(this.t0 - ausloeserZeit)}ms`;
    }
  },
  mark(name: string): void {
    if (this.t0 > 0)
      this.marks.push([name, Math.round(performance.now() - this.t0)]);
  },
  set(k: string, v: string): void {
    this.extra[k] = v;
  },
  /**
   * Welchen Weg dieser Druck nahm — für die Zeilen NACH dem Start.
   * (24.09.2026, Ticket 2202)
   *
   * `[ptt-ende]` und `[ptt-text]` tragen dieselben drei Felder wie `[ptt-t]`,
   * damit sich jede Zeile allein nach `weg=` trennen lässt. Ohne sie müsste
   * man Ende und Wartezeit über die Uhrzeit dem Start zuordnen — genau die
   * Rekonstruktion, die am 16.09. je Druck von Hand nötig war.
   *
   * Leer, solange dieser Druck keinen Weg gewählt hat.
   */
  wegZeile(): string {
    const { weg, tages, wahl } = this.extra;
    return weg ? `weg=${weg} tages=${tages ?? "?"} wahl=${wahl ?? "?"}` : "";
  },
  flush(): void {
    if (this.t0 === 0) return;
    const parts = this.marks.map(([n, ms]) => `${n}=+${ms}ms`);
    const extras = Object.entries(this.extra).map(([k, v]) => `${k}=${v}`);
    const line = `[ptt-t] ${[...parts, ...extras].join(" ")}`;
    console.log(line);
    try {
      const payload = JSON.stringify({ line });
      let sent = false;
      try {
        sent = navigator.sendBeacon?.("/api/debug/ptt-timing", payload) ?? false;
      } catch {
        sent = false;
      }
      if (!sent) {
        void fetch("/api/debug/ptt-timing", {
          method: "POST",
          body: payload,
          headers: { "Content-Type": "text/plain" },
          keepalive: true,
          credentials: "include",
        }).catch(() => undefined);
      }
    } catch {
      /* Telemetrie best-effort */
    }
    this.t0 = 0;
  },
};

// Zeitpunkt des letzten TTS-Endes (Overlap-Diagnose in der Telemetrie).
let lastTtsEndAt = 0;

// 2026-07-09 (Audit Wurzel #2): SINGLE-FLIGHT getUserMedia — warmUpMic und
// PttRecorder.start teilen sich EINE Mikro-Anfrage. Vorher konnten beim
// Dazwischenreden ZWEI parallele getUserMedia laufen (Vorwaermung + PTT),
// die iOS serialisiert = Sekunden.
let micAcquire: Promise<MediaStream> | null = null;
/*
 * ⚠️⚠️ 22.09.2026 — WELCHEN STROM BENUTZT DIE SPRECHTASTE GERADE?
 *
 * Die Vorwaermung holt das Mikrofon ueber dieselbe Single-Flight-Anfrage
 * wie ein PTT-Start. Kommt der Druck waehrend des `await` dazwischen, laesst
 * die Vorwaermung ihren Strom fallen — richtig, wenn es DERSELBE ist (PTT
 * hat ihn uebernommen), ein Leck, wenn es ein anderer war: niemand haelt
 * dann noch eine Referenz, niemand kann ihn schliessen, und das Mikrofon
 * bleibt offen, bis der Reiter geschlossen wird.
 *
 * Blind `stop()` waere falsch — das riss der Sprechtaste den Strom unter
 * den Fuessen weg. Also wird verglichen.
 */
let aktiverPttStrom: MediaStream | null = null;
// 2026-07-10 (Martin, "Hoere zu geht nicht" — dev=iPhone-Mikrofon, peak 0.001):
// Chrome am Mac greift gern das Continuity-iPhone-Mikro, das gesperrt nur
// Stille liefert. Am NICHT-nativen Geraet waehlen wir deshalb SELBST das
// eingebaute Mikrofon, statt Chrome die Wahl zu lassen. Nativ (iOS-App)
// bleibt unangetastet — dort IST das iPhone-Mikro das richtige.
let preferredMicId: string | null | undefined;
/**
 * Ist dieses Mikrofon hier ein FREMDES? (22.09.2026)
 *
 * ## Woher das kommt
 *
 * An zwei Stellen wird ein warmer Strom verworfen, wenn sein Name „iphone"
 * oder „continuity" enthaelt — mit der Begruendung von 2026-07-10: Chrome am
 * Mac greift gern das Continuity-iPhone, das gesperrt nur Stille liefert.
 * *„Am Desktop verworfen"*, sagt der Kommentar.
 *
 * ⚠️ Die BEDINGUNG sagt aber nicht „am Desktop", sondern `!isNativePlatform()`
 *    — also in JEDEM Browser. Auf einem iPhone im Safari ist das
 *    iPhone-Mikrofon das einzige und das richtige. Dort wirft der Waechter
 *    genau den Strom weg, den er schuetzen soll.
 *
 * Bisher ist das nie aufgefallen, weil der warme Strom im Browser ohnehin
 * nach jedem Druck verlorenging (0 von 149 Starts warm, siehe pre.3156).
 * Mit dieser Reparatur wird der Waechter zum ersten Mal wirklich scharf.
 *
 * ## Die Regel — sie steht schon nebenan
 *
 * `pickBuiltinMicId` entscheidet dieselbe Frage bereits richtig: *„Nur
 * eingreifen, wenn es eine gute Alternative gibt."* Genau das fehlte dem
 * Waechter. Ein Continuity-iPhone ist nur dann falsch, wenn daneben ein
 * eingebautes Mikrofon liegt.
 */
function istFremdesGeraet(label: string | undefined): boolean {
  if (!label || !/iphone|continuity/i.test(label)) return false;
  if (isNativePlatform()) return false;
  /*
   * ⚠️ Die Alternative ist der Punkt. Steht `preferredMicId` auf einer
   *    Kennung, hat die Geraetewahl ein besseres Mikrofon gefunden — dann
   *    ist das iPhone hier wirklich fremd. Ist sie null (kein besseres
   *    Geraet, oder die Liste war nicht lesbar), bleibt der Strom.
   *
   * ⚠️ Kein Raten am User-Agent. Die Frage ist nicht „welches Geraet ist
   *    das?", sondern „gibt es hier etwas Besseres?" — und die beantwortet
   *    die Geraeteliste selbst.
   */
  return typeof preferredMicId === "string" && preferredMicId.length > 0;
}
async function pickBuiltinMicId(): Promise<string | null> {
  if (preferredMicId !== undefined) return preferredMicId;
  preferredMicId = null;
  try {
    if (!isNativePlatform() && navigator.mediaDevices?.enumerateDevices) {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const inputs = devs.filter((d) => d.kind === "audioinput");
      /*
       * ⚠️⚠️ 22.09.2026 — OHNE ERLAUBNIS SIND ALLE NAMEN LEER.
       *
       * Der Browser gibt Geraetenamen erst preis, nachdem einmal eine
       * Mikrofon-Erlaubnis erteilt wurde. Vorher liefert
       * `enumerateDevices()` zwar die Liste, aber mit leeren Labels — und
       * die ganze Auswahl unten arbeitet mit Labels.
       *
       * Das Ergebnis waere `null` („keine bessere Alternative") und wuerde
       * FUER IMMER gecacht: `preferredMicId !== undefined` bricht jede
       * weitere Ermittlung ab. Am MacBook mit angestecktem
       * Continuity-iPhone bliebe damit das stille iPhone-Mikro gewaehlt —
       * genau der Fall, fuer den die Auswahl am 10.07. gebaut wurde.
       *
       * Vorher fiel das nicht auf, weil die Ermittlung erst beim ersten
       * Druck lief — da stand die Erlaubnis schon. Seit pre.3151 waermt
       * der Browser aber beim Seitenstart vor, und der trifft genau den
       * Moment davor.
       *
       * ⚠️ Kein Ergebnis ist besser als ein falsches: `undefined` heisst
       *    „noch unbekannt", und die naechste Ermittlung laeuft neu.
       */
      if (inputs.length > 0 && inputs.every((d) => !d.label)) {
        preferredMicId = undefined;
        return null;
      }
      const bad = /iphone|continuity/i;
      const builtin = inputs.find((d) =>
        /macbook|built-in|internes|internal/i.test(d.label),
      );
      const nonBad = inputs.find((d) => d.label && !bad.test(d.label));
      const pick = builtin ?? nonBad;
      if (pick && bad.test(pick.label) === false) {
        // Nur eingreifen, wenn es eine gute Alternative gibt.
        const def = inputs.find(
          (d) => d.deviceId === "default" || d.deviceId === "",
        );
        const defaultIsBad = def ? bad.test(def.label) : false;
        // Auch ohne erkennbaren Default: explizit das eingebaute waehlen ist
        // am Mac immer die sichere Wahl fuer die Sprachaufnahme.
        preferredMicId = defaultIsBad || builtin ? pick.deviceId : null;
      }
      // Diagnose (Martin, iPhone-Mikro-Saga): einmal pro Ermittlung melden,
      // welche Eingaenge existieren und was gewaehlt wurde -> journalctl.
      try {
        const labels = inputs.map((d) => d.label || "(ohne Label)").join(" | ");
        const picked =
          inputs.find((d) => d.deviceId === preferredMicId)?.label ??
          "(Browser-Default)";
        const line = `[mic-t] inputs=[${labels}] picked="${picked}"`;
        console.log(line);
        void fetch("/api/debug/ptt-timing", {
          method: "POST",
          body: JSON.stringify({ line }),
          headers: { "Content-Type": "text/plain" },
          keepalive: true,
          credentials: "include",
        }).catch(() => undefined);
      } catch {
        /* Diagnose best-effort */
      }
    }
  } catch {
    preferredMicId = null;
  }
  return preferredMicId;
}
if (typeof navigator !== "undefined" && navigator.mediaDevices?.addEventListener) {
  navigator.mediaDevices.addEventListener("devicechange", () => {
    preferredMicId = undefined; // neu ermitteln (Geraet kam/ging)
  });
  installiereRouteMessung(() => warmStream?.getAudioTracks()[0]);
}
/**
 * Haelt die Seite gerade ein Mikrofon — laeuft eine Aufnahme, wird eines
 * geholt, oder ist eines vorgewaermt? (23.09.2026, Ticket 2503)
 *
 * ⚠️ Der Wach-Wort-Lauscher im Nativen sieht getUserMedia nicht. Sein Kopf
 *    behauptete, der Rekorder-Zustand decke die Sprechtaste ab — das galt nur
 *    fuer den nativen Sprechweg, der seit 08.09. abgeschaltet ist. Zwei Haende
 *    am selben Mikrofon. Die Seite sagt es ihm jetzt selbst.
 */
export function webMikroOffen(): boolean {
  if (recordingActive || micAcquire) return true;
  if (!!warmStream && warmStream.getTracks().some((t) => t.readyState === "live")) {
    return true;
  }
  /*
   * ⚠️ 23.09.2026: Auch Leitungen, die NICHT der Sprechtaste gehoeren — das
   *    Mikro der Tiefen Frage holt sich seinen Strom ueber dieselbe Anfrage
   *    (acquireDictationStream) und schliesst ihn selbst. Bis seine Spur
   *    wirklich beendet ist, ist das Mikro belegt.
   */
  for (const s of ausgegeben) {
    if (s.getTracks().some((t) => t.readyState === "live")) return true;
    ausgegeben.delete(s);
  }
  return false;
}
/**
 * Hält die Seite ein Mikrofon für etwas, das ein Neuladen abreißen würde — das WARME Mikro der Sprechtaste zählt
 * nicht? (11.10.2026, Ticket 2669) Die Sprechtaste selbst deckt `pttState` ab; hier geht es um das Diktat der Tiefen
 * Frage und andere Leitungen, die `acquireRawMic` ausgibt. Das warme Mikro bleibt im Web 45 s offen — ließe man es
 * zählen, lüde „Seite neu laden“ dort nie.
 */
export function mikroOffenOhneWarmes(): boolean {
  if (recordingActive || micAcquire) return true;
  for (const s of ausgegeben) {
    if (s === warmStream) continue;
    if (s.getTracks().some((t) => t.readyState === "live")) return true;
    ausgegeben.delete(s);
  }
  return false;
}
/** Jede Leitung, die acquireRawMic je ausgegeben hat — bis sie endet. */
const ausgegeben = new Set<MediaStream>();
let webMikroGemeldet: boolean | null = null;
/** Meldet einen WECHSEL (nicht jeden Aufruf) als `voice-v8:web-mikro`. */
export function meldeWebMikro(): void {
  const offen = webMikroOffen();
  if (offen === webMikroGemeldet) return;
  webMikroGemeldet = offen;
  try {
    window.dispatchEvent(new CustomEvent("voice-v8:web-mikro", { detail: { offen } }));
  } catch {
    /* kein window (Tests) */
  }
}

function acquireRawMic(): Promise<MediaStream> {
  if (micAcquire) return micAcquire;
  micAcquire = (async () => {
    const devId = await pickBuiltinMicId();
    const strom = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...(devId ? { deviceId: { exact: devId } } : {}),
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
    ausgegeben.add(strom);
    return strom;
  })().finally(() => {
    micAcquire = null;
    meldeWebMikro();
  });
  // ⚠️ VOR getUserMedia melden — der Lauscher muss den Eingang freigeben,
  //    bevor die Seite ihn nimmt, nicht danach.
  meldeWebMikro();
  return micAcquire;
}
// 2026-07-08 (Martin, "Ton mal leise"): laeuft gerade eine echte Aufnahme?
// Dann den warmen Stream NICHT freigeben (das waere die aktive Aufnahme).
let recordingActive = false;

/* ═══ DIE STROM-WACHE (Ticket 2494) ═══════════════════════════════════════
   Liest waehrend der Aufnahme MIT, rein lesend, auf einer eigenen
   Analyser-Kette. Sie greift nie in die Aufnahme ein — dieselbe Bauart wie
   die Pegelbalken im Modal (#311-Saga: die Aufnahme-Kette bleibt unberuehrt).

   Zweck: einen Strom erkennen, der gar nichts liefert. Begruendung und
   Messreihe in strom-pegel.ts.
   ═══════════════════════════════════════════════════════════════════════ */
let wacheKontext: AudioContext | null = null;
/**
 * ⚠️⚠️ 22.09.2026, TICKET 2501 — DIE KOPIE MUSS AUFGEHOBEN WERDEN.
 *
 * Hier stand `spur.clone()` nur inline im Aufruf. Der Klon war damit
 * nirgends greifbar — und einen AudioContext zu schliessen STOPPT KEINE
 * TONSPUR. Nach jeder Aufnahme blieb also ein lebendes Mikrofon offen.
 *
 * Martin hat es am Bluetooth gemerkt, wo es am meisten weh tut: ein offenes
 * Mikro zwingt die Verbindung ins Telefonprofil (HFP statt A2DP), und die
 * Antwort klingt „wie eine Telefonverbindung statt sauber". Gemeldet aus
 * dem Auto, mik=FORD-AUDIO.
 *
 * ⚠️ Das war MEINE Regression aus pre.3166 — eingebaut, um einen toten
 *    Strom zu erkennen, und dabei selbst einen Strom offen gelassen. Wer
 *    eine Spur klont, muss sie auch stoppen.
 */
let wacheSpur: MediaStreamTrack | null = null;
let wacheTakt: number | null = null;
let wacheSpitze = 0;
let wacheSeit = 0;
/*
 * 24.09.2026 (Ticket 2518): Wie stand der Kontext der Wache? Auf dem kalten
 * Weg sah sie sechsmal Null, während der Recorder 190 kB bis 5,8 MB lieferte.
 * Ein Kontext, der außerhalb der Fingergeste entsteht, bleibt auf iOS
 * angehalten und liest nur Stille — ob DAS die Fehlalarme erklärt, ist nicht
 * belegt. Darum nur mitschreiben; entschieden wird darauf nichts.
 */
let wacheKontextErst: string | null = null;
let wacheKontextLief = false;
/**
 * Ticket 2634: wie weit Wanduhr und Kontext-Uhr gelaufen sind — nur über
 * Takte, in denen der Kontext zweimal hintereinander „running" meldete. Eine
 * Pause dazwischen (angehalten) zählt nicht als stehender Takt.
 */
let wacheUhrLetzte: TaktProbe | null = null;
let wacheUhrWandMs = 0;
let wacheUhrKontextMs = 0;

/** Was die Wache bis jetzt gesehen hat — ohne sie anzuhalten. */
function stromWacheStand(): Omit<StromBefund, "bytes" | "weg"> {
  return {
    spitze: wacheSpitze,
    dauerMs: wacheSeit ? Date.now() - wacheSeit : 0,
    kontextErst: wacheKontextErst ?? "-",
    kontextLief: wacheKontextLief,
    ...(wacheUhrWandMs >= TAKT_STEHT_AB_MS
      ? { taktLief: wacheUhrKontextMs >= wacheUhrWandMs * TAKT_MIN_ANTEIL }
      : {}),
  };
}

function beendeStromWache(): Omit<StromBefund, "bytes" | "weg"> {
  if (wacheTakt !== null) {
    clearInterval(wacheTakt);
    wacheTakt = null;
  }
  const befund = stromWacheStand();
  wacheSeit = 0;
  wacheSpitze = 0;
  wacheKontextErst = null;
  wacheKontextLief = false;
  wacheUhrLetzte = null;
  wacheUhrWandMs = 0;
  wacheUhrKontextMs = 0;
  // ⚠️ ZUERST die Spur, dann den Kontext: das Schliessen des Kontexts
  //    stoppt sie nicht, und nur sie haelt das Mikrofon offen.
  try {
    wacheSpur?.stop();
  } catch {
    /* schon gestoppt */
  }
  wacheSpur = null;
  try {
    void wacheKontext?.close();
  } catch {
    /* schon zu */
  }
  wacheKontext = null;
  return befund;
}

function starteStromWache(stream: MediaStream): void {
  beendeStromWache();
  try {
    const Ctx: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    wacheKontext = ctx;
    const knoten = ctx.createAnalyser();
    knoten.fftSize = 512;
    /*
     * ⚠️ 26.09.2026 (Ticket 2643): in Gleitkomma, nicht in 8 Bit. In 8 Bit
     *    liest leises Raumrauschen (unter ~-42 dBFS) als exakt 0 — ein
     *    stiller Raum wäre von einer stummen Quelle nicht zu unterscheiden.
     *    In Gleitkomma ist nur echte digitale Stille exakt 0.
     *    Kann der Knoten das nicht, gibt es keine Wache (fail-open): eine
     *    Wache, die nur Nullen sähe, würde gesunde Leitungen wegwerfen.
     */
    if (typeof knoten.getFloatTimeDomainData !== "function") return;
    /*
     * ⚠️ Auf einer KOPIE der Spur, nicht auf dem Original: sonst haengt die
     *    Aufnahme-Kette an diesem Kontext, und sein Schliessen reisst sie
     *    mit. Genau daran ist die Pegelanzeige frueher gescheitert.
     */
    const spur = stream.getAudioTracks()[0];
    if (!spur) return;
    const kopie = spur.clone();
    wacheSpur = kopie;
    ctx.createMediaStreamSource(new MediaStream([kopie])).connect(knoten);
    const puffer = new Float32Array(knoten.fftSize);
    wacheSeit = Date.now();
    wacheSpitze = 0;
    // Eigener Takt statt Bildschirm-Takt: die Frage haengt an Zeit, nicht
    // an Bildern — und sie muss auch im Hintergrund weiterlaufen.
    wacheTakt = window.setInterval(() => {
      wacheKontextErst ??= ctx.state;
      if (ctx.state === "running") {
        wacheKontextLief = true;
        const probe: TaktProbe = { wandMs: performance.now(), kontextS: ctx.currentTime, laeuft: true };
        if (wacheUhrLetzte) {
          wacheUhrWandMs += probe.wandMs - wacheUhrLetzte.wandMs;
          wacheUhrKontextMs += (probe.kontextS - wacheUhrLetzte.kontextS) * 1000;
        }
        wacheUhrLetzte = probe;
      } else {
        wacheUhrLetzte = null;
      }
      knoten.getFloatTimeDomainData(puffer);
      let spitze = 0;
      for (const w of puffer) {
        const a = Math.abs(w);
        if (a > spitze) spitze = a;
      }
      if (spitze > wacheSpitze) wacheSpitze = spitze;
    }, 100);
  } catch {
    // Fail-open: ohne Wache laeuft alles wie bisher weiter.
    wacheKontext = null;
  }
}

/**
 * Nach jeder Aufnahme: hat die Leitung getragen? Gibt `true` zurueck, wenn
 * sie tot war — dann darf sie NICHT warm gehalten werden, sonst erbt der
 * naechste Druck dieselbe Stille.
 *
 * ⚠️⚠️ 23.09.2026, TICKETS 2518/2519 — HIER STAND DIE FREIGABE SELBST, UND
 * SIE TAT NIE ETWAS.
 *
 * `releaseWarmMic` kehrt in seiner ersten Zeile zurueck, solange
 * `recordingActive` steht — und diese Pruefung laeuft (richtig) VOR
 * `cleanup()`, also genau dann. Die tote Leitung blieb, und `cleanup()` hielt
 * sie anschliessend sogar warm. Im Protokoll von heute, 09:12–09:13:
 *
 *   ptt-stiller-strom dauer=471ms spitze=0     → zu-klein bytes=5
 *   … viermal derselbe warme Weg …             → zu-klein bytes=5
 *   ptt-stiller-strom dauer=41639ms spitze=0   → zu-klein bytes=5
 *
 * Martin: „Ich habe nichts gehört, bitte erneut aufnehmen" — fünfmal — und
 * „eine Minute gesprochen, nichts aufgenommen". Der Waechter-Test war die
 * ganze Zeit gruen: er pruefte, DASS `releaseWarmMic(` hier steht, nicht, ob
 * es greift.
 *
 * Jetzt: hier nur der Befund (die Wache misst nur bis cleanup), die Freigabe
 * macht der Aufrufer NACH cleanup().
 *
 * ⚠️ 24.09.2026 (Ticket 2518): Tot ist die Leitung erst, wenn AUCH der
 *    Recorder nichts geliefert hat. Sechs Wache-Nullen auf dem kalten Weg
 *    kamen mit 190 kB bis 5,8 MB an — sie melden sich jetzt als
 *    `ptt-wache-null` und geben nichts frei.
 */
function pruefeStromNachAufnahme(
  bytes: number,
  weg: LeitungsWeg,
  strom: MediaStream | null,
): boolean {
  const befund: StromBefund = { ...beendeStromWache(), bytes, weg };
  const meldung = stromMeldung(befund);
  // Ticket 2643: nach dem Druck gilt die stumme Quelle wie eine tote
  // Leitung — sie wird nicht warm gehalten. Mit Diagnose der Spur.
  const stumm = istStummeQuelle(befund);
  if (meldung) meldeMikroAnlass(meldung, stumm ? spurDiagnose(strom) : "");
  return istToterStrom(befund) || stumm;
}

/**
 * Was die Spur selbst über sich sagt — bei jeder stummen Quelle eine Zeile
 * lang. (26.09.2026, Ticket 2643) Ob `muted`, `readyState` oder die
 * Abtastrate die Nullen ankündigen, ist nicht belegt; genau das sollen diese
 * Felder zeigen. ⚠️ Keine `deviceId`: sie kennzeichnet das Gerät dauerhaft.
 */
function spurDiagnose(strom: MediaStream | null): string {
  const spur = strom?.getAudioTracks()[0];
  if (!spur) return "spur=keine";
  let rate = "?";
  let kanaele = "?";
  try {
    const e = spur.getSettings?.() ?? {};
    if (e.sampleRate) rate = String(e.sampleRate);
    if (e.channelCount) kanaele = String(e.channelCount);
  } catch {
    /* Einstellungen sind Kür */
  }
  return (
    `muted=${spur.muted ? "ja" : "nein"} ready=${spur.readyState} ` +
    `enabled=${spur.enabled ? "ja" : "nein"} rate=${rate} kanaele=${kanaele}`
  );
}

/* ═══ LEITUNGSWECHSEL IM DRUCK (Ticket 2518) ═══════════════════════════════
   Wer den Strom der Sprechtaste mitliest (Live-Diktat, Pegel, Pausen-
   Erkennung), erfährt hier, dass die tote warme Leitung mitten im Druck
   gegen eine frische getauscht wurde — und liest auf der neuen weiter,
   statt ein zweites Mikrofon zu öffnen.
   ═══════════════════════════════════════════════════════════════════════ */
/** Eine Liste von Hörern: anmelden (gibt die Abmeldung zurück) und rufen. */
function hoererListe<A extends unknown[]>(): {
  hoere: (fn: (...a: A) => void) => () => void;
  melde: (...a: A) => void;
} {
  const alle = new Set<(...a: A) => void>();
  return {
    hoere: (fn) => {
      alle.add(fn);
      return () => {
        alle.delete(fn);
      };
    },
    melde: (...a) => {
      for (const h of [...alle]) {
        try {
          h(...a);
        } catch {
          /* Ein Hörer darf den Druck nicht aufhalten. */
        }
      }
    },
  };
}

const leitungsHoerer = hoererListe<[alt: MediaStream, neu: MediaStream]>();
/** Welche Leitung eine gewechselte abgelöst hat — für Leser, die spät kommen. */
const nachfolger = new WeakMap<MediaStream, MediaStream>();

/** Meldet jeden Leitungswechsel. Gibt die Abmeldung zurück. */
export const hoereAufLeitungsWechsel = leitungsHoerer.hoere;

/**
 * Ticket 2643: auch die frische Leitung liefert nur Stille. Dann wird nicht
 * noch einmal getauscht — das Aufnahmefenster sagt es dem Menschen.
 */
const stummHoerer = hoererListe<[]>();
/** Meldet, dass die Quelle nach dem Tausch stumm blieb. Gibt die Abmeldung zurück. */
export const hoereAufStummeQuelle = stummHoerer.hoere;

/**
 * Die Leitung, die heute an Stelle von `strom` läuft. Wer sich einen Strom
 * geliehen hat und erst nach dem Wechsel zu lesen beginnt, hat die Meldung
 * verpasst — und läse sonst auf einer gestoppten Spur.
 */
export function jetzigeLeitung(strom: MediaStream): MediaStream {
  let s = strom;
  for (let n = nachfolger.get(s); n; n = nachfolger.get(s)) s = n;
  return s;
}

function meldeLeitungsWechsel(alt: MediaStream, neu: MediaStream): void {
  nachfolger.set(alt, neu);
  // Ein Leser darf den Wechsel nicht aufhalten (hoererListe fängt ab).
  leitungsHoerer.melde(alt, neu);
}

/** Wie lange die Wechselzeile auf den Schlussblock des Verworfenen wartet. */
const REST_FRIST_MS = 1000;
/** Wie lange das Loslassen auf das `stop` des Rekorders wartet, bevor es mit den vorhandenen Teilen weitergeht. */
const STOPP_FRIST_MS = 2000;
/** Steht der Rekorder schon, wartet das Loslassen nur kurz auf sein letztes Stück (Chromium). */
const NACH_STOPP_FRIST_MS = 300;
/** Wie lange das Loslassen auf einen laufenden Leitungswechsel wartet. */
const WECHSEL_FRIST_MS = 3000;

/** Auf `p` warten, höchstens `ms`: true, wenn es fertig wurde, false bei Frist. */
function abwartenMitFrist(p: Promise<unknown>, ms: number): Promise<boolean> {
  return new Promise((fertig) => {
    const t = setTimeout(() => fertig(false), ms);
    void p.then(
      () => {
        clearTimeout(t);
        fertig(true);
      },
      () => {
        clearTimeout(t);
        fertig(true);
      },
    );
  });
}

/**
 * Den Recorder einer verworfenen Leitung anhalten und zählen, was er beim
 * Stopp noch liefert. (Nachprüfung 24.09.2026, Ticket 2518)
 *
 * Vorher wurde sein Schlussblock ungesehen verworfen. Kam iOS mit der
 * ersten Zeitscheibe nur zu spät, steckte der ganze Satzanfang darin — und
 * die Zeile sah genauso aus wie bei einer toten Leitung. `null`, wenn der
 * Stopp nicht binnen REST_FRIST_MS kam.
 */
function stoppeUndZaehleRest(rec: MediaRecorder): Promise<number | null> {
  return new Promise((fertig) => {
    let rest = 0;
    const frist = setTimeout(() => fertig(null), REST_FRIST_MS);
    const ende = (): void => {
      clearTimeout(frist);
      fertig(rest);
    };
    rec.ondataavailable = (ev: BlobEvent) => {
      rest += ev.data?.size ?? 0;
    };
    rec.onstop = ende;
    try {
      if (rec.state === "inactive") ende();
      else rec.stop();
    } catch {
      ende();
    }
  });
}

/**
 * Ticket 2618: einen Rekorder anhalten und auf sein Schlussstück warten —
 * es landet in SEINER Liste (neuerRekorder). Kommt `stop` nicht, gilt nach
 * REST_FRIST_MS, was da ist.
 */
function stoppeUndWarte(rec: MediaRecorder): Promise<void> {
  return new Promise((fertig) => {
    const frist = setTimeout(fertig, REST_FRIST_MS);
    const ende = (): void => {
      clearTimeout(frist);
      fertig();
    };
    rec.onstop = ende;
    try {
      if (rec.state === "inactive") ende();
      else rec.stop();
    } catch {
      ende();
    }
  });
}

/** Ticket 2618: einen Rekorder anhalten, der nichts mehr beitragen soll. */
function stoppeStill(rec: MediaRecorder): void {
  try {
    if (rec.state !== "inactive") rec.stop();
  } catch {
    /* lief nie */
  }
}

/**
 * Die Tagesaufnahme fürs Roh-Mikro pausieren — höchstens 400 ms warten.
 * Für den kalten Start und den Leitungswechsel (Ticket 2518) derselbe Weg.
 *
 * #519: laeuft die Tagesaufnahme, ihr Mikro sauber uebernehmen (weiche
 * Pause) statt dagegen anzukaempfen — der Kampf war ein grosser Teil der
 * 5-Sekunden-Latenz.
 * 2026-07-06 (Martin: „5 Sekunden gedrückt, nichts passiert"): nie
 * unbegrenzt auf die Brücke warten — 400 ms, dann weiter.
 */
async function pausiereTagesaufnahmeFuerMikro(): Promise<void> {
  try {
    const m = await import("./native-recorder.js");
    await Promise.race([
      m.pauseDayRecorderForMic("ptt"),
      new Promise<void>((res) => window.setTimeout(res, 400)),
    ]);
  } catch {
    /* Web ohne native Huelle */
  }
}


/**
 * Warmen Mikro-Stream freigeben. Grund: solange ein Live-Mikro-Stream
 * existiert, haelt iOS die Audio-Session in playAndRecord und routet die
 * WIEDERGABE leise auf den Ohrhoerer statt den lauten Boden-Lautsprecher.
 * Waehrend des Vorlesens brauchen wir das Mikro nicht -> freigeben = laut.
 */
/**
 * Warmen Mikro-Stream UEBERNEHMEN (Ownership-Transfer) — fuers Live-Diktat:
 * sofortiger Start wie beim PTT statt 1-5s Kaltaufbau (Martin: "einfach
 * das Push-to-Talk verwenden"). Der Track wird wieder aktiviert; der
 * Rufer stoppt ihn am Ende selbst.
 */
/**
 * EINE Mikro-Leitung fuer alle Aufnahme-Wege (Martins Architektur-Ansage
 * 22.07.: "die gleiche Architektur wie Push-to-Talk verwenden"): erst das
 * vorgewaermte PTT-Mikro uebernehmen (sofort), sonst ueber die bewaehrte
 * Single-Flight-Anfrage mit Geraete-Wahl kalt holen.
 */
export async function acquireDictationStream(): Promise<MediaStream> {
  const warm = takeWarmStream();
  if (warm) return warm;
  // 2026-07-23 (Stabilisierung Teil 2): laeuft die native Tagesaufnahme,
  // pausiert sie fuers Roh-Mikro — sonst kaempfen beide um die iOS-Audio-
  // Session (Engine-Abriss-Klasse). Fortsetzung: Diktat-Stopp-Pfade +
  // 5-min-Sicherheitsnetz in native-recorder.ts.
  try {
    const m = await import("./native-recorder.js");
    await m.pauseDayRecorderForMic("ptt");
  } catch {
    /* Web ohne native Huelle */
  }
  return acquireRawMic();
}

export function takeWarmStream(): MediaStream | null {
  if (recordingActive) return null;
  if (!warmStream) return null;
  const track = warmStream.getAudioTracks()[0];
  if (track?.readyState !== "live") return null;
  if (warmStreamTimer) {
    clearTimeout(warmStreamTimer);
    warmStreamTimer = null;
  }
  const s = warmStream;
  warmStream = null;
  track.enabled = true;
  console.log("[ptt] warmStream uebernommen (diktat)");
  return s;
}

/**
 * Darf im Browser vorgewaermt werden? (22.09.2026)
 *
 * ## Warum es diese Schranke gibt
 *
 * Martin hat ausdruecklich gewuenscht, dass auch im Browser vorgewaermt wird
 * — dort sind die kalten Starts gemessen bis 13.740 ms lang, nativ hoechstens
 * 3.025 ms. Eine unabhaengige Pruefung der Aenderung fand dabei sechzehn
 * schwere Nebenwirkungen; zwei davon haetten einem zahlenden Kunden echten
 * Schaden zugefuegt:
 *
 *   1. **Ein Dialog beim Seitenladen.** Waermt die Seite beim Oeffnen vor,
 *      fragt der Browser nach dem Mikrofon. Ein einziges „Nicht erlauben"
 *      legt die Sprechtaste DAUERHAFT still — der Nutzer muesste sie in
 *      den Browser-Einstellungen suchen.
 *   2. **Die genaue Dauerortung.** `voice-v8:mikro-bedarf` faehrt in
 *      `geo-watch.ts` die Standortverfolgung mit hoher Genauigkeit hoch.
 *      Der Kommentar dort rechnet ausdruecklich damit, dass das
 *      Mikro-Signal KURZ ist („dort ist sie kurz"). Ein dauerhaft warmes
 *      Mikro macht daraus Dauerortung.
 *
 * ## Die Regel
 *
 * Nativ bleibt alles wie bisher. Im Browser wird nur vorgewaermt, wenn die
 * Erlaubnis nachweislich schon steht — dann gibt es keinen Dialog, und der
 * Nutzer hat das Mikrofon dieser Seite bereits bewusst freigegeben.
 *
 * ⚠️ Safari und iPhone-Safari kennen `permissions.query({name:"microphone"})`
 *    nicht. Genau Alberts Faelle. Dort zaehlt der zweite Weg: hat diese Seite
 *    schon einmal erfolgreich ein Mikrofon bekommen, ist die Erlaubnis da —
 *    das ist ein Beleg und keine Annahme.
 */
let erlaubnisBelegt = false;

/** Merkt sich, dass diese Seite das Mikrofon schon einmal bekommen hat. */
export function merkeMikroErlaubnis(): void {
  erlaubnisBelegt = true;
}

/** Nur fuer Tests. */
export function _setzeErlaubnisZurueck(): void {
  erlaubnisBelegt = false;
  schonGesprochen = false;
}

/**
 * Hat der Nutzer in DIESER Sitzung schon gesprochen? (22.09.2026)
 *
 * ⚠️ Unterscheidet sich von `erlaubnisBelegt`: die Erlaubnis kann aus einem
 *    frueheren Besuch stehen (`permissions.query` sagt dann „granted"),
 *    ohne dass jemand heute die Sprechtaste beruehrt hat.
 */
let schonGesprochen = false;

/**
 * Darf der SICHT-Ausloeser waermen? (22.09.2026)
 *
 * ## ⚠️ Warum dieser Ausloeser strenger ist als die anderen
 *
 * Im Browser ist der Tab-Wechsel der Normalfall, nicht die Ausnahme. Jede
 * Rueckkehr in den Vespera-Tab und jeder Zurueck-Knopf wuerde sonst das
 * Mikrofon oeffnen — auch wenn jemand nur lesen will. Damit aendert sich
 * die Grundregel von *„Mikro offen, solange ich die Taste halte"* zu
 * *„Mikro offen, solange der Tab vorne ist"*, samt dauerhaftem
 * Mikrofon-Hinweis im Tab und in der Menueleiste.
 *
 * Dieselbe Lehre steht seit dem 18.08. ein paar Zeilen weiter unten, fuer
 * den Tipp-Ausloeser: *„KEIN Warm-Tipp. pointerdown auf dem Dokument hat
 * nach jedem beliebigen Tap 12s Capture gehalten."* Der Sicht-Ausloeser ist
 * dieselbe Bauart, nur mit groesserem Fenster.
 *
 * ⚠️ Er loest nebenbei die groesste Gefahr der ganzen Aenderung: wer die
 *    Seite in einem zweiten Tab oeffnet, bekommt dort KEIN warmes Mikro —
 *    und kann damit die laufende Aufnahme des ersten Tabs nicht mehr still
 *    unterbrechen (Safari tut das bei zwei Griffen derselben Herkunft).
 *
 * Nativ bleibt es wie bisher: dort gibt es keine Tabs, keinen
 * Mikrofon-Hinweis im Fenster, und die Audio-Session wird vom System
 * verwaltet.
 */
export async function darfSichtWaermen(): Promise<boolean> {
  if (isNativePlatform()) return true;
  if (!schonGesprochen) return false;
  return darfVorwaermen();
}

export async function darfVorwaermen(): Promise<boolean> {
  if (isNativePlatform()) return true;
  if (erlaubnisBelegt) return true;
  try {
    const nav = navigator as Navigator & {
      permissions?: {
        query?: (d: { name: string }) => Promise<{ state?: string }>;
      };
    };
    const st = await nav.permissions?.query?.({ name: "microphone" });
    if (st?.state === "granted") {
      erlaubnisBelegt = true;
      return true;
    }
  } catch {
    /* Safari kennt die Abfrage nicht — dann gilt der Beleg oben. */
  }
  return false;
}

/**
 * Ticket 782/777: der Session melden, ob das Mikro gebraucht wird.
 *
 * ## ⚠️⚠️ 22.09.2026 — DIESES SIGNAL FAEHRT AUCH DIE ORTUNG HOCH
 *
 * Zwei Hoerer haengen daran, und sie wollen Verschiedenes:
 *
 *   - `playback-hifi.ts` braucht es fuer die iOS-Tonkategorie: „Mikro ist
 *     scharf, die Sitzung gehoert auf .playAndRecord."
 *   - `geo-watch.ts` startet daraufhin die STANDORTVERFOLGUNG mit hoher
 *     Genauigkeit. Der Kommentar dort rechnet ausdruecklich damit, dass das
 *     Signal kurz ist: *„Das Mikrofon-Signal (PTT, Anruf) faehrt die genaue
 *     Ortung weiterhin hoch — dort ist sie kurz."*
 *
 * Seit pre.3151 waermt auch der Browser vor. Ein VORGEWAERMTES Mikro ist
 * aber kein Sprechen — es haelt nur einen Strom offen, damit der naechste
 * Druck schnell ist. Ohne Unterscheidung wuerde daraus Dauerortung: GPS mit
 * hoher Genauigkeit, solange die Seite offen ist, und der Standort geht an
 * den Server.
 *
 * ⚠️ Die Nebenwirkung war im Diff dieser Datei nicht zu sehen. Sie stand in
 *    einer anderen Datei, hinter einem Ereignisnamen.
 *
 * Darum sagt das Signal jetzt, WARUM das Mikro gebraucht wird. Die
 * Tonkategorie interessiert das nicht — die Ortung schon.
 */
function meldeMikroBedarf(
  aktiv: boolean,
  grund: "vorwaermen" | "nutzung" = "nutzung",
): void {
  try {
    window.dispatchEvent(
      new CustomEvent("voice-v8:mikro-bedarf", { detail: { aktiv, grund } }),
    );
  } catch {
    /* kein window (Tests) */
  }
}

/**
 * Das Warm-Mikro fuer eine gleich startende Tages-Aufnahme aus dem Weg raeumen.
 *
 * 2026-08-23 (Martin: "Ich druecke auf Aufnahme, dann laeuft es 3 Sekunden,
 * steht 'pausiert'. Wenn ich auf Play druecke, springt es von 3 Sekunden auf
 * 12 — wie wenn der Timer im Hintergrund weitergelaufen waere.")
 *
 * BEWIESEN im Serverprotokoll, auf die Sekunde genau:
 *
 *   16:33:54  [mikro] ptt-warmmikro          Push-to-Talk greift zu
 *   16:33:54  Session ios-F1F9E4B2 entsteht  und stirbt sofort
 *
 *   Dasselbe um 16:41, 16:42 und 16:43 — bei jedem seiner Versuche.
 *
 * Die Engine sagt dazu selbst (PawbotRecorderEngine.swift): "iOS gibt das
 * Mikrofon nicht her, weil es gerade jemand anderes hat" — und geht nach drei
 * abgelehnten Versuchen in eine WEICHE PAUSE. Das sind Martins drei Sekunden.
 * Die Widget-Uhr laeuft davon unbeeindruckt weiter, daher der Sprung auf 12.
 *
 * WARUM DER BESTEHENDE SCHUTZ NICHT REICHTE
 *
 *   warmUpMic() prueft `dayRecorderBusy` — aber diese Sperre wird erst gesetzt,
 *   wenn `recorder-will-start` feuert, also IM Aufnahmestart. Zwischen dem Tipp
 *   aufs Widget und diesem Moment liegt der Aufbau des Overlays: ein Fenster,
 *   in dem ein pointerdown das Vorwaermen ausloest und das Mikro wegschnappt.
 *
 *   Diese Funktion schliesst das Fenster: Wer weiss, dass gleich eine
 *   Tages-Aufnahme kommt, sperrt VORHER.
 *
 * Selbstheilend: Kommt die Aufnahme doch nicht zustande, setzt das naechste
 * `recorder-state`-Ereignis die Sperre zurueck (state=idle ohne dayIntent).
 */
export function warmMikroSperrenFuerTagesaufnahme(grund: string): void {
  dayRecorderBusy = true;
  releaseWarmMic(grund);
}

export function releaseWarmMic(reason: string): void {
  if (recordingActive) return;
  merkeWarmGrund(reason);
  gibVorstartFrei(reason); // 19.10.2026: ein vorgezogenes Mikro geht mit dem warmen (neuer Beitrag, Seite weg, Anruf …)
  // ⚠️ Die Decken-Uhr gehoert zum offenen Mikro. Bleibt sie stehen, haelt
  //    das naechste frische Mikro die alte Restzeit fuer die eigene.
  warmSeit = 0;
  // Ticket 782/777: Das Warm-Mikro ist weg — wenn auch sonst nichts das
  // Mikro braucht, darf die Audio-Session auf .playback zurueck. Sonst bliebe
  // sie bis zum Beenden der App auf .playAndRecord: Mikro-Indikator dauerhaft
  // an, Aufnahme-Hardware dauerhaft bereit. playback-hifi entscheidet, ob der
  // Wechsel wirklich passiert (waehrend Wiedergabe/Anruf/Aufnahme nicht).
  //
  // 2026-09-01 (Martin, Alltag-Rekorder stirbt im Hintergrund): Die
  // Tagesaufnahme BRAUCHT das Mikro. releaseWarmMic("tages-aufnahme") hat
  // trotzdem meldeMikroBedarf(false) geschickt — die Session sollte auf
  // .playback ruhen, waehrend der native Recorder aufnimmt. Der native
  // Waechter (enterPlaybackHiFi) faengt den Wechsel oft ab, aber nicht
  // in der Luecke (WebView-Reload, Haeppchen-Naht, weiche Pause). Dann
  // faellt die Lockscreen-Aufnahme tot um.
  /*
   * ⚠️⚠️ 23.09.2026 — UND NICHT, WENN DAS WACH-WORT LAUSCHT.
   *
   * Martin mit Build 68: *„Vespera geht immer noch nicht … ich sehe überhaupt
   * kein Zeichen."* Protokoll 11:20:41: `category: Playback,
   * playbackHiFiActive: true, tonBelegt: true` — und der Lauscher stand ab
   * 11:20:42 auf `wartet: tonbelegung`, ohne dass etwas spielte.
   *
   * Die Kette: der Wach-Wort-Vorrang (pre.3189) gibt das Web-Mikro frei →
   * hier ging `meldeMikroBedarf(false)` raus → playback-hifi.ts legt die
   * Sitzung auf .playback → das Native wertet genau das als „Ton läuft"
   * (Tonbelegung) — und eine .playback-Sitzung kann ohnehin nicht aufnehmen.
   * Zwei eigene Fixes haben sich gegenseitig aufgehoben.
   *
   * Das Mikro WIRD gebraucht — nur eben vom Lauscher, nicht von der Seite.
   */
  if (!dayRecorderBusy && !wachwortVorrang) {
    meldeMikroBedarf(false);
  }
  if (warmStreamTimer) {
    clearTimeout(warmStreamTimer);
    warmStreamTimer = null;
  }
  if (warmStream) {
    warmStream.getTracks().forEach((t) => t.stop());
    warmStream = null;
    console.log("[ptt] warmStream freigegeben:", reason);
  }
  meldeWebMikro();
}

// 2026-07-09 (Martin, PTT-Verzoegerung): das Mikro PROAKTIV vorwaermen (nach dem
// TTS-Ende), damit das naechste PTT SOFORT startet statt ~3s Kaltstart. Holt
// einen rohen Stream (VPU aus, wie beim PTT), haelt den Track stumm (enabled=
// false) und gibt ihn nach 15s wieder frei, falls kein PTT kam (kein Dauer-
// Mikro, kein Ton-leise-Risiko: der naechste TTS-Start gibt ihn ohnehin frei).
// 2026-07-12 (Martin, "Pausiert direkt nach Start"): laeuft die TAGES-
// Aufnahme (nativer Recorder), kaempfen Warm-Mikro und Recorder um die
// Audio-Session — iOS unterbricht den Recorder und er pausiert sich.
// Solange busy: Warm-Stream sofort freigeben, kein neues Vorwaermen.
// (PTT waehrend der Tages-Aufnahme hat seinen eigenen Pause/Resume-Tanz
// und startet dann eben kalt — korrekt statt schnell.)
let dayRecorderBusy = false;
/*
 * Der Zustand der Tagesaufnahme, wie ihn `voice-v8:recorder-state` meldet.
 * (24.09.2026, Ticket 2202)
 *
 * ⚠️ `dayRecorderBusy` ist dafür zu weit: es zählt auch „pausiert",
 *    „wird abgeschlossen" und die Lücke zwischen zwei Sitzungen — richtig
 *    fürs Vorwärmen (dann lieber nicht ans Mikro), falsch für die Frage
 *    „nimmt die Tagesaufnahme GERADE auf?". Nur dann hält sie ein offenes
 *    Mikro, das die Sprechtaste nativ mitbenutzen kann.
 *
 *    Dieselbe Quelle wie `getRecorderState()` in native-recorder.ts
 *    (notifyState und syncFromNative melden beide über dieses Ereignis),
 *    nur ohne Umweg über einen Import — die Messzeile braucht ihn auch im
 *    Browser, und dort soll kein zusätzliches Warten vor dem Mikrofon
 *    entstehen.
 */
let tagesZustand = "";
/*
 * ⚠️⚠️ 22.09.2026 — DER ANRUF BRAUCHT DAS MIKROFON ALLEIN.
 *
 * Seit dem 12.07. steht in app.ts, warum vor jedem Anruf `releaseWarmMic`
 * gerufen wird: *„das PTT-Warm-Mikro BLOCKIERT den Anruf-Mikro-Zugriff ->
 * LiveKit bekommt ein stummes Mikro."* (Martins Meldung: „Anruf stumm, kein
 * User-Audio-Track".)
 *
 * Diese Freigabe ist ein EINMALIGER Griff. Sie schuetzt nicht davor, dass
 * gleich danach wieder vorgewaermt wird — und genau das passiert seit
 * pre.3151 im Browser nach jeder Antwort, bei jedem Zurueckkommen auf die
 * Seite und bei jedem Wiedergabe-Ende.
 *
 * Nativ war das nie ein Thema, weil dort das Native die Tonbelegung regelt.
 * Im Browser gibt es diese Instanz nicht. Darum ein Riegel nach dem Muster
 * von `dayRecorderBusy`: solange ein Anruf laeuft, wird gar nicht gewaermt.
 */
let anrufLaeuft = false;

/** Dem Mikrofon sagen, dass ein Anruf das Geraet braucht. (22.09.2026) */
export function setzeAnrufAktiv(aktiv: boolean): void {
  anrufLaeuft = aktiv;
}

/** Läuft gerade ein Anruf in der App? (Ticket 2849: der Zuruf „Alltag starten“ startet dann nie den Rekorder.) */
export function istAnrufAktiv(): boolean {
  return anrufLaeuft;
}

/*
 * ⚠️⚠️ 23.09.2026 — DAS WACH-WORT HAT VORRANG VOR DEM VORWAERMEN.
 *
 * Martin, mit Build 67: *„Habe gesagt: Hey Vespera, nichts passiert. Ich sehe
 * überhaupt kein Signal. Wobei oben bei der Notch ein leuchtender Punkt ist."*
 *
 * Der Punkt war das vorgewaermte Mikro der Sprechtaste. Im Protokoll 10:59:41:
 * `[wachwort] lauscht` → `tritt beiseite: web-mikro` → `ptt-warmmikro-frisch`.
 * Die Seite waermt bei jedem Oeffnen und nach jeder Antwort vor und haelt
 * danach 45 s warm; der Lauscher tritt dafuer beiseite (pre.3186, damit nicht
 * zwei am selben Mikro haengen). Ergebnis: bei OFFENER App lauschte er
 * praktisch nie — genau dann, wenn Martin es probiert.
 *
 * Das Vorwaermen spart der Sprechtaste ein paar hundert Millisekunden. Wer
 * das Wach-Wort einschaltet, will die Sprechtaste gar nicht druecken. Also
 * gehoert das ruhende Mikro dann dem Lauscher: kein Vorwaermen, und nach
 * einer Aufnahme wird die Leitung freigegeben statt warm gehalten.
 */
let wachwortVorrang = false;

/** Vom Wach-Wort-Einrichten (app.ts) und vom Schalter (Einstellungen). */
export function setzeWachwortVorrang(an: boolean): void {
  wachwortVorrang = an;
  if (an) {
    releaseWarmMic("wachwort-vorrang");
    // Die Sitzung bleibt aufnahmebereit (.playAndRecord) — der Lauscher
    // braucht sie. „vorwaermen", damit die Ortung aus bleibt (geo-watch).
    meldeMikroBedarf(true, "vorwaermen");
  } else if (!dayRecorderBusy && !webMikroOffen()) {
    meldeMikroBedarf(false);
  }
}
// 2026-07-15 (Martin, "Player im Telefon-Modus"): waehrend echter
// Wiedergabe (buildAudioPlayer) darf das Warm-Mikro NICHT laufen — ein
// aktiver getUserMedia-Stream zwingt iOS in .playAndRecord (HFP/Telefon)
// und ueberschreibt den nativen .playback-(A2DP-)Modus. Der Player feuert
// voice-v8:playback-start/-end; hier sperren wir das Vorwaermen dazu.
let playbackActive = false;

/**
 * Ticket 2246 (13.09.2026): reiner Lesezugriff für die Diagnose des
 * täglichen Sichtbarkeits-Flackerns (>99% der "Sitzungen" unter 1 Minute
 * seit 05.09., server/bildschirmzeit.ts) — testet, ob es mit einer offen
 * gehaltenen Mikro-/Wiedergabe-Session zusammenfällt. Keine
 * Verhaltensänderung, nur ein Blick auf bestehenden Zustand.
 */
export function mikroSitzungsStatus(): {
  tagesaufnahme: boolean;
  wiedergabe: boolean;
} {
  return { tagesaufnahme: dayRecorderBusy, wiedergabe: playbackActive };
}

if (typeof window !== "undefined") {
  window.addEventListener("voice-v8:playback-start", () => {
    playbackActive = true;
    // ⚠️ Eine laufende Wartezeit ist hinfaellig: es war eine Luecke, kein Ende.
    if (ruheTimer) {
      clearTimeout(ruheTimer);
      ruheTimer = null;
    }
    releaseWarmMic("wiedergabe");
  });
  window.addEventListener("voice-v8:playback-end", () => {
    playbackActive = false;
    // Ticket 627: JETZT ist die ganze Antwort durch — erst hier lohnt das
    // Vorwaermen, ohne die Route waehrend des Sprechens zu verstellen.
    // 22.09.2026: auch im Browser — aber nur mit belegter Erlaubnis.
    warteAufRuheDannWaermen();
  });
}
if (typeof window !== "undefined") {
  window.addEventListener("voice-v8:recorder-state", (e) => {
    const d = (e as CustomEvent<{ state?: string; dayIntent?: boolean }>)
      .detail;
    const st = d?.state;
    // #519 (2026-07-23): busy folgt dem ECHTEN Zustand — vorher blieb es
    // nach dem ersten Aufnahme-Start fuer immer true und das Mikro-
    // Warmhalten war dauerhaft tot (= 5s-Kaltstart bei jedem PTT).
    // 2026-07-25: Der alte Wert MUSS fuer den Flanken-Vergleich unten
    // erhalten bleiben. Vorher wurde dayRecorderBusy hier bereits auf den
    // NEUEN Zustand gesetzt — damit war `busy && !dayRecorderBusy` fuer
    // "recording" nie wahr und das Warm-Mikro wurde beim Start der
    // Tagesaufnahme nie freigegeben (die Absicht des #519-Fixes erfuellt
    // ohnehin die Zuweisung am Ende dieses Blocks).
    const vorherBusy = dayRecorderBusy;
    if (!st) return;
    tagesZustand = st;
    // 2026-07-14 (Martin, "Aufnahme starb"): dayIntent zaehlt MIT — auch
    // in Unterbrechungs-Luecken (state=idle, Auto-Restart wartet aufs
    // freie Mikro) bleibt das Warm-Mikro fern, sonst blockiert es den
    // Neustart der Tages-Aufnahme.
    const busy =
      st === "recording" ||
      st === "paused" ||
      st === "finishing" ||
      d?.dayIntent === true;
    if (busy && !vorherBusy) {
      // ZUERST busy, DANN Warm-Mikro weg — sonst ruft releaseWarmMic
      // meldeMikroBedarf(false) und die Session kippt auf .playback,
      // obwohl die Tagesaufnahme das Mikro gerade uebernimmt.
      dayRecorderBusy = true;
      releaseWarmMic("tages-aufnahme");
      meldeMikroBedarf(true);
    } else {
      dayRecorderBusy = busy;
    }
    // 23.09.2026: der Lauscher braucht das Mikro auch nach der Tagesaufnahme.
    if (!busy && vorherBusy && !wachwortVorrang) meldeMikroBedarf(false);
  });
  // 2026-07-14: Aufnahme-Start kuendigt sich an — Warm-Mikro SOFORT und
  // SYNCHRON freigeben, BEVOR der native Recorder die Session aufbaut.
  // detail.held meldet dem Starter, ob gewartet werden muss (WebKit-
  // Session-Abbau braucht einen Moment).
  window.addEventListener("voice-v8:recorder-will-start", (e) => {
    const hadWarm = !!(
      warmStream && warmStream.getTracks().some((t) => t.readyState === "live")
    );
    dayRecorderBusy = true;
    if (hadWarm) releaseWarmMic("tages-aufnahme-start");
    meldeMikroBedarf(true);
    (e as CustomEvent<{ held?: boolean }>).detail.held = hadWarm;
  });
}

/*
 * 2026-09-02 (Martin: "das Mikrofon schaltet oft viel zu langsam ein, ich
 * muss zwei, drei Sekunden warten").
 *
 * An der Telemetrie nachgerechnet (532 Zeilen, 3 Tage):
 *
 *     warm    2 Druecke   Median  144 ms
 *     kalt   12 Druecke   Median 1715 ms   p90 2200 ms   schlimmster 6434 ms
 *
 * 85 % der Druecke sind kalt. Der warme Weg ist also nicht langsam — er
 * greift nur fast nie.
 *
 * ⚠️ Die Spur konnte das nicht erklaeren: `ptt-warmmikro` wurde VOR den
 * Abbruchbedingungen gemeldet. 57 Meldungen standen im Log, aber nur zwei
 * warme Druecke — man sah die Absicht, nie das Ergebnis. Deshalb nennt die
 * Meldung jetzt den Ausgang.
 */
const WARM_FENSTER_MS = 45_000;
/**
 * Wie lange ein warmes Mikro HÖCHSTENS offen bleibt — egal wie oft
 * nachgewärmt wird. (15.09.2026)
 *
 * ⚠️⚠️ Martin, mit Foto: *„Ich sehe, dass oben im Mikrofon immer noch läuft,
 *    obwohl eigentlich nichts mehr offen ist. Welches Mikro, was macht das
 *    jetzt genau im Hintergrund laufend? Ich hatte gerade die Fragen
 *    beantwortet."*
 *
 * Gemessen im Journal zwischen 07:00 und 07:36:
 *
 *     10 Vorwärmungen · Abstand median 11 s · 7 von 9 unter 45 s
 *     Freigaben: 0
 *
 * Vorgewärmt wird nach JEDER gesprochenen Antwort (`playback-end`) und bei
 * JEDER Rückkehr in die App (`visibilitychange`). Beides kommt beim
 * Beantworten von Fragen im Sekundentakt — um 07:35 sechsmal in 52
 * Sekunden. Und jede Vorwärmung setzte die 45-Sekunden-Uhr ZURÜCK. Eine
 * Frist, die sich selbst verlängert, ist keine Frist.
 *
 * ⚠️ Das Mikro nimmt dabei nichts auf (die Spuren sind stummgeschaltet) —
 *    aber es hält die Ton-Sitzung im Aufnahmemodus, zwingt Bluetooth ins
 *    Telefonprofil und kostet Akku. Genau das ist Ticket 2254.
 *
 * Deshalb eine ABSOLUTE Decke: Ab dem Öffnen läuft eine Uhr, die keine
 * Nachwärmung verlängert. Danach schließt das Mikro; der nächste Druck ist
 * ein Kaltstart — gemessen rund 300 ms, und ohnehin der Normalfall
 * (85 % der Drücke, Ticket 2202).
 */
const WARM_DECKE_MS = 3 * 60_000;
/** Wann das warme Mikro geöffnet wurde. 0 = keines offen. */
let warmSeit = 0;

/**
 * Vorwaermen, aber nur wenn es erlaubt ist. (22.09.2026)
 *
 * ⚠️ EIN Weg fuer alle Ausloeser. Vorher stand an jeder Stelle eine eigene
 *    `isNativePlatform()`-Sperre; fuenf Kopien derselben Entscheidung, von
 *    denen die unabhaengige Pruefung zwei fand, die KEIN Test abdeckt. Wer
 *    hier eine Bedingung aendert, aendert sie fuer alle.
 */
/**
 * Ein Wiedergabe-Ende ist noch kein Ende. (22.09.2026)
 *
 * ## ⚠️⚠️ WARUM NICHT SOFORT GEWAERMT WIRD
 *
 * Der Code sagt seit Ticket 627: *„JETZT ist die ganze Antwort durch."* Fuer
 * eine gesprochene Antwort stimmt das. Fuers VORLESEN nicht: die Narration
 * spielt Stueck fuer Stueck ueber EIN Audio-Element und tauscht nur die
 * Quelle. Jeder Quellenwechsel feuert `emptied`, und das zaehlt wie ein
 * Ende — zwischen zwei Saetzen eines Kapitels also ein `playback-end` nach
 * dem anderen.
 *
 * Was daraus folgt, steht ein paar Zeilen weiter unten schon als
 * Geraetephysik:
 *
 *   *„Ueber Bluetooth haelt ein stummer, aber weiterhin OFFENER
 *   Mikrofon-Stream die Verbindung im Telefonprofil (HFP): schmalbandig und
 *   leiser. Bluetooth Classic kann pro Verbindung nur EIN Profil."*
 *
 * Wer in jeder Stueck-Luecke vorwaermt, schaltet die Verbindung mitten im
 * Kapitel ins Telefonprofil — und beim naechsten Stueck zurueck. Das sind
 * die hoerbaren Lautstaerke-Spruenge, gegen die derselbe Kommentar schon
 * einmal argumentiert hat.
 *
 * ⚠️ Die Ruhe-Schwelle ist deutlich groesser als die Gnadenfrist der
 *    Tonanlage (EXIT_GRACE_MS, 700 ms). Kommt in dieser Zeit ein neues
 *    Stueck, war es keine Pause, sondern eine Luecke — dann wird nicht
 *    gewaermt.
 */
const RUHE_VOR_WAERMEN_MS = 2_000;
let ruheTimer: ReturnType<typeof setTimeout> | null = null;

function warteAufRuheDannWaermen(): void {
  if (ruheTimer) clearTimeout(ruheTimer);
  ruheTimer = setTimeout(() => {
    ruheTimer = null;
    // In der Zwischenzeit wieder etwas gestartet? Dann war es eine Luecke.
    if (playbackActive) return;
    void warmUpMitErlaubnis();
  }, RUHE_VOR_WAERMEN_MS);
}

async function warmUpMitErlaubnis(): Promise<void> {
  if (!(await darfVorwaermen())) {
    merkeWarmGrund("nicht-gewaermt:keine-erlaubnis");
    return;
  }
  await warmUpMic();
}

/**
 * Spielt wirklich noch etwas? (22.09.2026)
 *
 * ## ⚠️ Der Kreis, den eine unabhaengige Pruefung mit einer Sonde gefunden hat
 *
 * `playbackActive` ist ein Merker, den nur ein EREIGNISPAAR umlegt:
 * `playback-start` setzt ihn, `playback-end` loescht ihn. Im Browser wurde
 * gemessen: 1x Start, 0x Ende — danach steht er fuer immer auf true.
 *
 * Die Kette dahinter: `releaseWarmMic` schickt `meldeMikroBedarf(false)`,
 * damit faellt `mikroBedarf`, damit gibt `sessionRuhtAufPlayback()` true
 * zurueck, damit bleibt `hiFiOn` true — und `playback-end` kommt nie.
 * Zurueckgesetzt wuerde `mikroBedarf` nur von `warmUpMic` selbst, das aber
 * wegen `playbackActive` gar nicht mehr laeuft. **Ein Kreis.**
 *
 * Ohne diese Gegenprobe waere der ganze Browser-Vorwaermer wirkungslos: er
 * wuerde nach der ersten Antwort nur noch `ptt-warmmikro-abbruch:wiedergabe`
 * schreiben. (Zum Vergleich: nativ, wo das Ende faellt, steht dieser Abbruch
 * trotzdem 333x in der Datenbank.)
 *
 * ⚠️ Darum fragen wir die Tonanlage direkt. `wiedergabeHaltStand()` kennt den
 *    Saldo der gesendeten Start/Ende-Ereignisse und die Menge der wirklich
 *    spielenden Elemente — sie weiss es besser als ein Merker, der einmal
 *    haengenbleiben kann. Stimmen beide nicht ueberein, gilt die Tonanlage.
 */
function spieltWirklich(): boolean {
  if (!playbackActive) return false;
  try {
    const w = wiedergabeHaltStand();
    // Die Tonanlage sagt: nichts spielt, nichts ist vorgemerkt, keine
    // Stimme — dann ist der Merker haengengeblieben.
    if (w.spielend === 0 && !w.vorwaermung && !w.stimme) return false;
  } catch {
    /* Ohne Tonanlage bleibt der Merker die einzige Auskunft. */
  }
  return true;
}

/**
 * ⚠️⚠️ 25.09.2026 — IN DER APP KEIN WARMES MIKRO MEHR.
 *
 * Albert, 15:14 im Tesla: fünfmal gedrückt, fünfmal nichts (bytes=5,
 * puffer-leer), erst der sechste Druck holte eine frische Leitung und lief.
 * Journal 23.–25.09.: auf dem warmen Weg 14 von 21 Drücken leer (Martin 6,
 * Albert 8), auf dem kalten 74 von 74 mit Text. Die warme Spur überlebt den
 * Bluetooth-Umschlag nach dem Vorlesen nicht zuverlässig; `spurStumm`
 * erkennt es nicht sicher (ja: 6 leer / 3 Text, nein: 8 / 6).
 *
 * Im Browser bleibt das Warmhalten: dort erspart es auf iOS den
 * Erlaubnis-Dialog bei jedem Druck (Pre.834). In der App fragt niemand.
 * Zurückdrehen erst mit einer Messung, die den warmen Weg in der App bei
 * unter 5 % leeren Drücken zeigt.
 */
export const WARMES_MIKRO_IN_DER_APP = false;

/*
 * ═══ MIKRO-VORSTART (19.10.2026, Befund Journal 06:30–07:41) ═════════════════
 *
 * Geht das Mikro nach einer Antwort von selbst auf (`via=nach-frage`), braucht getUserMedia über Bluetooth ~1,2 s
 * (`gumEnd=+1220ms … kalt=tts-start-hifi`), und Martins Satzanfang fehlt. Warmhalten ist abgelehnt (Telefon-Klang im Auto,
 * Hi-Fi-Regel vom 28.07.). Darum holt `mikroVorstarten` den Strom NUR dann, wenn app.ts sagt „nach dieser Wiedergabe geht
 * das Mikro auf“ (mikro-vorstart.ts), und erst in den letzten ~Vorlauf-Millisekunden der Wiedergabe.
 *
 * ⚠️ Das ist KEIN warmer Strom: eigener Platz, eigener Verfall (Restdauer + 5 s), nie über ein Ende hinaus. Er wird beim
 *    Öffnen (`nimmVorstart`) als Strom der Sprechtaste übernommen — der warme Weg danach (Spur an, Leitungsprüfung im
 *    Druck, die bei einer stummen Spur auf ein frisches Mikro wechselt) fängt eine tote Spur ab. Jede andere Freigabe
 *    (`releaseWarmMic`, Abbruch, neuer Beitrag, Verfall) schließt ihn und meldet den Bedarf wieder ab.
 * ⚠️ Nie, wenn schon aufgenommen wird, eine Tagesaufnahme/ein Anruf läuft, das Wach-Wort Vorrang hat, ein warmer Strom da
 *    ist oder die Erlaubnis nicht belegt ist (sonst käme der Dialog mitten in die Antwort).
 */
const VORSTART_NACHLAUF_MS = 5000;
let vorstartStrom: MediaStream | null = null;
let vorstartTimer: number | null = null;
/** Läuft/lief ein Vorstart für das nächste Öffnen? Trägt die Zeile `[ptt-t]` (`vorstart=ja vorstartMs= restMs=`). */
let vorstartInfo: { t0: number; restMs: number } | null = null;
let vorstartNr = 0;
/**
 * Der Abruf (Promise von `acquireRawMic`), den die Sprechtaste im kalten Weg ÜBERNOMMEN hat (Single-Flight). Wer denselben Abruf
 * zuvor gestartet hat (Vorstart, Vorwärmen), darf seinen Strom danach NIE schließen — er gehört dem Druck, auch wenn dessen
 * `aktiverPttStrom` noch nicht eingetragen ist (die ältere Fortsetzung läuft zuerst). 19.10.2026, Prüfbefund.
 */
let uebernommenerAbruf: Promise<MediaStream> | null = null;

/**
 * Darf der Vorstart in dieser Umgebung laufen? In der App (native) NICHT: dort gilt WARMES_MIKRO_IN_DER_APP=false (25.09.2026,
 * 14 von 21 warmen Drücken leer) — ein vorgezogener Strom wäre ein warmer. Nur im Browser. `null` = ja, sonst ein Wort.
 */
export function vorstartPlattformGrund(): string | null {
  return isNativePlatform() && !WARMES_MIKRO_IN_DER_APP ? "app" : null;
}

export function gibVorstartFrei(grund: string): void {
  const hatte = vorstartStrom !== null || vorstartInfo !== null;
  vorstartNr++; // ein noch laufender Abruf sieht die neue Nummer und gibt seinen Strom wieder her
  if (vorstartTimer) {
    clearTimeout(vorstartTimer);
    vorstartTimer = null;
  }
  const s = vorstartStrom;
  vorstartStrom = null;
  vorstartInfo = null;
  if (s) {
    // Der Strom der laufenden Sprechtaste gehört ihr — den schließt ihr Aufräumen.
    if (s !== aktiverPttStrom && s !== warmStream) s.getTracks().forEach((t) => t.stop());
    console.log("[ptt] vorstart freigegeben:", grund);
    if (!recordingActive && !dayRecorderBusy && !wachwortVorrang && !warmStream) meldeMikroBedarf(false);
    meldeWebMikro();
  }
  if (hatte && !s) console.log("[ptt] vorstart verworfen:", grund);
}

/** Der Vorstart hält (oder holt) gerade einen Strom für das nächste Öffnen? Nur für Tests und Messzeile. */
export function vorstartLage(): "aus" | "holt" | "bereit" {
  return vorstartStrom ? "bereit" : vorstartInfo ? "holt" : "aus";
}

/**
 * Das Mikro jetzt schon holen, `restMs` vor dem Ende der Wiedergabe. Antwort: `null` = es läuft; sonst ein Wort, warum nicht
 * (kein Inhalt). Wirft nie.
 */
export async function mikroVorstarten(restMs: number): Promise<string | null> {
  try {
    const plattform = vorstartPlattformGrund();
    if (plattform) return plattform;
    if (recordingActive) return "aufnahme";
    if (dayRecorderBusy) return "tagesaufnahme";
    if (anrufLaeuft) return "anruf";
    if (wachwortVorrang) return "wachwort";
    if (warmStream && warmStream.getTracks().some((t) => t.readyState === "live")) return "schon-warm";
    if (vorstartStrom || vorstartInfo) return "laeuft-schon";
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return "kein-mikro";
    if (!(await darfVorwaermen())) return "keine-erlaubnis";
    const nr = ++vorstartNr;
    vorstartInfo = { t0: performance.now(), restMs: Math.round(restMs) };
    vorstartTimer = window.setTimeout(() => gibVorstartFrei("verfall"), Math.max(0, restMs) + VORSTART_NACHLAUF_MS);
    let s: MediaStream;
    const abruf = acquireRawMic();
    try {
      s = await abruf;
    } catch {
      if (nr === vorstartNr) gibVorstartFrei("verweigert");
      return "verweigert";
    }
    // Währenddessen hat die Sprechtaste (Single-Flight: derselbe Abruf, derselbe Strom), ein Abbruch oder das Wach-Wort entschieden.
    if (recordingActive || nr !== vorstartNr || wachwortVorrang || dayRecorderBusy || anrufLaeuft) {
      // Hat der Druck den Abruf übernommen, gehört ihm der Strom — hier NIE schließen (er schließt ihn beim Aufräumen).
      if (uebernommenerAbruf !== abruf && s !== aktiverPttStrom && s !== warmStream && s !== vorstartStrom) {
        s.getTracks().forEach((t) => t.stop());
      }
      meldeWebMikro();
      return recordingActive ? "ptt-war-schneller" : "ueberholt";
    }
    s.getTracks().forEach((t) => (t.enabled = false));
    vorstartStrom = s;
    // Mikro ist scharf — die Sitzung gehört auf .playAndRecord (wie beim Vorwärmen; ⚠️ kein Sprechen, die Ortung bleibt aus).
    meldeMikroBedarf(true, "vorwaermen");
    meldeMikroAnlass("ptt-vorstart");
    return null;
  } catch {
    gibVorstartFrei("fehler");
    return "fehler";
  }
}

/** Beim Öffnen: den vorgezogenen Strom übernehmen, wenn er taugt (lebt, nicht stumm, kein fremdes Gerät). Sonst frei und null. */
function nimmVorstart(): MediaStream | null {
  const s = vorstartStrom;
  if (!s) return null;
  const spuren = s.getAudioTracks();
  const taugt = spuren.length > 0 && spuren.every((sp) => sp.readyState === "live" && !sp.muted && !istFremdesGeraet(sp.label));
  if (!taugt) {
    gibVorstartFrei("untauglich");
    return null;
  }
  if (vorstartTimer) {
    clearTimeout(vorstartTimer);
    vorstartTimer = null;
  }
  vorstartStrom = null; // ab jetzt gehört er der Sprechtaste (als `warmStream` eingetragen, bis sie ihn übernimmt)
  return s;
}

async function warmUpMic(): Promise<void> {
  // Ticket 570 (2026-07-30): Push-to-Talk greift hier nach dem Mikrofon —
  // genau ein Moment, in dem die laufende Alltags-Aufnahme abreissen kann.
  if (
    recordingActive ||
    dayRecorderBusy ||
    anrufLaeuft ||
    wachwortVorrang ||
    spieltWirklich()
  ) {
    const grund = recordingActive
      ? "ptt-aufnahme"
      : dayRecorderBusy
        ? "tagesaufnahme"
        : anrufLaeuft
          ? "anruf"
          : wachwortVorrang
            ? "wachwort"
            : "wiedergabe";
    merkeWarmGrund(`nicht-gewaermt:${grund}`);
    meldeMikroAnlass(`ptt-warmmikro-abbruch:${grund}`);
    return;
  }
  // 25.09.2026: in der App kein warmes Mikro (WARMES_MIKRO_IN_DER_APP).
  if (isNativePlatform() && !WARMES_MIKRO_IN_DER_APP) {
    merkeWarmGrund("nicht-gewaermt:app");
    return;
  }
  /*
   * ⚠️ 22.09.2026 — HIER STAND EINE ZWEITE MELDUNG.
   *
   * `ptt-warmmikro` ging unbedingt raus, und je nach Ausgang folgte eine
   * weitere (`-decke`, `-schon-warm`, `-frisch`, `-abbruch:…`). Jede davon
   * ist ein eigener Server-Aufruf; bei einem Anlass-Abstand von median elf
   * Sekunden sind das rund zehn pro Minute und Sitzung.
   *
   * Dasselbe Protokoll ist die Quelle der Zahlen, auf denen Ticket 2202
   * steht — die Messung verrauschte sich selbst. Und die Lehre dazu steht
   * dreissig Zeilen weiter oben schon einmal: *„57 Meldungen standen im
   * Log, aber nur zwei warme Druecke — man sah die Absicht, nie das
   * Ergebnis."*
   *
   * Es bleibt bei EINER Meldung, und die nennt den Ausgang.
   */
  if (
    warmStream &&
    warmStream.getTracks().some((t) => istFremdesGeraet(t.label))
  ) {
    releaseWarmMic("falsches-geraet-warmup");
  }
  if (
    warmStream &&
    warmStream.getTracks().some((t) => t.readyState === "live")
  ) {
    const offenSeit = warmSeit ? Date.now() - warmSeit : 0;
    if (offenSeit >= WARM_DECKE_MS) {
      // ⚠️ Die Decke ist erreicht: NICHT verlaengern, sondern schliessen.
      //    Sonst haelt eine Kette von Nachwaermungen das Mikro fuer immer
      //    offen — genau der Zustand, den Martin am 15.09. gesehen hat.
      releaseWarmMic("warm-decke");
      meldeMikroAnlass("ptt-warmmikro-decke");
      return;
    }
    if (warmStreamTimer) clearTimeout(warmStreamTimer);
    // ⚠️ Vorwaermen, kein Sprechen — die Ortung bleibt aus.
    meldeMikroBedarf(true, "vorwaermen");
    warmStreamTimer = window.setTimeout(
      () => releaseWarmMic("warmup-timeout"),
      // Nie ueber die Decke hinaus: Die Restzeit ist das Minimum aus
      // Fenster und dem, was von der Decke uebrig ist.
      Math.max(1_000, Math.min(WARM_FENSTER_MS, WARM_DECKE_MS - offenSeit)),
    );
    meldeMikroAnlass("ptt-warmmikro-schon-warm");
    return;
  }
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    merkeWarmGrund("nicht-gewaermt:kein-mikro");
    meldeMikroAnlass("ptt-warmmikro-abbruch:kein-mikro");
    return;
  }
  try {
    // Single-Flight: teilt sich die Anfrage mit einem evtl. parallelen
    // PTT-Start (Audit Wurzel #2) statt zu racen.
    const abruf = acquireRawMic();
    const s = await abruf;
    // Waehrend des await hat ein PTT-Start den Stream evtl. schon uebernommen
    // (recordingActive) — dann NICHTS anfassen (nicht stummschalten!).
    if (recordingActive) {
      meldeMikroAnlass("ptt-warmmikro-abbruch:ptt-war-schneller");
      /*
       * ⚠️ Hier wurde der Strom bisher einfach fallengelassen. Ist es
       *    DERSELBE, den die Sprechtaste gerade benutzt (Single-Flight),
       *    ist das richtig — sie schliesst ihn beim Aufraeumen. War es ein
       *    anderer, haelt ihn danach niemand mehr: das Mikrofon bliebe
       *    offen, bis der Reiter geschlossen wird, und der Hinweis in der
       *    Menueleiste mit ihm.
       */
      // 19.10.2026: auch hier zählt der übernommene Abruf — die ältere Fortsetzung läuft VOR dem Eintrag in aktiverPttStrom.
      if (uebernommenerAbruf !== abruf && s !== aktiverPttStrom && s !== warmStream) {
        for (const t of s.getTracks()) t.stop();
      }
      return;
    }
    /*
     * ⚠️ 23.09.2026: Waehrend getUserMedia lief, kann das Wach-Wort Vorrang
     *    bekommen haben (beim App-Start laeuft beides gleichzeitig los). Im
     *    Protokoll 11:07:49: „warmmikro-frisch" NACH dem Vorrang — und der
     *    naechste Druck bekam eine stumme Spur (spurStumm=ja), weil der
     *    Lauscher das Mikro inzwischen hatte.
     */
    if (wachwortVorrang) {
      for (const t of s.getTracks()) t.stop();
      meldeWebMikro();
      meldeMikroAnlass("ptt-warmmikro-abbruch:wachwort-nachtraeglich");
      return;
    }
    s.getTracks().forEach((t) => (t.enabled = false));
    warmStream = s;
    warmSeit = Date.now();
    // Ticket 2765: reisst die Spur von selbst ab (Bluetooth weg), steht es im Protokoll.
    for (const t of s.getAudioTracks()) {
      beobachteSpurEnde(t, () => warmStream?.getAudioTracks()[0], () => warmStream !== s);
    }
    // Ticket 782/777: Mikro ist scharf — Session gehoert auf .playAndRecord.
    // ⚠️ Vorwaermen, kein Sprechen — die Ortung bleibt aus.
    meldeMikroBedarf(true, "vorwaermen");
    if (warmStreamTimer) clearTimeout(warmStreamTimer);
    warmStreamTimer = window.setTimeout(
      () => releaseWarmMic("warmup-timeout"),
      WARM_FENSTER_MS,
    );
    meldeMikroAnlass("ptt-warmmikro-frisch");
  } catch {
    /* Mikro verweigert/belegt — dann eben Kaltstart beim naechsten PTT */
    meldeMikroAnlass("ptt-warmmikro-abbruch:verweigert");
  }
}

if (typeof window !== "undefined") {
  // Bei TTS-Wiedergabe-Start (Event aus tts-queue) den warmen Stream nur auf
  // der nativen App freigeben — die PWA haelt ihn bewusst 5min (dort ist die
  // Wiedergabe ohnehin laut).
  // Ticket 2421 (21.09.2026): Die Freigabe kommt jetzt schon beim
  // SYNTHESE-Start (tts-queue.ts enqueue), nicht erst beim ersten Ton. Der
  // Bluetooth-Umschlag laeuft dann, waehrend die Sprache erzeugt wird — und
  // der Weg steht, wenn der erste Satz kommt. Gemessen 18.-20.09.: 45 kalte
  // Starts mit Grund tts-start-hifi, 24 davon am FORD-AUDIO; die erste
  // Sekunde fehlte dort. Der Hoerer unten bleibt als Netz (Freigabe ist
  // idempotent).
  window.addEventListener("voice-v8:tts-synthese-start", () => {
    /*
     * ⚠️ 22.09.2026 — KEINE Plattform-Sperre mehr. Freigabe und Vorwaermung
     *    sind ein PAAR: waermt der Browser nach jeder Antwort vor, ohne beim
     *    naechsten Sprechen freizugeben, bleibt sein Mikro ueber die ganze
     *    Antwort offen. Genau der Zustand aus Alberts Meldung vom 16.09.
     *    („er kann die Aufnahme nicht mehr stoppen"), nur haeufiger.
     */
    releaseWarmMic("tts-synthese-hifi");
  });
  window.addEventListener("voice-v8:tts-audio-start", () => {
    /*
     * ⚠️ 22.09.2026 — auch hier keine Plattform-Sperre mehr (Gegenstueck zu
     *    den Vorwaermern). Die Begruendung unten gilt weiter, sie betrifft
     *    das WIE der Freigabe, nicht das OB.
     */
    // 2026-07-09 (Audit Wurzel #1) hatte hier auf STUMMSCHALTEN statt Stoppen
    // umgestellt: t.stop() riss die iOS-Capture-Pipeline ab, und WebKit baute
    // die Session beim naechsten getUserMedia komplett neu auf — das waren die
    // 3-7 s. Die Annahme dabei: die nativ gepinnte .playAndRecord-Session
    // halte den Ton trotzdem laut.
    //
    // 2026-07-28 (Martin: "die Lautstaerke ist noch nicht optimal, das
    // Zurueckkehren zu Hi-Fi dauert zu lange"): Diese Annahme gilt NUR fuer
    // den iPhone-Lautsprecher. Ueber Bluetooth nicht — dort haelt ein stummer,
    // aber weiterhin OFFENER Mikrofon-Stream die Verbindung im Telefonprofil
    // (HFP): schmalbandig und leiser. Bluetooth Classic kann pro Verbindung
    // nur EIN Profil. Solange das Warm-Mikro offen ist, bleibt die Wiedergabe
    // also leise — und weil nach jedem Sprech-Ende sofort wieder vorgewaermt
    // wird, wurde sie NIE richtig laut.
    //
    // Der urspruengliche Grund fuers Offenhalten ist inzwischen entfallen: Die
    // Session wird seit pre.1682 nativ gepinnt (AppDelegate.pinAudioSession),
    // WebKit baut sie nicht mehr neu auf. Also geben wir das Mikro beim
    // Sprech-Start wirklich frei — dann darf die Session auf .playback und
    // Bluetooth auf A2DP wechseln. Vorgewaermt wird direkt nach dem Sprechen
    // wieder (playback-end -> warmUpMic), das Mikro ist also warm, sobald
    // Martin antworten will.
    releaseWarmMic("tts-start-hifi");
  });
  // 2026-07-09 (Martin): nach dem TTS-Ende das Mikro proaktiv wieder vorwaermen
  // -> naechstes PTT sofort (loest den wiederkehrenden 3s-Konflikt).
  window.addEventListener("voice-v8:tts-audio-end", () => {
    lastTtsEndAt = performance.now();
    // 2026-07-25 (Ticket 627): NICHT mehr nach jedem einzelnen Satz
    // vorwaermen. Eine Antwort wird satzweise gesprochen; ein offenes Mikro
    // zwingt Bluetooth ins Telefonprofil (leiser), die naechste Freigabe
    // schaltet zurueck auf Hi-Fi (lauter) — das ergab die hoerbaren
    // Lautstaerke-Spruenge MITTEN in einer Antwort. Vorgewaermt wird jetzt
    // erst, wenn die GANZE Wiedergabe fertig ist (playback-end).
    if (!playbackActive) void warmUpMitErlaubnis();
  });
  /*
   * ⚠️ 15.09.2026 (Ticket 2202) — der Abbruch zaehlt wie ein Ende.
   *
   * Ein abgebrochener Sprech-Zug (Martin redet dazwischen) sendet bewusst
   * KEIN `tts-audio-end`, sondern `tts-audio-abbruch`. Hier hoerte nur das
   * Ende zu — nach einem Abbruch blieb das Mikro kalt, bis irgendwann eine
   * vollstaendige Antwort durchlief.
   *
   * Gemessen 13.-15.09.: 11 der 33 kalten Starts trugen den Grund
   * `tts-start-hifi`, fuenf davon ueber einer Sekunde; die Telemetrie nennt
   * `kaltSeit` von 8 bis 80 Sekunden. Ein warmer Start braucht 76 ms
   * (Median, n=7), ein kalter bis 3,6 s.
   *
   * ⚠️ Der Umweg ueber playback-hifi.ts (Abbruch -> refresh -> applyHiFi(false)
   *    -> playback-end -> hier) besteht seit pre.2919, aber er haengt daran,
   *    dass KEIN anderer Halt mehr steht. Diese Zeile haengt an nichts.
   */
  window.addEventListener("voice-v8:tts-audio-abbruch", () => {
    gibVorstartFrei("abbruch"); // 19.10.2026: Barge-in / Stopp — ein vorgezogenes Mikro wieder freigeben
    lastTtsEndAt = performance.now();
    if (!playbackActive) void warmUpMitErlaubnis();
  });
  // 2026-08-18 (Ein Mikro): KEIN Warm-Tipp. pointerdown auf dem Dokument
  // hat nach jedem beliebigen Tap 12s Capture gehalten und Bluetooth ins
  // Telefonprofil (HFP) gezwungen. Mikro oeffnet erst beim PTT-Druck.
  {
    /*
     * ⚠️⚠️ 22.09.2026 — DIESER BLOCK LIEF NUR NATIV, UND DAS WAR DIE URSACHE.
     *
     * Gemessen an 322 Mikro-Starts seit dem 15.09.: die acht langsamsten
     * alle im Browser mit kaltem Mikro, der schlimmste 13.740 ms (Albert).
     * Nativ ist das Maximum 3.025 ms. Der Grund stand hier: der ganze
     * Vorwaerm-Block — Erstaufruf, `pageshow`, `visibilitychange` — hing
     * hinter `isNativePlatform()`.
     *
     * ⚠️ Der Schutz sitzt jetzt in `darfVorwaermen()`, nicht in der
     *    Plattform: im Browser wird nur gewaermt, wenn die Erlaubnis
     *    belegt ist. Damit erscheint beim Seitenladen KEIN Dialog — ein
     *    einziges „Nicht erlauben" wuerde die Sprechtaste dauerhaft
     *    stilllegen.
     */
    // Sofort statt 2.5s — Martin drueckt oft direkt nach dem Oeffnen
    // (Telemetrie 19:33: overlapWarmup=yes = Vorwaermung kam zu spaet).
    // 2026-09-01: Vor dem Vorwaermen den nativen Recorder abgleichen.
    // Nach WebView-Reload ist dayRecorderBusy false, obwohl die
    // Alltag-Aufnahme im Hintergrund laeuft — warmUpMic stiehlt dann
    // das Mikro ("starts warm") und die Hintergrund-Strecke ist weg.
    const vorwaermenWennAlltagFrei = (): void => {
      void import("./native-recorder.js")
        .then((m) => m.syncFromNative())
        .catch(() => undefined)
        .then(async () => {
          /*
           * ⚠️ Die STRENGERE Schranke: dieser Weg wird vom Laden, von
           *    `pageshow` und von jeder Rueckkehr in den Tab ausgeloest —
           *    im Browser der Normalfall. Ohne sie waere das Mikrofon
           *    offen, solange der Tab vorne ist, statt solange jemand
           *    sprechen will.
           */
          if (dayRecorderBusy) return;
          /*
           * ⚠️⚠️ 22.09.2026 — IM BROWSER DIE ENGINE SELBST FRAGEN.
           *
           * `dayRecorderBusy` ist ein Merker dieser Seite. Im Browser wird
           * er ueber `notifyState` gesetzt und faellt danach nicht mehr
           * zuverlaessig zurueck — und `syncFromNative()` oben ist dort ein
           * No-op, weil es kein Plugin gibt.
           *
           * `laeuftImBrowser()` fragt den Rekorder direkt. Das ist die
           * einzige ehrliche Auskunft, und sie zaehlt: ein warmes Mikro
           * neben einer laufenden Browser-Aufnahme ist genau der
           * Datenverlust vom 01.09. (eine halbe Stunde).
           */
          try {
            const m = await import("./native-recorder.js");
            if (m.laeuftImBrowser?.()) {
              merkeWarmGrund("nicht-gewaermt:browser-aufnahme");
              return;
            }
          } catch {
            /* ohne Auskunft lieber vorsichtig weiter wie bisher */
          }
          if (!(await darfSichtWaermen())) {
            merkeWarmGrund("nicht-gewaermt:noch-nicht-gesprochen");
            return;
          }
          await warmUpMic();
        });
    };
    vorwaermenWennAlltagFrei();
    window.addEventListener("pageshow", vorwaermenWennAlltagFrei);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") vorwaermenWennAlltagFrei();
      // hidden: Alltag NICHT anfassen. Chat-PTT laeuft ueber MediaRecorder
      // und darf mit der WebView einschlafen — das ist ein anderer Weg.
      // (Die FREIGABE des warmen Mikros beim Wegschalten steht unten und
      //  gilt fuer alle — auch fuer den Browser.)
    });
  }

  /*
   * ⚠️⚠️ WER DIE SEITE VERLAESST, LAESST DAS MIKROFON NICHT OFFEN.
   * (16.09.2026 — Albert ueber Martin: *„er kann die Aufnahme nicht mehr
   * stoppen, die laeuft jetzt ewig weiter"*.)
   *
   * ## Die Kette, am Protokoll nachgezeichnet
   *
   * Beim Beenden einer Sprechtasten-Aufnahme wird der Mikrofon-Strom NICHT
   * geschlossen, sondern als `warmStream` offen gehalten und nur
   * stummgeschaltet (`enabled = false`) — damit der naechste Druck warm ist.
   * Freigegeben wird er allein durch eine Uhr: `setTimeout` ueber
   * WARM_FENSTER_MS (45 s).
   *
   * ⚠️ Diese Uhr steht still, sobald die Seite im Hintergrund liegt. iOS
   *    Safari friert die Zeitgeber eines Hintergrund-Tabs ein. Aus 45
   *    Sekunden wird damit „bis zur Rueckkehr" — und eine stummgeschaltete
   *    Spur ist fuer iOS trotzdem `live`: der Mikrofon-Hinweis oben bleibt
   *    an. Genau das sieht der Nutzer, und es gibt keinen Knopf dagegen.
   *
   * Alberts Telemetrie zeigt das Muster: `[sichtbar-d] hidden` mit Luecken
   * von 18 Minuten, 30 Minuten, 34 Minuten und 3,6 Stunden — bei
   * `aufnahme=false`. Es lief nie eine Aufnahme; es lag nur das Mikro offen.
   *
   * ⚠️ Derselbe Befund stand am 15.09. schon einmal hier, mit Martins Foto
   *    („oben laeuft immer noch das Mikrofon, obwohl nichts offen ist").
   *    Damals wurde die DECKE eingezogen (WARM_DECKE_MS). Auch die haengt an
   *    einer Uhr — und Uhren laufen im Hintergrund nicht.
   *
   * Deshalb hier kein weiterer Zeitgeber, sondern ein EREIGNIS:
   * `visibilitychange` wird noch zugestellt, bevor die Seite einfriert.
   *
   * ⚠️ NICHT hinter `isNativePlatform()`. Der Browser ist genau der Ort, an dem
   *    das Problem auftritt — dort gibt es keinen nativen Waechter, der es
   *    auffaengt.
   *
   * ⚠️ Gefahrlos fuer laufende Aufnahmen: releaseWarmMic steigt bei
   *    `recordingActive` sofort aus und laesst `dayRecorderBusy` in Ruhe.
   *    Wer gerade spricht, verliert nichts.
   */
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      releaseWarmMic("seite-verlassen");
    }
  });
  // Manche Browser schicken beim Schliessen/Wegschieben nur `pagehide`.
  window.addEventListener("pagehide", () => {
    releaseWarmMic("seite-weggeschoben");
  });
  /*
   * ⚠️⚠️ 22.09.2026 — AM DESKTOP FAELLT DAS NETZ OBEN AUS.
   *
   * `visibilityState` bleibt „visible", solange das Fenster irgendwo auf
   * dem Bildschirm steht — auch wenn daneben ein anderes Programm arbeitet
   * und niemand mehr auf Vespera schaut. Am Telefon ist „weggeschaltet"
   * dasselbe wie „unsichtbar"; am Desktop nicht.
   *
   * Vor pre.3151 war das folgenlos: im Browser wurde ohnehin nie
   * vorgewaermt. Jetzt hielte ein beiseitegeschobenes Fenster das Mikrofon
   * offen — und der Hinweis in der Menueleiste zeigt es systemweit an.
   *
   * ⚠️ Nur der Browser. Nativ gibt es kein zweites Fenster daneben, und die
   *    Audio-Session gehoert dort dem System.
   */
  if (!isNativePlatform()) {
    window.addEventListener("blur", () => {
      releaseWarmMic("fenster-nicht-vorn");
    });
  }
}

/**
 * Den nativen Weg eine Weile meiden. (05.09.2026, Martin im Auto)
 *
 * Das iPhone-Mikro hörte im fahrenden Auto Lärm und Navi („Wiener Nesadt"),
 * nicht Martin; der alte Weg nimmt über HFP das Auto-/Headset-Mikro, näher
 * am Mund. Verwirft der Server eine native Aufnahme als „nichts verstanden",
 * gehen die nächsten Aufnahmen den alten Weg — selbstkorrigierend, ohne
 * Schalter.
 */
let nativGemiedenBis = 0;
export function nativMeiden(ms: number): void {
  nativGemiedenBis = Date.now() + ms;
  meldeMikroAnlass("ptt-nativ-gemieden", `${Math.round(ms / 60000)}min`);
}

/** Ein Fehler als ein Feld einer Protokollzeile: Name, sonst Text, ohne Leerzeichen. */
function fehlerKurz(e: unknown): string {
  const roh =
    e instanceof Error ? (e.name && e.name !== "Error" ? e.name : e.message) : String(e);
  return roh.replace(/\s+/g, "-").slice(0, 60) || "?";
}

/**
 * Der Entwicklerschalter — oder null. (24.09.2026, Ticket 2202)
 *
 * ⚠️ Wirft der Speicher (Privatmodus), gilt „nicht gesetzt": dann entscheidet
 *    allein die Probe mit ihrem Enddatum. Ein Speicherfehler schaltet den
 *    nativen Weg also nie über die Probe hinaus ein.
 */
function leseNativSchalter(): string | null {
  try {
    return localStorage.getItem(NATIV_SCHALTER);
  } catch {
    return null;
  }
}

export class PttRecorder {
  /**
   * PTT aus der nativen Engine (05.09.2026). Gesetzt, solange die Aufnahme
   * dort läuft; die MediaRecorder-Felder bleiben dann leer.
   */
  private nativ: {
    stop(): Promise<{ base64: string; mime?: string }>;
    cancel(): Promise<void>;
    pegel(): Promise<number>;
  } | null = null;
  private nativLaeuft = false;
  private letzterWarNativ = false;
  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startTs = 0;
  private mimeType = "audio/webm";
  private autoStopTimer: ReturnType<typeof setTimeout> | null = null;
  // 2026-07-04: cancel() kann WAEHREND des async Mikro-Starts kommen (kurzer
  // Tipp). Dann darf der verspaetete Start KEINE Aufnahme mehr beginnen.
  private cancelRequested = false;
  /** Ticket 2882: der laufende Start (bis die Aufnahme steht oder scheitert). */
  private startLauf: Promise<void> | null = null;
  /** Ticket 2882: zählt jedes `cancel()` — ein wartender Start sieht daran, dass er selbst gemeint war. */
  private abbruchNr = 0;
  /*
   * ── Ticket 2518 (24.09.2026): die tote warme Leitung MITTEN im Druck ──
   * Woher die Leitung kam, was der Recorder davon geliefert hat, die EINE
   * Prüfung je warmem Druck und ein laufender Wechsel (Loslassen wartet).
   * `druckNr` zählt jedes Aufräumen: ein Wechsel, dessen Druck inzwischen
   * zu Ende ist, darf nichts mehr eintragen.
   */
  private weg: LeitungsWeg = "kalt";
  private recorderBytes = 0;
  private leitungsPruefung: ReturnType<typeof setTimeout> | null = null;
  /**
   * Ticket 2643 (26.09.2026): die EINE Prüfung auf eine stumme Quelle je
   * Leitung — nach dem Start und, nach einem Tausch, auf der neuen.
   */
  private stummPruefung: ReturnType<typeof setTimeout> | null = null;
  private leitungsWechsel: Promise<void> | null = null;
  private druckNr = 0;
  /** Wie oft der Recorder bis jetzt geliefert hat — für die Wechselzeile. */
  private recorderTeile = 0;
  /** Ticket 2672: zählt die Rekorder dieses Laufs — ein neuer beginnt eine neue Datei. */
  private recorderNr = 0;
  /*
   * Der Wechsel fand kein Mikro (oder keinen Recorder): der Druck läuft
   * OHNE Leitung weiter, bis der Finger loslässt. (Nachprüfung 24.09.2026)
   * Sofort aufzuräumen hieß: `isRecording()` sagte false, während app.ts
   * noch „recording“ führte — das Loslassen hielt das für „Mikro noch
   * nicht da“ und rastete die Aufnahme ein, jeder weitere Tipp wieder.
   */
  private ohneLeitung = false;
  /**
   * Pre.489: Callback der gefeuert wird wenn die Aufnahme automatisch
   * stoppt (nach PTT_MAX_MS) statt durch expliziten User-Stop. Caller
   * (Modal) kann dann auto-send + Modal-Cleanup triggern statt blind
   * weiterlaufenden Timer zu zeigen.
   */
  public onAutoStop: (() => void) | null = null;
  /**
   * 30.10.2026 (Aufnahme-Wächter): die Aufnahme steht, ohne dass jemand sie
   * beendet hat. Das Bisherige ist noch da (`stopAndGet` liefert es); der
   * Aufrufer meldet es dem Menschen und sendet.
   */
  public onAusfall: ((a: AusfallInfo) => void) | null = null;
  /** 30.10.2026: die Seite kam nach einer Lücke zurück, in der nichts aufgenommen wurde. */
  public onRueckkehr: ((r: RueckkehrInfo) => void) | null = null;
  private readonly wachter: AufnahmeWachter = erzeugeAufnahmeWachter({
    lage: () => {
      const spur = this.mediaStream?.getAudioTracks()[0] ?? null;
      return {
        bytes: this.recorderBytes,
        laeuft: this.recorder?.state === "recording",
        spurLebt: spur === null || spur.readyState === "live",
        spurStumm: spur?.muted === true,
        pausiert: this.recorder === null || !!this.leitungsWechsel || !!this.teilWechsel || this.ohneLeitung,
      };
    },
    jetzt: () => Date.now(),
    setzeTakt: (fn, ms) => setInterval(fn, ms),
    loescheTakt: (k) => clearInterval(k as ReturnType<typeof setInterval>),
    beiAusfall: (a) => {
      try {
        this.onAusfall?.(a);
      } catch {
        /* die Anzeige darf den Wächter nicht stören */
      }
    },
    beiRueckkehr: (r) => {
      try {
        this.onRueckkehr?.(r);
      } catch {
        /* wie oben */
      }
    },
    spaeter: (fn, ms) => setTimeout(fn, ms),
    loescheSpaeter: (k) => clearTimeout(k as ReturnType<typeof setTimeout>),
    zeile: (z) => meldeMikroAnlass("ptt-wachter", z),
  });
  /** Spuren, an denen der Wächter schon horcht. */
  private wachterSpuren = new WeakSet<MediaStreamTrack>();
  /** Der Druck endet: ein später fertiger Leitungswechsel startet den Wächter nicht neu. */
  private wachterGesperrt = false;
  /** Die Seite ist wieder sichtbar — der Wächter prüft einen Zeitsprung. */
  private readonly beiSichtbar = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") this.wachter.sichtbar();
  };
  /**
   * Ticket 2618 (29.09.2026): erkennt einen fertigen Teil eines langen
   * Diktats. Ohne ihn gibt es keine Teile — an der Höchstdauer endet die
   * Aufnahme wie bisher (onAutoStop).
   */
  public erkenneTeil: TeilErkenner | null = null;
  /** Ticket 2618: Teil `nr` (ab 2) läuft — der vorige ist aufgehoben. */
  public onTeil: ((nr: number) => void) | null = null;
  /** Ticket 2618: die früheren Teile DIESES Drucks — null, solange es keine gibt. */
  private vorlauf: DiktatVorlauf | null = null;
  /** Ticket 2618: ein laufender Teilwechsel — das Loslassen wartet auf ihn. */
  private teilWechsel: Promise<void> | null = null;
  /** Ticket 2618: wann der laufende Teil begann (Web-Weg) — 0 ohne Teile-Uhr. */
  private teilSeit = 0;

  /**
   * 2026-08-28 (Martin: "ein Mikrofonzeichen"): Der laufende Mikrofon-Strom,
   * damit die Live-Erkennung MITLESEN kann, statt ein zweites Mikrofon zu
   * oeffnen.
   *
   * ⚠️ Zwei parallele getUserMedia sind auf iOS die Abriss-Ursache (siehe
   * Single-Flight weiter oben). Ein MediaStream vertraegt dagegen mehrere
   * Leser problemlos: der MediaRecorder schneidet die Datei mit, die
   * Erkennung liest denselben Strom.
   *
   * Wer ihn ausleiht, darf seine Spuren NICHT stoppen — das taete der
   * Aufnahme weh. Dafuer gibt es `LiveDictation.start(geliehen)`.
   */
  getStream(): MediaStream | null {
    return this.mediaStream;
  }

  /**
   * Welchen Weg dieser Druck nimmt — als drei Felder der Messzeile.
   * (24.09.2026, Ticket 2202)
   *
   * `weg=nativ|web` ist das Merkmal, nach dem die Probewoche verglichen wird;
   * `tages=` trennt die Drücke, bei denen die Tagesaufnahme lief (nur dort
   * gibt es überhaupt eine Wahl); `wahl=` sagt, warum — damit ein Web-Druck
   * mitten in der Probe nicht wie ein Fehler aussieht, wenn er bloß
   * gemieden oder abgelehnt wurde.
   */
  private merkeWeg(weg: "nativ" | "web", tages: boolean, wahl: WegGrund): void {
    pttTiming.set("weg", weg);
    pttTiming.set("tages", tages ? "ja" : "nein");
    pttTiming.set("wahl", wahl);
  }

  /**
   * Der native Weg: kein getUserMedia, kein Profilwechsel, keine Pause der
   * Tagesaufnahme VOR dem Start. Nur, wenn die Hülle ihn kennt UND die
   * Tagesaufnahme das Mikro gerade hält — sonst lohnt er nicht. Jeder
   * Fehler → false, und der Aufrufer geht den alten Weg.
   *
   * (24.09.2026, Ticket 2202: NACH dem Start hält er die Tagesaufnahme an
   * wie der Web-Weg — siehe halteTagesaufnahmeAn.)
   *
   * ⚠️⚠️ 24.09.2026 (Ticket 2202) — EINE WOCHE WIEDER AN, als Probe.
   *
   * Martins Entscheidung, Variante C: „Erst messen — ich schalte ihn
   * probeweise ein und vergleiche eine Woche." Die Regel (wer, wann, bis
   * wann) steht in ptt-weg.ts; hier bleibt, was sich erst am Mikrofon zeigt.
   *
   * Der Grund, warum sich die Rechnung vom 08.09. (unten) gedreht hat: Der
   * Vergleich damals war 187 ms nativ gegen 464 ms kalt. Gemessen am 21.09.
   * kostet der kalte Weg BEI LAUFENDER TAGESAUFNAHME 1.521 bis 1.749 ms —
   * das Vorwärmen ist dann gesperrt (Ticket 570), jeder Druck ist kalt. Der
   * Unterschied ist rund 1,3 Sekunden, nicht 0,28.
   *
   * ⚠️ Außerhalb einer laufenden Tagesaufnahme ändert sich nichts: dort wählt
   *    die Regel immer den Web-Weg, und die neuen Felder stehen nur mit in
   *    der Zeile.
   */
  private async starteNativ(): Promise<boolean> {
    const tages = tagesZustand === "recording";
    const wahl = wegWahl({
      jetzt: Date.now(),
      nativeApp: isNativePlatform(),
      tagesaufnahme: tages,
      anruf: anrufLaeuft,
      hauptnutzer: istHauptnutzer(),
      schalter: leseNativSchalter(),
      gemiedenBis: nativGemiedenBis,
    });
    if (!wahl.nativ) {
      this.merkeWeg("web", tages, wahl.grund);
      return false;
    }
    /*
     * ⚠️ Ab hier steht der Weg erst fest, wenn der native Start gelungen oder
     *    gescheitert ist — jeder Ausgang unten trägt ihn ein. Vorher stünde
     *    in einer `[ptt-ende]`-Zeile, die während des Starts kommt (Abbruch),
     *    ein Weg, den dieser Druck gar nicht genommen hat.
     */
    /*
     * ⚠️⚠️ 08.09.2026 — der native Weg ist ab jetzt AUSGESCHALTET, bis er
     *      zeigen kann, was der alte Weg zeigt.
     *
     * Martin: *„Kannst Du mir sagen, warum der Visualizer beim Push to Talk
     * jetzt nicht mehr funktioniert und warum der Text sich nicht mehr
     * erzeugt und es danach sehr lange dauert? Warum ist es ploetzlich
     * anders, verdammt noch mal?"*
     *
     * Weil die TAGES-AUFNAHME seit heute wieder laeuft. Der native Weg wird
     * nur genommen, solange sie das Mikro haelt (`dayRecorderBusy` in der
     * Zeile darueber) — und sie lief tagelang nicht. Im Journal gezaehlt:
     *
     *     06.09.  22 x cold,  3 x warm,  0 x nativ
     *     07.09.  21 x cold,  5 x warm,  0 x nativ
     *     08.09.   1 x cold,  0 x warm, 10 x nativ
     *
     * Der native Weg hat keinen MediaStream (siehe `this.nativ` — „die
     * MediaRecorder-Felder bleiben dann leer"). Daran haengen BEIDE Dinge,
     * die Martin vermisst: die Live-Mitschrift wartet auf `getStream()` und
     * gibt nach vier Sekunden auf, und der Pegelbalken hat keine Tonspur.
     * Und weil keine Mitschrift entsteht, muss der Server hinterher die
     * ganze Datei erkennen — das ist das „dauert sehr lange".
     *
     * Er kauft dafuer 277 ms Startzeit (gemessen: 187 ms nativ gegen 464 ms
     * kalt). Das ist der schlechteste Handel im ganzen Haus.
     *
     * ⚠️ NICHT geloescht, sondern abgeschaltet: Auf Bluetooth war der alte
     *    Weg gemessen 0,6 bis 4,5 s langsam (05.09.), dafuer wurde der
     *    native Weg ueberhaupt gebaut. Wer ihn zurueckholt, muss vorher die
     *    Mitschrift dorthin bringen — nicht vorher.
     *
     * (24.09.2026: Die Probe holt ihn OHNE Mitschrift zurück — das ist der
     *  Preis, den Martin mit Variante C ausdrücklich angenommen hat, und die
     *  Messung soll zeigen, ob er sich lohnt. Siehe oben.
     *  ⚠️ Den Satz „muss der Server hinterher die ganze Datei erkennen"
     *  trägt der Code nicht mehr: seit dem 04.09. erkennt der Server die
     *  Datei auf BEIDEN Wegen (voice-command.ts, `liveAlsRueckfall`). Den
     *  Pegelbalken hat der native Weg seit Ticket 2442 über `pegelNativ`.
     *  Was wirklich fehlt, ist der Text beim Sprechen — und damit das
     *  Auslösewort; „Absenden nach Stille" geht nativ über den Pegel.)
     */
    try {
      const m = await import("./native-recorder.js");
      /*
       * ⚠️ Erst den ECHTEN Zustand holen. Der gespiegelte kann veraltet sein —
       *    nach einer Unterbrechung durch iOS oder der Rückkehr aus dem
       *    Hintergrund kam das Ereignis vielleicht nie an. Dieselbe Lehre wie
       *    in `pauseDayRecorderForMic` (25.07.): Die Engine nimmt den
       *    nativen Start auch bei „paused" an (PawbotRecorderPlugin.swift,
       *    pttStart) — dann liefe ein zweiter Rekorder auf einer angehaltenen
       *    Sitzung, mitten in ihren Fortsetz-Versuchen.
       *
       *    Mit demselben Deckel wie dort (400 ms): eine hängende Brücke darf
       *    den Druck nicht festhalten. Kam der Abgleich nicht rechtzeitig,
       *    gilt der gespiegelte Zustand.
       */
      await Promise.race([
        m.syncFromNative().catch(() => undefined),
        new Promise<void>((res) => window.setTimeout(res, 400)),
      ]);
      if (tagesZustand !== "recording") {
        this.merkeWeg("web", false, "abgleich");
        return false;
      }
      /*
       * ⚠️ Übergabe Tagesaufnahme ↔ Sprechtaste: Hat sich gerade jemand
       *    eingetragen, der die Tagesaufnahme anhält (Diktat, Wiedergabe,
       *    Anruf — `pauseDayRecorderForMic`), gehört das Mikro ihm. Der
       *    Zustand kann dann noch „recording" melden, weil das Anhalten
       *    selbst ein `await` braucht. Dann bleibt es beim alten Weg.
       */
      if (m.hatOffenePausenGruende()) {
        this.merkeWeg("web", tages, "pausengrund");
        return false;
      }
      const p = m.holePttNativ();
      if (!p) {
        this.merkeWeg("web", tages, "alte-app");
        return false;
      }
      pttTiming.set("path", "nativ");
      await p.start();
      /*
       * ⚠️ Derselbe Riegel wie beim Web-Weg („Race-Guard" in start()): Kam
       *    während des Starts ein Abbruch, darf hier keine Aufnahme mehr
       *    beginnen. Bisher fehlte er, weil der Weg aus war — ohne ihn liefe
       *    die native Aufnahme verwaist weiter, und die Tagesaufnahme hielte
       *    sie für einen, der gerade spricht (native-recorder.ts,
       *    pttNativHaelt).
       */
      if (this.cancelRequested) {
        void p.cancel().catch(() => undefined);
        return true;
      }
      this.merkeWeg("nativ", tages, wahl.grund);
      this.nativ = p;
      this.nativLaeuft = true;
      this.letzterWarNativ = true;
      this.mimeType = "audio/mp4";
      this.chunks = [];
      this.startTs = Date.now();
      pttTiming.mark("rec");
      this.autoStopTimer = setTimeout(() => {
        if (!this.nativLaeuft) return;
        try {
          this.onAutoStop?.();
        } catch {
          /* egal */
        }
      }, PTT_MAX_MS);
      await this.halteTagesaufnahmeAn(m);
      pttTiming.flush();
      return true;
    } catch (e) {
      meldeMikroAnlass("ptt-nativ-rueckfall", String(e).slice(0, 80));
      this.merkeWeg("web", tages, "startfehler");
      this.nativ = null;
      this.nativLaeuft = false;
      return false;
    }
  }

  /**
   * Die Tagesaufnahme anhalten, sobald die native Sprechtaste aufnimmt.
   * (24.09.2026, Ticket 2202 — Befund der unabhängigen Prüfung)
   *
   * ⚠️⚠️ SONST STEHT JEDE FRAGE AN VESPERA ZWEIMAL IM ALLTAG.
   *
   * Der erste Bau ließ die Tagesaufnahme bei nativen Drücken weiterlaufen.
   * Sie schnitt also jeden Satz an Vespera mit — und die Alltag-Auswertung
   * (server/alltag-absichten.ts) liest darin Martins Du-Sätze: „Leg ein
   * Ticket an: …" wurde dort ein ZWEITES Ticket samt Chatzeile und Push,
   * eine Einkaufsliste eine zweite Erinnerungsfrage. Denselben Mechanismus
   * gab es schon einmal: Ticket 2097 (05.09., aus der Alltag-Aufnahme)
   * wiederholte Ticket 2092 (aus dem Chat).
   *
   * Darum hält die native Sprechtaste die Tagesaufnahme genau so an wie der
   * Web-Weg: über `pauseDayRecorderForMic("ptt")`, mit demselben Grund und
   * demselben Fortsetzen in `cleanup()`. EIN Besitzer für „die Sprechtaste
   * hat die Tagesaufnahme angehalten".
   *
   * ⚠️ Die Reihenfolge ist der Unterschied zum Web-Weg: ERST der native
   *    Start, DANN die Pause. So startet die Sprechtaste noch auf dem
   *    offenen Mikro der Tagesaufnahme — dafür gibt es die Probe überhaupt.
   *    Die Pause selbst fasst die Tonsitzung nicht an (die Engine schließt
   *    nur ihr Häppchen ab, PawbotRecorderEngine.pause); der zweite
   *    Rekorder läuft weiter.
   *
   *    Der Preis: Zwischen dem Start und dem Anhalten hört die Tagesaufnahme
   *    noch mit — wenige Brückenaufrufe lang, meist bevor das erste Wort
   *    fällt. Die Messzeile trägt ihn je Druck: `tagesHalt=+Nms` neben
   *    `rec=+Nms` (die Differenz ist die Überlappung), `tagesHalt=offen`,
   *    wenn das Anhalten länger als 400 ms brauchte, `tagesHalt=nein`, wenn
   *    es nichts anzuhalten gab.
   *
   * ⚠️ Mit Deckel wie beim Web-Weg (400 ms) — eine hängende Brücke darf den
   *    Druck nicht festhalten. Kommt das Anhalten erst, wenn der Druck schon
   *    vorbei ist, hat `cleanup()` vergeblich fortgesetzt (der Grund stand
   *    noch nicht drin). Dann setzt es hier fort — sonst läge die
   *    Tagesaufnahme bis zur Notbremse (5 min) still.
   */
  private async halteTagesaufnahmeAn(
    m: typeof import("./native-recorder.js"),
  ): Promise<void> {
    const halt = m.pauseDayRecorderForMic("ptt").then(
      (angehalten) => {
        // `recordingActive` fällt erst in cleanup() — und steigt beim
        // nächsten Druck wieder. Solange irgendein Druck läuft, setzt dessen
        // eigenes Aufräumen fort, nicht diese Zeile.
        if (angehalten && !recordingActive) {
          void m.resumeDayRecorderAfterMic("ptt");
        }
        return angehalten;
      },
      () => false,
    );
    const ergebnis = await Promise.race([
      halt,
      new Promise<"offen">((res) => window.setTimeout(() => res("offen"), 400)),
    ]);
    if (ergebnis === true) pttTiming.mark("tagesHalt");
    else pttTiming.set("tagesHalt", ergebnis === false ? "nein" : "offen");
  }

  /**
   * Läuft schon ein Start — er wartet noch auf das Mikrofon, die Tagesaufnahme
   * oder die Sitzung? (Ticket 2882) Für die Messzeile und die Tests.
   */
  startLaeuft(): boolean {
    return this.startLauf !== null;
  }

  /**
   * Der Start einer Aufnahme — EIN Start je Sprechtaste, auch im Anlauf.
   * (01.10.2026, Ticket 2882)
   *
   * ⚠️⚠️ Die Sperre steht im ERSTEN Schritt, nicht erst, wenn das Mikrofon da
   *    ist. Bis zum 01.10. prüfte `start()` nur `this.recorder` — den setzt erst
   *    `starteRecorder()` NACH dem Mikrofon (gemessen: `gumEnd=+1804ms`, bis
   *    4 s). Ein zweiter Aufruf in diesem Fenster fand alles leer, nahm
   *    dieselbe Mikrofon-Anfrage und legte einen zweiten MediaRecorder an; der
   *    erste lief verwaist weiter. Dazu setzte der zweite Aufruf
   *    `cancelRequested` zurück und machte so einen abgebrochenen Start wieder
   *    scharf.
   *
   * Jetzt teilt sich jeder Aufruf im Anlauf den laufenden Start. Nur wenn der
   * laufende schon abgebrochen ist (kurzer Tipp), wartet der neue, bis jener
   * sein Mikrofon wieder freigegeben hat — und beginnt dann frisch.
   */
  start(): Promise<void> {
    const laeuft = this.startLauf;
    if (laeuft) {
      if (!this.cancelRequested) return laeuft;
      // Wer wartet, dem gilt ein weiterer Abbruch ebenso: er beginnt dann gar nicht.
      const nr = this.abbruchNr;
      return laeuft.catch(() => undefined).then(() => (this.abbruchNr === nr ? this.start() : undefined));
    }
    // Ticket 2518: ein laufender Leitungswechsel IST der laufende Druck —
    // und ebenso einer, dessen Wechsel ohne Mikro blieb.
    if (this.recorder || this.nativ || this.leitungsWechsel || this.ohneLeitung) {
      return Promise.resolve();
    }
    const lauf: Promise<void> = this.starteAufnahme().finally(() => {
      if (this.startLauf === lauf) this.startLauf = null;
    });
    this.startLauf = lauf;
    return lauf;
  }

  private async starteAufnahme(): Promise<void> {
    this.cancelRequested = false;
    this.wachterGesperrt = false;
    // Nach Aktualisieren laeuft die Vorwaermung parallel zum ersten Druck.
    // Anspruch SOFORT, sonst schaltet warmUpMic die Spuren stumm (0 ms Ton).
    recordingActive = true;
    /*
     * ⚠️ 23.09.2026 (Ticket 2524): Ab hier schaltet die Tonanlage die native
     *    Sitzung NICHT mehr um — auch nicht, wenn ihre Gnadenfrist abläuft.
     *    12:57:19 ging das Mikro auf, 12:57:20 wurde umgeschaltet, danach
     *    54 s spitze=0. Jede Stelle, die `recordingActive` wieder löscht,
     *    löst auch diese Sperre (alle vier stehen in dieser Klasse).
     */
    // Ticket 2941: klingt eine Antwort noch aus (der Kontext läuft 150–500 ms nach ihrem Ende weiter), schläft er jetzt —
    // ein `suspend()` mitten im Wechsel der Sitzung, den die nächste Zeile auslöst, war Ticket 2524.
    ausklangBeenden();
    setSeitenAufnahme(true);
    // 17.10.2026 (lib/fertig.ts): neues Sprechen hebt „Fertig“ sofort auf — schon beim Aufnahme-Beginn, nicht erst beim Senden.
    // Nur der Merker fällt; hier öffnet sich nichts zusätzlich.
    loescheFertig();
    if (await this.starteNativ()) return;
    // 25.09.2026: lag die Sitzung noch auf .playback, erst zurück zur
    // Aufnahme — sonst nimmt getUserMedia Stille auf (playback-hifi.ts).
    const sitzung = warteAufAufnahmeSitzung();
    if (sitzung) await sitzung;
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      recordingActive = false;
      setSeitenAufnahme(false);
      throw new Error("media-devices-unavailable");
    }
    // Pre.834 (Martins „jedes Mal Mikro bestätigen"): einen WARMEN Stream
    // wiederverwenden — iOS (PWA) zeigt sonst bei JEDEM getUserMedia den
    // Erlaubnis-Dialog. Der Stream bleibt nach der Aufnahme 90s erhalten
    // (Track disabled = stumm) und wird dann wirklich freigegeben.
    // 2026-07-10 (Martin, iPhone-Mikro-Saga Teil 3): ein WARMER Stream vom
    // falschen Geraet (Continuity-iPhone) wird am Desktop verworfen — sonst
    // konserviert das Warmhalten das stille Mikro, obwohl der Kaltstart
    // laengst das MacBook-Mikro waehlen wuerde (bewiesen: warm=iPhone 0.14,
    // kalt=MacBook 1.0).
    // 25.09.2026: in der App nie die warme Spur (WARMES_MIKRO_IN_DER_APP).
    // releaseWarmMic greift hier nicht (recordingActive steht schon) — darum
    // direkt: Spuren schließen, dann nimmt der Druck den kalten Weg.
    if (warmStream && isNativePlatform() && !WARMES_MIKRO_IN_DER_APP) {
      if (warmStreamTimer) {
        clearTimeout(warmStreamTimer);
        warmStreamTimer = null;
      }
      warmStream.getTracks().forEach((t) => t.stop());
      warmStream = null;
      warmSeit = 0;
      merkeWarmGrund("app-kalt");
    }
    /*
     * 19.10.2026 (Mikro-Vorstart): ein vorgezogener Strom wird hier zum Strom dieser Sprechtaste — über den warmen Weg darunter
     * (Spur an, Leitungsprüfung im Druck). Ist ein warmer da, gewinnt er und der vorgezogene geht zu. `vorstart=` steht in
     * `[ptt-t]`; läuft der Abruf noch, teilt sich der kalte Weg unten ihn (Single-Flight) — auch das zählt als `vorstart=ja`.
     */
    const vorstartWar = vorstartInfo;
    const vorgezogen = nimmVorstart();
    if (vorgezogen) {
      if (warmStream && warmStream.getTracks().some((t) => t.readyState === "live")) {
        vorgezogen.getTracks().forEach((t) => t.stop());
      } else {
        warmStream = vorgezogen;
        warmSeit = Date.now();
      }
    }
    if (vorstartWar) {
      pttTiming.set("vorstart", "ja");
      pttTiming.set("vorstartMs", `${Math.round(performance.now() - vorstartWar.t0)}`);
      pttTiming.set("restMs", `${vorstartWar.restMs}`);
    } else {
      pttTiming.set("vorstart", "nein");
      const warum = letzterVorstartGrund();
      if (warum) pttTiming.set("vorstartGrund", warum);
    }
    vorstartInfo = null;
    if (
      warmStream &&
      warmStream.getTracks().some((t) => istFremdesGeraet(t.label))
    ) {
      releaseWarmMic("falsches-geraet");
    }
    if (
      warmStream &&
      warmStream.getTracks().some((t) => t.readyState === "live")
    ) {
      warmStream.getTracks().forEach((t) => (t.enabled = true));
      this.mediaStream = warmStream;
      this.weg = "warm";
      pttTiming.set("path", "warm");
      /*
       * 23.09.2026 (Tickets 2518/2519): Um 09:12:48 kam die App aus dem
       * Hintergrund, holte ein FRISCHES Warm-Mikro — und das lieferte ab dem
       * ersten Druck Spitze 0. Warum, ist nicht belegt. `muted` ist das
       * Zeichen, mit dem der Browser „die Quelle liefert gerade nichts"
       * meldet; ob es hier steht, entscheidet, ob man es VOR dem Druck
       * erkennen kann. Erst messen, dann bauen.
       */
      pttTiming.set(
        "spurStumm",
        warmStream.getAudioTracks().some((t) => t.muted) ? "ja" : "nein",
      );
      pttTiming.set(
        "warmAlter",
        warmSeit ? `${Math.round((Date.now() - warmSeit) / 1000)}s` : "?",
      );
    } else {
      // 2026-07-07 (Martin, PTT 3s + wachsend): Roh-Audio OHNE iOS Voice-
      // Processing-Unit (echoCancellation/noiseSuppression/autoGainControl).
      // 2026-07-09 (Audit): via Single-Flight — teilt sich die Anfrage mit
      // einer evtl. laufenden Vorwaermung statt zu racen.
      pttTiming.set("path", "cold");
      this.weg = "kalt";
      // Ticket 2202: WARUM kalt. Ohne das ist jede Reparatur geraten.
      pttTiming.set("kalt", warmGrund);
      pttTiming.set(
        "kaltSeit",
        warmGrundAt && typeof performance !== "undefined"
          ? `${Math.round(performance.now() - warmGrundAt)}ms`
          : "?",
      );
      pttTiming.set("nativ", isNativePlatform() ? "ja" : "nein");
      pttTiming.set("overlapWarmup", micAcquire ? "yes" : "no");
      pttTiming.set(
        "lastTtsEnd",
        lastTtsEndAt > 0
          ? `-${Math.round(performance.now() - lastTtsEndAt)}ms`
          : "nie",
      );
      // #519 / 06.07.: Tagesaufnahme weich pausieren, höchstens 400 ms.
      await pausiereTagesaufnahmeFuerMikro();
      pttTiming.mark("pause");
      /*
       * ⚠️⚠️ 22.09.2026 (Ticket 2202) — STAND DA EIN DIALOG ODER NICHT?
       *
       * Heute um 10:42 brauchte ein Mikro-Start bei Albert 13.740 ms.
       * Nachgemessen an 322 Starts seit dem 15.09.: 33 davon (10 %) liegen
       * ueber zwei Sekunden, und ALLE acht langsamsten tragen
       * `path=cold nativ=nein` — im Browser, mit kaltem Mikro. Nativ ist
       * das Maximum 3.025 ms, im Browser 13.740 ms.
       *
       * Die Ursache dafuer ist gefunden: der ganze Vorwaerm-Block in dieser
       * Datei steht hinter `isNativePlatform()`. Im Browser wird NIE
       * vorgewaermt.
       *
       * ⚠️ Daraus folgt aber noch NICHT, dass Vorwaermen hilft. Steht die
       *    Berechtigung nicht, zeigt der Browser einen Dialog — und dann
       *    sind die 13,7 Sekunden die Bedenkzeit eines Menschen, kein
       *    technisches Problem. Ein Vorwaermer wuerde den Dialog nur
       *    frueher zeigen, nicht schneller machen.
       *
       *    Beides sieht in der Messung IDENTISCH aus. Genau dieser
       *    Unterschied entscheidet, ob eine Reparatur etwas bringt — also
       *    wird er ab jetzt mitgeschrieben, statt geraten.
       */
      try {
        const nav = navigator as Navigator & {
          permissions?: {
            query?: (d: { name: string }) => Promise<{ state?: string }>;
          };
        };
        const st = await nav.permissions?.query?.({ name: "microphone" });
        if (st?.state) pttTiming.set("erlaubnis", st.state);
      } catch {
        // Firefox und Safari kennen die Abfrage fuer „microphone" nicht —
        // dann bleibt die Zeile weg. Eine fehlende Angabe ist ehrlicher
        // als eine erfundene.
      }
      pttTiming.mark("gumStart");
      uebernommenerAbruf = micAcquire; // läuft schon ein Abruf (Vorstart, Vorwärmen), teilt sich der Druck ihn und besitzt dann den Strom
      try {
        this.mediaStream = await acquireRawMic();
      } catch (e) {
        recordingActive = false;
        setSeitenAufnahme(false);
        throw e;
      }
      pttTiming.mark("gumEnd");
      // 19.10.2026: wie lange eine echte kalte Öffnung dauert, bestimmt den Vorlauf des Mikro-Vorstarts (nie ein vorgezogener Start).
      if (!vorstartWar) {
        const ende = pttTiming.marks.find(([n]) => n === "gumEnd");
        if (ende) merkeMikroOeffnung(ende[1]);
      }
      /*
       * ⚠️⚠️ 10.09.2026 (Ticket 2202) — WELCHES Mikrofon war es?
       *
       * Gemessen: getUserMedia braucht mal 278 ms und mal 1.766 ms, und der
       * Unterschied entsteht INNERHALB des Aufrufs. Das Muster passt genau
       * auf den am 05.09. bewiesenen Bluetooth-Profilwechsel (Lautsprecher
       * 320-400 ms, A2DP 1,5-2 s) — beweisen liess es sich nicht, weil
       * nirgends steht, an welchem Geraet das Telefon gerade hing.
       *
       * Der Name des Tracks sagt es und kostet nichts. Er wird gekuerzt und
       * entschaerft: Die Zeile ist mit Leerzeichen getrennt, ein
       * „AirPods Pro von Martin" waere sonst drei Felder.
       */
      pttTiming.set("mik", geraeteName(this.mediaStream));
      // 23.09.2026 (Tickets 2496/2506/2527): und wohin die Stimme zuletzt
      // ging. Mit Bluetooth-Ausgabe kostet das Öffnen einen Profilwechsel —
      // am Mikrofonnamen allein war das nicht zu sehen.
      pttTiming.set("aus", letzterWiedergabeWeg() ?? "?");
      this.trageFrischesMikroEin();
      // Die Seite hat das Mikrofon bekommen — ab jetzt gibt es keinen Dialog
      // mehr, also darf vorgewaermt werden (auch dort, wo `permissions`
      // fehlt: Safari, iPhone-Safari — genau Alberts Faelle).
      merkeMikroErlaubnis();
      // ⚠️ Nur HIER: dieser Weg laeuft nur, wenn jemand die Sprechtaste
      //    gedrueckt hat. Ein Vorwaermen zaehlt nicht als „gesprochen".
      schonGesprochen = true;
    }
    if (warmStreamTimer) {
      clearTimeout(warmStreamTimer);
      warmStreamTimer = null;
    }
    // Race-Guard: waehrend getUserMedia lief, kam cancel() (kurzer Tipp) —
    // Stream still freigeben, NICHT aufnehmen.
    if (this.cancelRequested) {
      recordingActive = false;
      for (const t of this.mediaStream.getTracks()) t.stop();
      if (this.mediaStream === warmStream) warmStream = null;
      this.mediaStream = null;
      // Ticket 2524: erst das Mikro weg, dann die Sperre der Tonanlage lösen.
      // Meist schon geschehen — cancel() läuft über cleanup(). Die Zeile
      // bleibt, damit `recordingActive` nirgends ohne die Sperre fällt.
      setSeitenAufnahme(false);
      return;
    }
    this.letzterWarNativ = false;
    this.mimeType = pickMimeType();
    /*
     * ⚠️ 23.09.2026 (Ticket 2524, Befund des Prüfers): Warf `new
     *    MediaRecorder` oder `start()`, blieb `recordingActive` stehen — eine
     *    Zombie-Aufnahme, die bisher nur das Vorwärmen blockierte. Seit die
     *    Tonanlage an derselben Stelle gesperrt wird, hätte sie auch jede
     *    Hi-Fi-Umschaltung eingefroren. Deshalb hier durch das EINE
     *    Aufräumen (cleanup), das auch die Sperre löst.
     */
    try {
      this.startTs = Date.now();
      this.starteRecorder();
    } catch (e) {
      this.recorder = null;
      this.cleanup();
      throw e;
    }
    pttTiming.mark("rec");
    pttTiming.flush();
    // Ticket 2518: nur der warme Weg, und genau EINE Prüfung je Druck.
    if (this.weg === "warm") {
      this.leitungsPruefung = setTimeout(
        () => this.pruefeLeitungImDruck(),
        WECHSEL_NACH_MS,
      );
    }
    // Ticket 2643: warm wie kalt — die stumme Quelle kam auf dem kalten Weg.
    this.planeStummPruefung();
    // Ticket 2618: nach PTT_MAX_MS ein neuer Teil — oder, wo das nicht geht,
    // wie bisher anhalten und die Karte informieren.
    this.planeTeilGrenze();
  }

  /** Ticket 2618: ein Teil beginnt — seine Uhr läuft ab jetzt. */
  private planeTeilGrenze(): void {
    this.teilSeit = Date.now();
    this.planeTeilVersuch(PTT_MAX_MS - PTT_TEIL_WECHSEL_VOR_MS);
  }

  private planeTeilVersuch(inMs: number): void {
    this.autoStopTimer = setTimeout(() => this.anDerTeilGrenze(), Math.max(0, inMs));
  }

  /**
   * Der laufende Teil nähert sich der Höchstdauer. (29.09.2026, Ticket 2618,
   * nachgebessert nach dem Prüfbefund)
   *
   * PTT_TEIL_WECHSEL_VOR_MS davor beginnt der Wechsel in einen neuen Teil
   * (wechsleTeil, nebeneinander). Gelingt er, geht der fertige Teil in den
   * Vorlauf (seine Erkennung beginnt sofort), und die Uhr beginnt von vorn.
   * Lieferte der neue Rekorder nicht, nimmt der alte weiter auf, und alle
   * TEIL_WIEDER_MS gibt es einen neuen Versuch — an der Höchstdauer selbst
   * den letzten, nacheinander. Erst wenn auch der nichts liefert, endet die
   * Aufnahme wie vor Ticket 2618; die Karte hat 30 s vorher gewarnt
   * (ptt-modal.ts, an der Uhr DIESES Teils).
   *
   * ⚠️ Ohne Erkenner gibt es keine Teile: das Ende kommt an der Höchstdauer,
   *    ohne `await`, im selben Takt wie vor Ticket 2618.
   */
  private anDerTeilGrenze(): void {
    this.autoStopTimer = null;
    const nr = this.druckNr;
    const erkenne = this.erkenneTeil;
    const bisEnde = this.teilSeit + PTT_MAX_MS - Date.now();
    if (!erkenne) {
      if (bisEnde > 0) this.planeTeilVersuch(bisEnde);
      else this.hoechstdauerErreicht();
      return;
    }
    const nacheinander = bisEnde <= 0;
    const lauf: Promise<void> = (async (): Promise<void> => {
      const w = await this.wechsleTeil(nacheinander);
      // Inzwischen losgelassen oder abgebrochen: der Teil gehört niemandem mehr.
      if (this.druckNr !== nr) return;
      if (w) {
        if (w.teil) {
          this.vorlauf ??= new DiktatVorlauf(erkenne);
          this.vorlauf.nimm(w.teil, this.mimeType);
        }
        const teilNr = (this.vorlauf?.anzahl ?? 0) + 1;
        meldeMikroAnlass(
          "ptt-teil",
          `nr=${teilNr} bytes=${w.teil?.size ?? 0} art=${w.art}` +
            (w.art === "nacheinander" ? ` luecke=${w.luecke}ms` : ""),
        );
        try {
          this.onTeil?.(teilNr);
        } catch {
          /* die Anzeige darf den Wechsel nicht stören */
        }
        this.planeTeilGrenze();
        return;
      }
      if (!nacheinander) {
        this.planeTeilVersuch(Math.min(TEIL_WIEDER_MS, this.teilSeit + PTT_MAX_MS - Date.now()));
        return;
      }
      this.hoechstdauerErreicht();
    })().finally(() => {
      if (this.teilWechsel === lauf) this.teilWechsel = null;
    });
    this.teilWechsel = lauf;
  }

  /** Das Ende an der Höchstdauer — wie vor Ticket 2618. */
  private hoechstdauerErreicht(): void {
    this.beendeWachter();
    try {
      // Ticket 2518: auch ein Druck, dessen Wechsel ohne Mikro blieb.
      if (this.recorder?.state === "recording" || this.ohneLeitung) {
        this.recorder?.stop();
        // Pre.489: Modal-Callback feuern damit das UI weiß "Max-Dauer
        // erreicht, auto-send läuft jetzt" — vorher lief Modal-Timer
        // blind weiter obwohl recorder schon tot war.
        try {
          this.onAutoStop?.();
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * In einen neuen Rekorder auf DERSELBEN Leitung. (29.09.2026, Ticket
   * 2618; nachgebessert nach dem Prüfbefund) Liefert den fertigen Teil
   * (`teil` null, wenn er nichts trug) — oder null, wenn kein Wechsel ging.
   *
   * Nebeneinander (der Regelfall): der neue läuft an, WÄHREND der alte
   * weiter aufnimmt. Der alte endet erst, wenn der neue nachweislich
   * liefert (liefertInhalt). Liefert er nicht, fällt er weg, und der alte
   * nimmt unverändert weiter auf — kein Ton geht verloren.
   *
   * Nacheinander (der letzte Versuch, an der Höchstdauer): falls zwei
   * Rekorder auf einer Leitung nicht nebeneinander laufen, endet ERST der
   * alte (sein Teil ist fertig), DANN beginnt der neue. Am Schnitt kann ein
   * Augenblick fehlen — die Zeile nennt ihn (`luecke=`); ohne diesen
   * Versuch endete hier das ganze Diktat. Liefert auch er nicht, läuft der
   * Druck ohne Leitung weiter (`ohneLeitung`), und die Aufnahme bis hierher
   * ist sein Ende (stopAndGet) — die Karte sendet sie (onAutoStop).
   *
   * ⚠️ Prüfbefund 29.09.: vorher reichte `state === "recording"`. Ein
   *    Rekorder, der das meldet und nichts liefert, ließ den alten nach
   *    500 ms enden — alles danach fehlte, und das Senden meldete ok.
   * ⚠️ Kein getUserMedia, keine neue Wache: die Leitung, ihre Mitleser
   *    (Mitschrift, Pegel, Nachhören) und die Uhr des Drucks bleiben.
   */
  private async wechsleTeil(nacheinander: boolean): Promise<TeilWechsel | null> {
    const alt = this.recorder;
    const altTeile = this.chunks;
    if (
      this.nativ ||
      this.leitungsWechsel ||
      this.ohneLeitung ||
      !this.mediaStream ||
      alt?.state !== "recording"
    ) {
      return null;
    }
    const nr = this.druckNr;
    const art = nacheinander ? "nacheinander" : "neben";
    // Der letzte Versuch: kein Rekorder mehr, die Aufnahme bis hierher ist das Ende.
    const ohneNeuen = (): null => {
      if (nacheinander) {
        this.recorder = null;
        this.ohneLeitung = true;
      }
      return null;
    };
    let altZu = 0;
    if (nacheinander) {
      await stoppeUndWarte(alt);
      altZu = Date.now();
      if (this.druckNr !== nr) return null;
    }
    let neu: NeuerRekorder;
    try {
      neu = this.neuerRekorder();
    } catch (e) {
      meldeMikroAnlass("ptt-teil-fehler", `art=${art} ${fehlerKurz(e)}`);
      return ohneNeuen();
    }
    const liefert = await this.liefertInhalt(neu.teile, nr);
    if (this.druckNr !== nr) {
      // Abgebrochen, während der neue anlief: keiner nimmt mehr auf.
      stoppeStill(neu.rec);
      stoppeStill(alt);
      return null;
    }
    if (!liefert) {
      meldeMikroAnlass("ptt-teil-fehler", `art=${art} neu=${neu.rec.state} scheiben=${neu.teile.length}`);
      stoppeStill(neu.rec);
      return ohneNeuen();
    }
    this.trageEin(neu);
    // ERST liefert der neue, DANN endet der alte — sein Schlussstück gehört
    // noch zu SEINEM Teil.
    if (!nacheinander) await stoppeUndWarte(alt);
    return {
      teil: altTeile.length ? new Blob(altTeile, { type: this.mimeType }) : null,
      art,
      luecke: nacheinander ? neu.seit - altZu : 0,
    };
  }

  /**
   * Liefert der neue Rekorder wirklich? (Ticket 2618, Prüfbefund 29.09.)
   * TEIL_BELEG_SCHEIBEN Zeitscheiben mit Inhalt binnen TEIL_ANLAUF_MS —
   * und nicht vor TEIL_UEBERLAPPUNG_MS, so lange nehmen beide gemeinsam
   * auf. `teile` ist seine Liste; leere Scheiben landen gar nicht darin.
   */
  private async liefertInhalt(teile: readonly Blob[], nr: number): Promise<boolean> {
    const ab = Date.now();
    for (;;) {
      await new Promise<void>((r) => setTimeout(r, TEIL_PRUEF_TAKT_MS));
      if (this.druckNr !== nr) return false;
      const seit = Date.now() - ab;
      if (teile.length >= TEIL_BELEG_SCHEIBEN && seit >= TEIL_UEBERLAPPUNG_MS) return true;
      if (seit >= TEIL_ANLAUF_MS) return false;
    }
  }

  /**
   * Recorder und Wache auf `this.mediaStream` starten — derselbe Aufbau beim
   * ersten Start und beim Leitungswechsel (Ticket 2518). Wirft, wenn der
   * Recorder nicht entsteht; aufräumen muss der Aufrufer.
   */
  private starteRecorder(): void {
    if (!this.mediaStream) throw new Error("kein-strom");
    this.recorderBytes = 0;
    this.recorderTeile = 0;
    recBereitZuruecksetzen(); // 19.10.2026: der Bereit-Ton wartet auf den Start DIESER Aufnahme (rec-bereit.ts)
    this.trageEin(this.neuerRekorder());
    // Ticket 2494: ab jetzt mitlesen, ob die Leitung ueberhaupt traegt.
    starteStromWache(this.mediaStream);
    this.starteWachter();
  }

  /** 30.10.2026: der Aufnahme-Wächter beginnt (neu) auf der jetzigen Leitung. */
  private starteWachter(): void {
    // Der Druck endet schon (Loslassen, Verwerfen): kein neuer Wächter.
    if (this.wachterGesperrt) return;
    const spur = this.mediaStream?.getAudioTracks()[0];
    if (spur && !this.wachterSpuren.has(spur)) {
      this.wachterSpuren.add(spur);
      const ist = (): boolean => this.mediaStream?.getAudioTracks()[0] === spur;
      try {
        spur.addEventListener("ended", () => ist() && this.wachter.ereignis("ended"));
        spur.addEventListener("mute", () => ist() && this.wachter.ereignis("mute"));
        spur.addEventListener("unmute", () => ist() && this.wachter.ereignis("unmute"));
      } catch {
        /* eine Spur ohne Ereignisse (Attrappe): dann bleiben die Lebenszeichen */
      }
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.beiSichtbar);
      document.addEventListener("visibilitychange", this.beiSichtbar);
    }
    this.wachter.start();
  }

  /** 30.10.2026: gewolltes Ende — Zeitgeber weg, nie ein Alarm danach. */
  private beendeWachter(): void {
    this.wachterGesperrt = true;
    this.wachter.ende();
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", this.beiSichtbar);
  }

  /**
   * Ein laufender Rekorder auf `this.mediaStream` — beim Start, beim
   * Leitungswechsel und an der Teilgrenze (Ticket 2618). Der Aufnahme
   * gehört er erst mit `trageEin` — an der Teilgrenze erst, wenn er
   * liefert. Wirft er, bleibt alles, wie es war.
   *
   * ⚠️ Jeder Rekorder sammelt in SEINE Liste. Bis Ticket 2618 gab es nur
   *    `this.chunks`, und ein abgelöster Rekorder durfte nichts mehr
   *    hineinlegen — an der Teilgrenze gehört sein Schlussstück aber noch
   *    zu SEINEM Teil. Gezählt wird weiter nur der eingetragene.
   */
  private neuerRekorder(): NeuerRekorder {
    if (!this.mediaStream) throw new Error("kein-strom");
    const rec = new MediaRecorder(this.mediaStream, {
      mimeType: this.mimeType,
      audioBitsPerSecond: 64_000,
    });
    const teile: Blob[] = [];
    rec.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) teile.push(ev.data);
      // Teile eines verworfenen Recorders zählen nicht (Leitungswechsel).
      if (this.recorder !== rec) return;
      // 27.10.2026: solange aufgenommen wird, verfällt ein gewählter Chip „An: <Entwickler>“ nicht (lib/empfaenger-wahl.ts).
      empfaengerWahl.frische();
      // Ticket 2518: die echte Menge — auch leere Teile zählen mit (0).
      this.recorderTeile++;
      this.recorderBytes += ev.data?.size ?? 0;
    };
    // 30.10.2026: ein Fehler oder Stopp, den niemand ausgelöst hat, ist ein Ausfall (ptt-wachter.ts).
    // Nur der Rekorder des Drucks zählt; gewollte Stopps rufen vorher `wachter.ende()`.
    try {
      rec.addEventListener("error", () => {
        if (this.recorder === rec) this.wachter.ereignis("error");
      });
      rec.addEventListener("stop", () => {
        if (this.recorder === rec) this.wachter.ereignis("stop");
      });
    } catch {
      /* ein Rekorder ohne Ereignisse (Attrappe) */
    }
    // 19.10.2026: das Mikro nimmt wirklich auf (`start` kommt vom Rekorder, nicht vom Aufruf) — der Bereit-Ton wartet darauf.
    try {
      rec.addEventListener("start", () => meldeRecBereit());
    } catch {
      /* ein Rekorder ohne Ereignisse (Attrappe): dann kommt das Zeichen nie, und der Ton bleibt aus */
    }
    // Pre.284 Bug #199: timeslice=250ms — sonst feuert ondataavailable
    // erst beim stop() und bei kurzen/abrupten Stops sind chunks leer.
    rec.start(250);
    return { rec, teile, seit: Date.now() };
  }

  /**
   * Ab jetzt DER Rekorder des Drucks: seine Liste ist die Aufnahme, und das
   * Nachhören beginnt eine neue Datei (`recorderNr`, Ticket 2672).
   */
  private trageEin(n: NeuerRekorder): void {
    this.recorder = n.rec;
    this.chunks = n.teile;
    this.recorderNr++;
  }

  /**
   * Ein frisch geholtes Mikro als Strom der Sprechtaste eintragen — beim
   * kalten Start und beim Leitungswechsel (Ticket 2518) derselbe Weg.
   */
  private trageFrischesMikroEin(): void {
    if (!this.mediaStream) return;
    // Siehe aktiverPttStrom: die Vorwaermung muss wissen, welchen Strom
    // sie NICHT schliessen darf.
    aktiverPttStrom = this.mediaStream;
    // Explizit aktivieren — eine parallel gelandete Vorwaermung koennte die
    // Tracks stummgeschaltet haben (Single-Flight, Reihenfolge der .then).
    this.mediaStream.getTracks().forEach((t) => (t.enabled = true));
    warmStream = this.mediaStream;
    /*
     * ⚠️ 22.09.2026 — HIER FEHLTE DIE UHR, UND DAMIT GRIFF DIE DECKE NIE.
     *
     * `warmUpMic` prueft `warmSeit` gegen WARM_DECKE_MS und schliesst den
     * Strom, wenn er zu lange offen ist. Uebernimmt aber ein PTT-Druck den
     * Strom (hier und beim Aufraeumen), blieb `warmSeit` auf dem alten
     * Wert oder null — eine Kette von Nachwaermungen konnte das Mikro
     * unbegrenzt offen halten. Im Browser, wo jetzt nach jeder Antwort
     * gewaermt wird, waere das der Normalfall geworden.
     */
    warmSeit = Date.now();
  }

  /** Beide Prüfungen im Druck anhalten — die tote und die stumme. */
  private stoppeLeitungsPruefung(): void {
    if (this.leitungsPruefung) clearTimeout(this.leitungsPruefung);
    this.leitungsPruefung = null;
    if (this.stummPruefung) clearTimeout(this.stummPruefung);
    this.stummPruefung = null;
  }

  private planeStummPruefung(): void {
    this.stummPruefung = setTimeout(() => this.pruefeStummImDruck(), STUMM_PRUEFEN_NACH_MS);
  }

  /** Was die Wache und der Recorder gerade über die Leitung wissen. */
  private lageImDruck(): LeitungImDruck {
    return {
      ...stromWacheStand(),
      bytes: this.recorderBytes,
      weg: this.weg,
      recorderLaeuft: this.recorder?.state === "recording",
    };
  }

  /**
   * Die Prüfung auf eine STUMME QUELLE, STUMM_PRUEFEN_NACH_MS nach dem Start
   * einer Leitung. (26.09.2026, Ticket 2643) Die Regel steht in
   * strom-pegel.ts (istStummeQuelle, stummImDruck).
   *
   * Konto 23 nahm seit 24.09. achtmal nur digitale Stille auf, bis 42 s —
   * jedes Diktat verloren. Getauscht wird über denselben Weg wie die tote
   * warme Leitung; die verworfene trug nur Stille, es geht nichts verloren.
   * Bleibt auch die neue stumm, wird nicht noch einmal getauscht: dann sagt
   * es das Aufnahmefenster.
   */
  private pruefeStummImDruck(): void {
    this.stummPruefung = null;
    // Ein laufender Tausch plant nach dem Start seine eigene Nachprüfung.
    if (this.leitungsWechsel) return;
    const lage = this.lageImDruck();
    const was = stummImDruck(lage);
    if (!was) return;
    meldeMikroAnlass(
      was === "tauschen" ? "ptt-stumme-quelle" : "ptt-stumme-quelle-bleibt",
      `${befundTeil(lage)} ${spurDiagnose(this.mediaStream)}`,
    );
    if (was === "tauschen") this.starteWechsel("stumm");
    else stummHoerer.melde();
  }

  /**
   * Die EINE Prüfung eines warmen Drucks, WECHSEL_NACH_MS nach dem Start.
   * (24.09.2026, Ticket 2518) Die Regel steht in strom-pegel.ts.
   */
  private pruefeLeitungImDruck(): void {
    this.leitungsPruefung = null;
    if (!sollLeitungWechseln(this.lageImDruck())) return;
    this.starteWechsel("tot");
  }

  /** Den Tausch anstoßen — das Loslassen wartet auf ihn (`leitungsWechsel`). */
  private starteWechsel(grund: "tot" | "stumm"): void {
    const lauf: Promise<void> = this.wechsleLeitung(
      Date.now() - this.startTs,
      grund,
    ).finally(() => {
      if (this.leitungsWechsel === lauf) this.leitungsWechsel = null;
    });
    this.leitungsWechsel = lauf;
  }

  /**
   * Die tote warme Leitung MITTEN im Druck gegen eine frische tauschen.
   * (24.09.2026, Ticket 2518)
   *
   * Heute 12:03: App nach 1,6 h aus dem Hintergrund, 1,4 s später ein Druck
   * auf die warme Leitung. 38 Sekunden gesprochen — `spitze=0`, 5 Bytes,
   * alles verloren. Der nächste Druck ging den kalten Weg und lief sauber.
   * Die Freigabe nach dem Druck (pre.3184) rettet erst den ZWEITEN.
   *
   * ⚠️ Erst alles Tote weg (Recorder, Wache, Leitung), DANN ein frisches
   *    Mikro wie der kalte Weg: nie zwei getUserMedia nebeneinander — auf
   *    iOS die bekannte Abriss-Ursache. Wer mitliest (Live-Diktat, Pegel,
   *    Pausen-Erkennung), zieht über `hoereAufLeitungsWechsel` um.
   *
   * ⚠️ `releaseWarmMic` hilft hier nicht: es kehrt während der Aufnahme
   *    wortlos zurück (`recordingActive`). Darum wird die tote Leitung von
   *    Hand freigegeben — und `recordingActive` bleibt stehen, der Druck
   *    läuft ja weiter (Ticket 2524).
   *
   * 26.09.2026 (Ticket 2643): derselbe Weg für die STUMME Quelle (`grund`),
   * warm wie kalt. Die Zeilen tragen `grund=tot|stumm`.
   */
  private async wechsleLeitung(nachMs: number, grund: "tot" | "stumm"): Promise<void> {
    // Ticket 2643: keine alte Prüfung trifft die neue Leitung — sie bekommt
    // unten ihre eigene.
    this.stoppeLeitungsPruefung();
    const nr = this.druckNr;
    const tot = this.recorder;
    const alt = this.mediaStream;
    const teile = this.recorderTeile;
    const bytes = this.recorderBytes;
    // 1. Den toten Recorder anhalten; seine Teile zählen nicht mehr für die
    //    Aufnahme — seinen Schlussblock misst die Zeile trotzdem (unten).
    this.recorder = null;
    const rest = tot ? stoppeUndZaehleRest(tot) : Promise.resolve(0);
    this.chunks = [];
    this.recorderBytes = 0;
    this.recorderTeile = 0;
    // 2. Die Wache beenden — das stoppt auch ihre Kopie (Ticket 2501).
    const wache = beendeStromWache();
    /*
     * Nachprüfung 24.09.2026: Jede Zeile des Wechsels sagt, was die
     * verworfene Leitung lieferte — `rest=` ab LEER_UNTER_BYTES heißt
     * `fehlalarm=ja` (siehe verworfenMessung). Geschrieben wird, sobald
     * der Schlussblock da ist; der Wechsel wartet darauf nicht.
     */
    const meldeWechsel = (anlass: string, zusatz: string): void => {
      void rest.then((r) => {
        const messung = verworfenMessung({
          teile,
          bytes,
          rest: r,
          kontextErst: wache.kontextErst ?? "-",
          kontextLief: wache.kontextLief ?? false,
          stumm: grund === "stumm",
        });
        meldeMikroAnlass(anlass, `${zusatz} grund=${grund} ${messung}`);
      });
    };
    // 3. Die tote Leitung freigeben, samt Merkern.
    this.mediaStream = null;
    if (alt) {
      for (const t of alt.getTracks()) t.stop();
      if (warmStream === alt) warmStream = null;
      if (aktiverPttStrom === alt) aktiverPttStrom = null;
      warmSeit = 0;
      merkeWarmGrund(grund === "stumm" ? "quelle-stumm-im-druck" : "leitung-tot-im-druck");
    }
    // 4. Ein frisches Mikro — wie der kalte Weg.
    await pausiereTagesaufnahmeFuerMikro();
    /*
     * Nachprüfung 24.09.2026: Während der Pause (bis 400 ms) abgebrochen?
     * Dann kein getUserMedia mehr — cleanup() hat die Tagesaufnahme schon
     * wieder angeworfen, und ein Web-Mikro daneben ist genau die bekannte
     * Abriss-Ursache (#519/570). Ein neuer Druck holt sein eigenes.
     */
    if (this.druckNr !== nr) {
      meldeWechsel("ptt-leitung-wechsel-abgebrochen", "in=pause");
      return;
    }
    const gumAb = Date.now();
    let neu: MediaStream;
    try {
      neu = await acquireRawMic();
    } catch (e) {
      meldeWechsel("ptt-leitung-wechsel-fehler", fehlerKurz(e));
      // Der Druck endet wie heute — beim Loslassen, ohne Ton.
      if (this.druckNr === nr) this.ohneLeitung = true;
      return;
    }
    const gumMs = Date.now() - gumAb;
    // 5. Inzwischen abgebrochen? Dann gehört der Strom niemandem.
    if (this.druckNr !== nr) {
      meldeWechsel("ptt-leitung-wechsel-abgebrochen", "in=gum");
      this.gibVerwaistenStromFrei(neu);
      return;
    }
    this.mediaStream = neu;
    this.trageFrischesMikroEin();
    // Kein zweiter Wechsel: beide Regeln tauschen nur auf „warm“ bzw. „kalt“.
    this.weg = "gewechselt";
    try {
      this.starteRecorder();
    } catch (e) {
      meldeWechsel("ptt-leitung-wechsel-fehler", `recorder:${fehlerKurz(e)}`);
      // Wie oben. Die frische Leitung räumt das Loslassen ab (cleanup).
      this.recorder = null;
      this.ohneLeitung = true;
      return;
    }
    meldeWechsel(
      "ptt-leitung-gewechselt",
      `nach=${Math.round(nachMs)}ms gum=${gumMs}ms mik=${geraeteName(neu)}`,
    );
    if (alt) meldeLeitungsWechsel(alt, neu);
    // Ticket 2643: EINE Nachprüfung der neuen Leitung. Stumm heißt dann
    // „sagen“, nie ein zweiter Tausch (weg ist jetzt „gewechselt“).
    this.planeStummPruefung();
  }

  /**
   * Ein Strom, den ein abgebrochener Wechsel noch geholt hat. (Ticket 2518)
   *
   * ⚠️ Nicht sofort stoppen: `acquireRawMic` ist Single-Flight. Hat ein
   *    neuer Druck dieselbe Anfrage geteilt, trägt er den Strom erst in
   *    seinem eigenen Fortsetzungsschritt ein — der läuft NACH diesem. Ein
   *    Takt später ist klar, ob ihn jemand übernommen hat.
   */
  private gibVerwaistenStromFrei(s: MediaStream): void {
    setTimeout(() => {
      if (s === this.mediaStream || s === aktiverPttStrom || s === warmStream) {
        return;
      }
      for (const t of s.getTracks()) t.stop();
      meldeWebMikro();
    }, 0);
  }

  /**
   * Stoppt die Aufnahme und liefert den fertigen Blob — oder `null` wenn
   * kein Ton im Puffer ist.
   */
  async stopAndGet(): Promise<Blob | null> {
    if (this.nativ) {
      const p = this.nativ;
      this.nativLaeuft = false;
      try {
        const r = await p.stop();
        this.cleanup();
        if (!r?.base64) return null;
        return base64ZuBlob(r.base64, r.mime || "audio/mp4");
      } catch {
        this.cleanup();
        return null;
      }
    }
    // Ticket 2518: nach dem Loslassen wird nichts mehr gewechselt — und ein
    // laufender Wechsel wird abgewartet: geliefert wird der Ton der NEUEN
    // Leitung (oder nichts, wenn sie nicht kam).
    this.beendeWachter();
    this.stoppeLeitungsPruefung();
    /*
     * 30.10.2026 (Prüfer K2): Ein Wechsel, der auf sein Mikro wartet
     * (acquireRawMic hat keine Frist), hielte das Loslassen ewig auf. Nach
     * WECHSEL_FRIST_MS gilt, was da ist; `cleanup()` zählt `druckNr` hoch, der
     * späte Wechsel gibt sein Mikro dann selbst wieder frei.
     */
    let wechselHaengt = false;
    if (this.leitungsWechsel) {
      wechselHaengt = !(await abwartenMitFrist(this.leitungsWechsel, WECHSEL_FRIST_MS));
    }
    if (wechselHaengt && !this.recorder && !this.ohneLeitung) {
      const vorl = this.vorlauf;
      this.cleanup();
      return vorl ? vorl.abschluss(null) : null;
    }
    // Ticket 2618: ein laufender Teilwechsel ebenso — sonst fehlte sein Teil.
    if (this.teilWechsel) await this.teilWechsel;
    // Ticket 2618: die früheren Teile — VOR cleanup(), das sie vergisst. Die
    // letzte Aufnahme trägt sie (DiktatVorlauf.abschluss).
    const vorlauf = this.vorlauf;
    const mitVorlauf = (letzter: Blob | null): Blob | null =>
      vorlauf ? vorlauf.abschluss(letzter) : letzter;
    // Der Wechsel fand kein Mikro: hier endet der Druck, ohne Ton — die App
    // sagt wie vor dem Wechsel „Mikrofon war noch nicht bereit“.
    // Ticket 2618: blieb der letzte Teilwechsel ohne neuen Rekorder, liegt
    // hier die Aufnahme bis dahin (wechsleTeil) — sie ist das Ende.
    if (this.ohneLeitung) {
      const bisher = this.chunks.length > 0 ? new Blob(this.chunks, { type: this.mimeType }) : null;
      this.cleanup();
      return mitVorlauf(bisher);
    }
    const r = this.recorder;
    if (!r) return mitVorlauf(null);
    return new Promise<Blob | null>((resolve) => {
      let fertig = false;
      let frist: ReturnType<typeof setTimeout> | null = null;
      const finish = (): void => {
        if (fertig) return;
        fertig = true;
        if (frist) clearTimeout(frist);
        // ⚠️ VOR cleanup(): danach ist der Strom weg und mit ihm die Frage,
        //    ob er je etwas geliefert hat.
        const tot = pruefeStromNachAufnahme(this.recorderBytes, this.weg, this.mediaStream);
        this.cleanup();
        // ⚠️ NACH cleanup(): vorher steht `recordingActive`, und die
        //    Freigabe kehrt wortlos zurueck (Tickets 2518/2519).
        if (tot) releaseWarmMic("stiller-strom");
        // Finger war oft schon lange unten, bevor der Recorder lief.
        // Die Recorder-Uhr unter 300 ms darf den Ton dann nicht verwerfen.
        if (this.chunks.length === 0) {
          resolve(mitVorlauf(null));
          return;
        }
        resolve(mitVorlauf(new Blob(this.chunks, { type: this.mimeType })));
      };
      /*
       * 30.10.2026 (Prüfer M1): Steht der Rekorder schon (Ausfall des Wächters:
       * `stop`, Spur zu Ende), kommt nie ein weiteres `stop` — das Senden hinge,
       * cleanup() liefe nie, das Mikro bliebe offen. Dann zählt sofort, was in
       * den Teilen liegt. Sonst wie überall (stoppeUndWarte): `stop` abwarten,
       * mit Frist als Rückfall.
       */
      // Prüfer K1: Chromium liefert nach `inactive` noch ein letztes Stück (+ `stop`): kurz darauf warten.
      const fristMs = r.state === "inactive" ? NACH_STOPP_FRIST_MS : STOPP_FRIST_MS;
      r.onstop = finish;
      frist = setTimeout(finish, fristMs);
      if (r.state === "inactive") return;
      try {
        r.stop();
      } catch {
        finish();
      }
    });
  }

  cancel(): void {
    this.beendeWachter();
    this.cancelRequested = true;
    this.abbruchNr++;
    if (this.nativ) {
      void this.nativ.cancel().catch(() => undefined);
      this.nativLaeuft = false;
      this.cleanup();
      return;
    }
    try {
      if (this.recorder?.state === "recording") this.recorder.stop();
    } catch {
      /* ignore */
    }
    this.cleanup();
  }

  /**
   * Pegel der nativen Aufnahme (0…1) — oder null, wenn sie nicht nativ läuft.
   * Die Karte füttert damit ihre Balken (`pushLevel`), weil es im nativen
   * Weg keine Tonspur für den Analyser gibt.
   */
  pegelNativ(): Promise<number | null> | null {
    if (!this.nativ || !this.nativLaeuft) return null;
    // pre.3623: ein Fehler der Brücke ist keine Messung — null, nicht 0. Die
    // Stille-Regel zählt ihn weiter wie 0 (mic-visualizer.ts, pushLevel), nur
    // die Anzeige sagt danach „Pegel nicht messbar“.
    return this.nativ.pegel().catch(() => null);
  }

  /** Kam die letzte Aufnahme aus der nativen Engine? */
  warNativ(): boolean {
    return this.letzterWarNativ;
  }

  /*
   * ⚠️⚠️ 12.09.2026 — Ticket 2230, Martin: *„Wie kann kein Ton am Mikro
   * sein, dass es aber trotzdem aufgenommen wird? Das ist doch Bullshit."*
   *
   * Die „kein Pegel"-Warnung im Modal liest von einem EIGENEN AudioContext
   * + AnalyserNode auf einer TRACK-KOPIE (ptt-modal.ts, rein lesend, siehe
   * dort) — nicht von der echten Aufnahme. Bei einem kalten Start oder
   * Bluetooth-Profilwechsel (A2DP→HFP, siehe 05.09.-Befund) kann diese
   * Kopie spaeter echte Werte liefern als die Original-Spur, die
   * tatsaechlich aufgezeichnet wird — die Warnung zeigt dann Stille,
   * waehrend der Ton laengst ankommt.
   *
   * Diese Methode fragt die ECHTE Aufnahme, nicht die Kopie: Sind bereits
   * Bytes im Recorder (`ondataavailable` hat gefeuert) oder laeuft die
   * native Engine? Dann hat die Aufnahme Ton, ganz gleich, was der
   * separate Analyser gerade misst.
   *
   * Ticket 2618: ein neuer Teil gehört der Aufnahme erst, wenn er schon
   * geliefert hat (wechsleTeil) — seine Liste ist also nie leer.
   */
  hatEchtenTon(): boolean {
    if (this.nativ) return this.nativLaeuft;
    return this.chunks.length > 0;
  }

  /**
   * Der laufende Teil — für die Karte. (Ticket 2618, Nachbesserung 29.09.)
   *
   * `teilt`: nur der Web-Weg mit Erkenner und Leitung geht an der
   * Höchstdauer in einen neuen Teil über; nativ endet er weiter dort.
   * `seitMs` zählt ab dem Beginn DIESES Teils: die Karte kündigt danach
   * den nächsten an — und warnt wie vor Ticket 2618, wenn er 30 s vor der
   * Höchstdauer noch nicht begonnen hat. `null` ohne Teile-Uhr (nativ,
   * bevor der Rekorder läuft): dann zählt die Karte selbst.
   */
  teilLage(): TeilLage | null {
    if (this.teilSeit === 0) return null;
    return {
      teilt: this.erkenneTeil !== null && !this.nativ && !this.nativLaeuft && !this.ohneLeitung,
      nr: (this.vorlauf?.anzahl ?? 0) + 1,
      seitMs: Date.now() - this.teilSeit,
    };
  }

  /**
   * Trug die laufende Leitung schon einmal echten Ton? (26.09.2026 spät,
   * Ticket 2643) Aus der Wache: dieselbe Gleitkomma-Spitze, an der die
   * stumme Quelle erkannt wurde — nur echte digitale Stille ist dort 0.
   *
   * ⚠️ Nicht `hatEchtenTon()`: das zählt Bytes des Rekorders, und der
   *    Encoder schreibt auch reine Stille in Bytes — auf dem Web-Weg ist es
   *    nach 250 ms immer wahr, auch bei Konto 23s −91 dB.
   * ⚠️ VOR dem Stopp fragen: `cleanup()` beendet die Wache.
   * Nativ gibt es keine Wache und keine stumme Quelle — dort immer ja.
   */
  quelleGabTon(): boolean {
    if (this.nativ) return true;
    return stromWacheStand().spitze > 0;
  }

  isRecording(): boolean {
    if (this.nativ) return this.nativLaeuft;
    // Ticket 2518: während des Leitungswechsels läuft der Druck weiter —
    // sonst hielte das Loslassen ihn für „noch nicht gestartet“. Ebenso,
    // wenn der Wechsel ohne Mikro blieb: bis zum Loslassen.
    if (this.leitungsWechsel || this.ohneLeitung) return true;
    return this.recorder?.state === "recording";
  }

  getMimeType(): string {
    return this.mimeType;
  }

  /**
   * Die bisher gelieferten Teile der laufenden Web-Aufnahme — für das
   * Nachhören (26.09.2026, Ticket 2672).
   *
   * Der Rekorder liefert ohnehin alle 250 ms ein Teil (timeslice, siehe
   * starteRecorder). Hier wird nur gelesen; die Aufnahme bleibt, wie sie ist.
   *
   * `nr` wechselt mit jedem neuen Rekorder (Leitungswechsel, Ticket 2518):
   * dann beginnt die Datei neu, mit eigenem Kopf. `null` auf dem nativen Weg
   * und ohne Rekorder.
   */
  teileBisher(): { nr: number; mime: string; teile: readonly Blob[] } | null {
    if (this.nativ || !this.recorder) return null;
    return { nr: this.recorderNr, mime: this.mimeType, teile: this.chunks };
  }

  /** Mikro-Track fuer VAD/Pegel-Analyse (Freihand-Modus #303). null wenn inaktiv. */
  /**
   * Die Tonspur fuer die Pegelanzeige — NICHT waehrend die native Aufnahme
   * laeuft. (Ticket 2442, 21.09.2026)
   *
   * ⚠️ Albert brach einen Zuruf ab, weil die Balken stumm blieben, obwohl
   *    der Ton beim Server ankam. Ursache: beim nativen Start bleibt ein
   *    warm gehaltener Web-Strom (`warmStream`) stehen. Die Karte fragt
   *    ZUERST nach dieser Spur und misst dann an einem Strom, den das
   *    native Mikro laengst stumm gestellt hat — die native Pegelabfrage
   *    (pegelNativ, alle 100 ms) kommt gar nicht erst dran.
   */
  getAudioTrack(): MediaStreamTrack | null {
    if (this.nativLaeuft) return null;
    return this.mediaStream?.getAudioTracks()[0] ?? null;
  }

  /** Vergangene Millisekunden seit start(). 0 wenn nicht (mehr) am laufen. */
  getElapsedMs(): number {
    // Ticket 2518: ohne diese Uhr hielte das Loslassen den Druck für einen
    // Tipp (0 ms) und rastete ein.
    if (!this.recorder && !this.nativ && !this.leitungsWechsel && !this.ohneLeitung) {
      return 0;
    }
    return Date.now() - this.startTs;
  }

  private cleanup(): void {
    /*
     * ⚠️⚠️ 22.09.2026, TICKET 2501 — DAS SICHERHEITSNETZ.
     *
     * Die Strom-Wache wurde nur in `finish()` beendet. Der Abbruch und der
     * native Weg gehen daran vorbei — und liessen die geklonte Tonspur
     * leben, also das Mikrofon offen.
     *
     * `cleanup()` ist die EINE Stelle, durch die jeder Ausgang geht
     * (Stopp, Abbruch, Fehler, nativ). Die Auswertung bleibt in finish(),
     * weil sie dort noch echte Zahlen hat; dieser Aufruf ist dann ein
     * Nichts-mehr-zu-tun und kostet nichts.
     */
    beendeStromWache();
    this.beendeWachter();
    // Ticket 2518: keine Prüfung und kein Wechsel überlebt das Ende des
    // Drucks. Ein Wechsel, der noch auf sein Mikro wartet, sieht die neue
    // Nummer und gibt es wieder frei.
    this.stoppeLeitungsPruefung();
    this.druckNr++;
    this.leitungsWechsel = null;
    // Ticket 2618: die Teile gehören diesem Druck. Beim Loslassen hat sie
    // stopAndGet vorher an die letzte Aufnahme gehängt; beim Abbruch gehen
    // sie mit — „Verwerfen" verwirft das ganze Diktat.
    this.teilWechsel = null;
    this.vorlauf = null;
    this.teilSeit = 0;
    this.ohneLeitung = false;
    this.nativ = null;
    this.nativLaeuft = false;
    // Die Sperre der Tonanlage fällt erst am Ende dieser Methode (Ticket 2524).
    recordingActive = false;
    // Am Ende von cleanup() steht fest, ob der Strom warm bleibt — erst dann.
    queueMicrotask(meldeWebMikro);
    // #519: fuers PTT pausierte Tagesaufnahme wieder anwerfen.
    void import("./native-recorder.js")
      .then((m) => m.resumeDayRecorderAfterMic("ptt"))
      .catch(() => undefined);
    if (this.autoStopTimer) {
      clearTimeout(this.autoStopTimer);
      this.autoStopTimer = null;
    }
    this.recorder = null;
    if (this.mediaStream) {
      // ⚠️ Der Merker gehoert hierher zurueck: sonst haelt er eine Referenz
      //    auf einen Strom, den es nicht mehr gibt, und der Vergleich in
      //    warmUpMic schuetzt den falschen.
      if (aktiverPttStrom === this.mediaStream) aktiverPttStrom = null;
      // Naechster Druck muss warm sein. 2026-08-18 stoppte die Tracks
      // sofort — jeder folgende PTT ging den kalten Weg (479–1897 ms),
      // und Loslassen traf ein Mikro, das noch nicht da war.
      /*
       * ⚠️⚠️ 22.09.2026 — DIESE ZEILE KOSTET 73 VON 149 BROWSER-STARTS.
       *
       * Gemessen an 322 Mikro-Starts seit dem 15.09.: im Browser ist KEIN
       * EINZIGER warm (0 von 149). Der haeufigste Grund traegt den Namen
       * `ptt-ende-alltag` — 73 Mal, und der naechste Druck kommt im Median
       * schon nach ZWOELF Sekunden, bei einem Warmfenster von 45.
       *
       * Die Bedingung fragt `playbackActive` — genau den Merker, der im
       * Browser nach der ersten Antwort FUER IMMER auf „spielt" stehen
       * bleibt (mit einer Sonde gemessen: 1x playback-start, 0x -end; die
       * Kette steht bei `spieltWirklich`). Also wird nach JEDEM Druck
       * freigegeben, obwohl weder eine Aufnahme laeuft noch etwas klingt.
       *
       * ⚠️ Und der Name log dabei: `ptt-ende-alltag` sagt „wegen der
       *    Tagesaufnahme", obwohl die Wiedergabe derselbe Zweig ist. Wer
       *    das Protokoll liest, sucht an der falschen Stelle — mir ist
       *    genau das heute frueh schon einmal passiert.
       */
      // 23.09.2026: und nicht, wenn das Wach-Wort das ruhende Mikro braucht.
      if (dayRecorderBusy || wachwortVorrang || spieltWirklich()) {
        if (this.mediaStream === warmStream) {
          releaseWarmMic(
            dayRecorderBusy
              ? "ptt-ende-alltag"
              : wachwortVorrang
                ? "ptt-ende-wachwort"
                : "ptt-ende-wiedergabe",
          );
        } else {
          for (const t of this.mediaStream.getTracks()) t.stop();
        }
      } else {
        this.mediaStream.getTracks().forEach((t) => {
          t.enabled = false;
        });
        warmStream = this.mediaStream;
        // Siehe oben: ohne die Uhr greift die Drei-Minuten-Decke nie.
        warmSeit = Date.now();
        if (warmStreamTimer) clearTimeout(warmStreamTimer);
        // Dasselbe Fenster wie beim Vorwaermen — es gab drei Stellen mit
        // derselben Zahl; zwei zu aendern und eine zu vergessen war nur
        // eine Frage der Zeit.
        warmStreamTimer = window.setTimeout(
          () => releaseWarmMic("warmup-timeout"),
          WARM_FENSTER_MS,
        );
      }
      this.mediaStream = null;
    }
    /*
     * ⚠️ 23.09.2026 (Ticket 2524) — DIE SPERRE FÄLLT ALS LETZTES.
     *
     * Während der Aufnahme hat die Tonanlage nichts umgeschaltet (12:57:20
     * kostete genau das 54 s Ton). Jetzt wendet sie EINMAL an, was dann gilt.
     * Das kann sofort `voice-v8:playback-start` senden, und darauf gibt diese
     * Datei das warme Mikro frei. Stünde die Zeile oben bei
     * `recordingActive = false`, träfe diese Freigabe einen Strom, über den
     * hier noch gar nicht entschieden ist — er könnte gestoppt und danach
     * trotzdem als warm eingetragen werden. So entscheidet erst das
     * Aufräumen, dann die Tonanlage.
     */
    setSeitenAufnahme(false);
  }
}

// Pre.483: Streaming-PTT. Statt fetch().json() einen SSE-Stream lesen und
// pro Event den richtigen Callback feuern. So kann der Caller den Bot-Text
// inkrementell anzeigen + parallel TTS für die ersten Sätze starten,
// während der Server noch weiter tippt.
export interface StreamPttCallbacks {
  /**
   * Sobald STT fertig ist — Transcript + audio_url verfügbar.
   * `info.endwortGeprueft` (26.09.2026 spät): der Server hat das Endwort,
   * das die Aufnahme beendete, schon selbst aus SEINEM Text genommen
   * (shared/sende-wort.ts, ohneAusgeloestesEndwort). Fehlt es, kennt der
   * Server die Angabe noch nicht (vor dem Neustart).
   */
  onTranscript: (
    transcript: string,
    audioUrl: string | null,
    info?: { endwortGeprueft: boolean },
  ) => void;
  /** Pro Bot-Token. Caller akkumuliert + chunkt für TTS. */
  onTextChunk: (chunk: string) => void;
  /** Tool-Call gestartet (mit Input). */
  onToolCall: (tool: string, callId: string, input: unknown) => void;
  /** 2026-07-13 (Martin): Text-Stream vorbei, Tool laeuft gleich —
   *  gepufferten Satz jetzt sprechen (nicht bis nach dem Tool warten). */
  onFlush?: () => void;
  /**
   * 06.10.2026 (Ticket 2738): der Text bis hier war ein Satz, den Vespera selbst
   * vor die Antwort gestellt hat (Warte-Brücke, Vorantwort) — die Antwort beginnt
   * danach neu (TtsQueue.neuerAnfang). Ein älterer Server schickt es nie.
   */
  onVorsatz?: () => void;
  /** Tool-Result da. */
  onToolResult: (
    callId: string,
    name: string,
    result: unknown,
    isError: boolean,
    /** 15.10.2026: die Anzeige des Servers (server/werkzeug-anzeige.ts), ungeprüft. */
    anzeige?: unknown,
  ) => void;
  /** Ticket 281: fruehes Modell-Event — Footer sofort korrekt. */
  onModel?: (effectiveModel: string) => void;
  /**
   * 26.09.2026: Der Server hat die Aufnahme angenommen — genau einmal, nie
   * bei Netz- oder HTTP-Fehler. Hier klingt die Quittung „gesendet"
   * (audio-feedback.ts, quittungFuerVersand).
   */
  onAngenommen?: () => void;
  /**
   * 29.09.2026, Ticket 2780: Der Server hat die Aufnahme verworfen —
   * „Verwerfen"/„Stopp" stand für sich am Ende SEINER Erkennung. Es geht
   * nichts hinaus; `text` ist das Diktat ohne den Befehl. Danach kommt
   * `onDone(true, "")`.
   */
  onVerworfen?: (v: { text: string; fuerSich: boolean }) => void;
  /**
   * 21.10.2026: Die Vollständigkeits-Bremse am ENDGÜLTIGEN Text des Servers (`done.unvollstaendig`, shared/satz-vollstaendig.ts
   * `stilleZuruecklegen`): die Stille sandte von selbst, der Satz ist offen. Es geht nichts hinaus; `text` gehört ins Eingabefeld.
   * Danach `onDone(true, "")`.
   */
  onUnvollstaendig?: (text: string) => void;
  /**
   * 19.10.2026: Der Rauschfilter des Servers hat den Text verworfen (`done.rauschen`, server/schmied-rauschen.ts). Es geht nichts
   * hinaus; `text` ist der erkannte Wortlaut (nur für Martins eigene Anzeige), `hinweis` die ehrliche Meldung. Danach `onDone(true, "")`.
   */
  onRauschen?: (r: { text: string; hinweis: string }) => void;
  /**
   * Ticket 2624 (30.09.2026): „Hey Vespera nur mit meiner Stimme“ — der Server
   * hat die Aufnahme verworfen, weil Stimmabdruck und Klang zusammen eindeutig
   * eine andere Stimme sagten (nie nach dem rohen Abdruck allein)
   * (`done.grund === "fremde-stimme"`, server/wachwort-stimme.ts). Es geht
   * nichts hinaus und nichts ins Gedächtnis; `text` ist die ruhige Auskunft
   * des Servers. Danach kommt `onDone(true, "")` — kein Fehler, kein
   * „Erneut senden“ (dieselbe fremde Stimme würde wieder verworfen).
   */
  onFremdeStimme?: (text: string) => void;
  /**
   * Ticket 2882 (01.10.2026): Dieselbe Aufnahme kam binnen Sekunden schon an
   * (zwei Seiten, zwei Browser, ein Gerät und sein Zwilling) und wird am
   * Server nur EINMAL beantwortet (`done.grund === "dublette"`,
   * server/ptt-dublette.ts). Die Aufnahme ist nicht verloren — die Antwort der
   * ersten steht im Verlauf. `text` ist die ruhige Auskunft des Servers.
   * Danach kommt `onDone(true, "")`: kein Fehler, kein „Erneut senden“
   * (dieselbe Aufnahme noch einmal zu senden wäre genau die Dublette).
   */
  onDublette?: (text: string) => void;
  /**
   * 22.10.2026 (Martin, 07:52: „Ich habe nur getippt“): Der Server hat Silbensalat aus Geräuschen verworfen
   * (`done.grund === "salat"`, server/ptt-salat.ts). Nichts geht hinaus; `text` ist der kurze Hinweis (nie ein Wort der Rede).
   * Die Tondatei kommt vorher über `onTondatei` (Prüfauflage 22.10.: rettbar). Danach `onDone(true, "")` — kein Fehler, kein
   * „Erneut senden“ (dieselbe Aufnahme würde wieder verworfen); gerettet wird über „Zurückholen“ (app.ts).
   */
  onSalat?: (text: string) => void;
  /**
   * Ticket 3085 (11.10.2026): Der Server hat den Text eines LANGEN Diktats schon gespeichert (Ereignis `gespeichert`,
   * server/ptt-fruehspeichern.ts) und der Zug ist dennoch gescheitert (`done.gespeichert`) oder der Strom riss. `satz` ist die
   * Auskunft „gespeichert, nicht noch einmal sprechen“ — sie ersetzt „sag es bitte noch einmal“. Danach kommt wie sonst
   * `onDone(false, …)`; die Seite zeigt den Satz statt der Fehlermeldung.
   */
  onGespeichert?: (satz: string) => void;
  /**
   * 12.10.2026 (Bahn „fortsetzung-anhaengen“): Der Server hat die Angabe `fortsetzung` GEPRÜFT und angenommen (Ereignis `fortsetzung`) —
   * erst dann zeichnet die Seite das Etikett „setzt die vorige Nachricht fort“. `abgebrochen`: die Antwort der Vorrunde wurde abgebrochen.
   * Lehnt er sie ab, kommt das Ereignis nicht, und die Nachricht steht wie jede andere da.
   */
  onFortsetzung?: (abgebrochen: boolean) => void;
  /**
   * Ticket 2634 (30.09.2026): Der Strom ist verstummt (nach einer Rückkehr aus
   * dem Hintergrund oder nach 60 s Stille), aber die Antwort zu diesem Zug
   * steht schon im Verlauf des Servers. Der Strom ist beendet; die Seite holt
   * sich den Verlauf und schließt die Karte. NICHTS wird neu gesendet. Danach
   * kommt `onDone(true, "")`.
   */
  onAusVerlauf?: () => void;
  /**
   * Ticket 2618 (29.09.2026): Der Server nennt am Ende die abgelegte
   * Tondatei (`audio_url` im `done`) — auch ohne Text (gesperrt, nichts
   * gehört, Erkennung gescheitert). Der Teil eines langen Diktats merkt sie
   * sich (lib/diktat-teile.ts).
   */
  onTondatei?: (url: string) => void;
  /**
   * 29.09.2026, Ticket 2790: Am Zugende wartet eine Fragen-Runde auf seine
   * Antwort (tools/tickets.ts, rundeWartet). Kommt vor `onDone`.
   */
  onRunde?: (wartet: boolean) => void;
  /**
   * 07.10.2026, Ticket 3048: Jevs Meldung über das Mikro nach der Antwort
   * (`{ stufe: "zu" | "kurz" | "offen", grund, jev }`, roh — gelesen wird sie
   * in frontend/lib/jev-mikro.ts). Kommt vor `onDone`.
   */
  onMikro?: (roh: unknown) => void;
  /** Ticket #458: Server schlaegt ein thematisch passenderes Projekt vor. */
  onProjectSuggest?: (s: {
    projectId: string;
    name: string;
    score: number;
  }) => void;
  /** Pipeline fertig — text ist der gesamte gestreamte Bot-Text. */
  onDone: (
    ok: boolean,
    text: string,
    error?: string,
    effectiveModel?: string,
    /** 12.10.2026: Martin hat die Antwort gestoppt (Server: `done.gestoppt`) — sie ist eine Teilantwort. */
    gestoppt?: boolean,
  ) => void;
}

/**
 * Ticket 2624 (30.09.2026): Wodurch wurde diese Aufnahme geöffnet — hat es das
 * Wach-Wort getan? Aus der Angabe `via`, die `startPttRecording` je Aufnahme
 * setzt (`pttTiming.extra.via`). NUR „wachwort“ ergibt das Feld; Knopf,
 * Aktionstaste, Freihand, „nach Frage“ und „nach Antwort“ nie — dort hält oder
 * öffnet Martin selbst, und die Regel „nur meine Stimme“ gilt dort nicht.
 */
export function wachwortAusloeser(via: string | undefined): "wachwort" | undefined {
  return via === "wachwort" ? "wachwort" : undefined;
}

export async function streamPtt(
  blob: Blob,
  mimeType: string,
  cb: StreamPttCallbacks,
  /**
   * 2026-08-28 (Martin: "ein Mikrofonzeichen"): Der fertige Satz, falls er
   * schon WAEHREND des Sprechens entstanden ist. Die Datei geht immer mit,
   * sie ist die abspielbare Aufnahme in der Blase.
   *
   * ⚠️ 24.09.2026 (Ticket 2536): Hier stand bis heute, der Server
   *    ueberspringe mit diesem Text die Erkennung. Das gilt seit 04.09.
   *    nicht mehr: der Server erkennt die Tondatei IMMER selbst und nimmt
   *    diesen Text nur als Rueckfall, wenn seine Erkennung leer bleibt
   *    (server/voice-command.ts, `liveAlsRueckfall`).
   */
  liveText = "",
  /**
   * 24.09.2026 (Ticket 2536): Woher `liveText` stammt. „geraet" = der
   * Wach-Wort-Lauscher der App hat ihn erkannt, keine Soniox-Echtzeit — der
   * Server bucht ihn darum nicht als solche (voice-command.ts, textVomGeraet).
   */
  textQuelle?: "geraet",
  /**
   * 26.09.2026 spät (dritte Runde):
   *   `endwort` — welcher Befehl die Aufnahme beendete; der Server nimmt
   *   ihn aus seinem Text (Anzeige = Modell = Verlauf).
   *   `ziel` — „/api/voice/erkennen": derselbe Weg bis zur fertigen
   *   Erkennung, aber ohne Modellzug (ins Textfeld, Zurückholen).
   */
  extra: {
    endwort?: EndwortAngabe | null;
    ziel?: "/api/voice/command-stream" | "/api/voice/erkennen";
    /**
     * 29.09.2026, Ticket 2780: endete das Diktat nach Stille
     * (audio-feedback.ts, darfAmServerVerwerfen)? Nur dann darf der
     * Server es per „Verwerfen"/„Stopp" am Ende verwerfen — nach einem
     * Knopfdruck nie.
     */
    vonSelbst?: boolean;
    /**
     * 21.10.2026 (Vorfall 17:25:05): die Vollständigkeits-Bremse der Karte hielt an, weil die LIVE-Mitschrift offen endete.
     * Nur mit `vonSelbst`: der Server entscheidet am ENDGÜLTIGEN Text (shared/satz-vollstaendig.ts `stilleZuruecklegen`).
     */
    liveOffen?: boolean;
    /**
     * 29.09.2026, Ticket 2618: der Teil eines langen Diktats (nur mit
     * `/api/voice/erkennen`) — der Server legt seinen Ton ab wie beim
     * Senden, auch gesperrt oder ohne Text.
     */
    teil?: boolean;
    /**
     * 30.09.2026, Ticket 2624: das Mikro, das nach „Hey Vespera“ von selbst
     * aufging (`startPttHandsFree("wachwort")`). Nur dann schickt die Seite
     * das Feld `ausloeser=wachwort`; der Server wendet dort „Hey Vespera nur
     * mit meiner Stimme“ ebenso an wie auf den Satz in einem Atemzug
     * (`textQuelle`). Die Sprechtaste trägt es nie. Siehe `wachwortAusloeser`.
     */
    ausloeser?: "wachwort";
    /**
     * 12.10.2026 (Bahn live-primaer): der Stand der Live-Mitschrift (kam der Abschluss, riss die Leitung,
     * wie viel Ton kam an). Geht nur mit, wenn auch ihr Text mitgeht und er von der Soniox-Echtzeit
     * stammt (nicht vom Gerät). Der Server entscheidet darauf, ob er die Datei-Erkennung abwarten muss.
     */
    liveStand?: LiveStand | null;
    /**
     * 12.10.2026 (Bahn „fortsetzung-anhaengen“): die Kennung dieses Uploads, von der Seite vorgegeben — die nächste Aufnahme nennt sie als
     * `vorherId`, wenn sie eine Fortsetzung ist (frontend/lib/fortsetzung.ts). Ohne Angabe erzeugt `streamPtt` sie selbst, wie bisher.
     */
    anfrageKennung?: string;
    /**
     * 12.10.2026: diese Aufnahme beginnt binnen 12 s nach einem Auto-Senden — die Angabe geht als Feld `fortsetzung` mit (JSON, nur Zahlen
     * und feste Wörter). Der Server prüft sie (server/fortsetzung.ts) und ignoriert, was nicht stimmt.
     */
    fortsetzung?: FortsetzungAngabe;
  } = {},
): Promise<void> {
  const ext = mimeType.includes("mp4") ? "mp4" : "webm";
  // #123 / pre.1020: Netz-Blips beim Hochladen überleben. Der POST trägt die
  // GANZE Aufnahme — schlägt ER fehl, hat der Server noch NICHTS verarbeitet →
  // gefahrlos wiederholbar (3 Versuche, 1,5s/3s Backoff). Bricht erst der
  // SSE-Stream DANACH ab, wird NICHT automatisch neu gesendet (der Bot hätte
  // sonst doppelt geantwortet) — diesen Fall fängt der Caller mit dem
  // „Erneut senden"-Angebot ab.
  // FIX (lange Aufnahme): FormData je Versuch FRISCH bauen — eine bereits an
  // fetch übergebene kann beim Wiederholen leer ankommen (= „erneut senden"
  // greift nicht). Plus Upload-Timeout, damit ein hängender Upload nicht ewig
  // blockiert, sondern als Netzfehler neu versucht wird. 500 zählt jetzt als
  // (transient) wiederholbar.
  const RETRYABLE_STATUS = new Set([500, 502, 503, 504]);
  const UPLOAD_TIMEOUT_MS = 180000;
  let res: Response | null = null;
  let lastErr = "";
  /*
   * Ticket #1884 (2026-08-23, Martin): "Verbindung unterbrochen, bitte erneut
   * senden" — waehrend im Hintergrund die Aktion laengst lief.
   *
   * Die Schleife darunter wiederholt den Upload bis zu DREIMAL, von selbst.
   * Bricht der Server ab, NACHDEM er gehandelt hat (Rechnung angelegt, Mail
   * geschickt), tut der zweite Versuch es erneut — ohne dass jemand etwas
   * drueckt.
   *
   * Diese Kennung wird EINMAL pro Aufnahme erzeugt und bei jedem Versuch
   * mitgeschickt. Der Server fuehrt pro Kennung genau einmal aus und liefert
   * der Wiederholung das gespeicherte Ergebnis nach. Sie steht bewusst VOR der
   * Schleife — eine Kennung je Versuch waere gar keine.
   */
  const anfrageKennung = extra.anfrageKennung ?? neueAnfrageKennung();
  // Welle 0: die Kennung der Aufnahme JETZT festhalten — eine neue Aufnahme darf sie nicht mitten im Upload ersetzen.
  const az = aufnahmeKennung();
  /*
   * Ticket 2618 (29.09.2026): Trägt diese Aufnahme frühere Teile eines
   * langen Diktats, reist ihr Text mit (`vorText`) — der Server stellt ihn
   * vor seinen Text: EINE Nachricht — und ihre Tondateien am Server
   * (`vorTon`), die er an die Nachricht hängt. Hier, im EINEN Hochladeweg,
   * gilt das für jedes Ende (Senden, Erneut senden, ins Textfeld,
   * Zurückholen). Ist ein Teil auch im zweiten Anlauf ohne Text und liegt
   * sein Ton nicht am Server, geht nichts hinaus — wie bei einem
   * Netzfehler: der Aufrufer bietet „Erneut senden" mit derselben Aufnahme
   * an, die ihre Teile weiter trägt.
   */
  let vorText = "";
  let vorTon: string[] = [];
  const vorlauf = vorlaufVon(blob);
  if (vorlauf) {
    try {
      ({ text: vorText, ton: vorTon } = await vorlauf.sammle());
    } catch (err) {
      meldeMikroAnlass("ptt-teil-nicht-erkannt", `grund=${err instanceof TeilNichtErkannt ? err.grund : "?"}`);
      cb.onDone(false, "", "teil-nicht-erkannt");
      return;
    }
  }
  /*
   * 16.10.2026 (Dreier-Modus Stufe 1, lib/empfaenger-wahl.ts): Martins Wahl „An: Schmied“ reist als Feld `an` mit — EINMAL je
   * Aufnahme gelesen (alle Versuche tragen dasselbe), nie bei „nur erkennen“ (ins Textfeld, Teile eines Diktats): dort geht noch
   * keine Nachricht ab, die Wahl bleibt für sie stehen. Zurück auf Vespera erst, wenn der Server die Aufnahme angenommen hat.
   */
  const anFeld = extra.ziel === "/api/voice/erkennen" ? undefined : empfaengerWahl.anFeld();
  for (let attempt = 1; attempt <= 3; attempt++) {
    const fd = new FormData();
    fd.append("audio", blob, `ptt.${ext}`);
    if (anFeld) fd.append("an", anFeld);
    fd.append("anfrageKennung", anfrageKennung);
    if (az) fd.append("az", az); // Welle 0: Kennung der Aufnahme, nur für die Journalzeilen
    if (vorText) fd.append("vorText", vorText);
    if (vorTon.length > 0) fd.append("vorTon", JSON.stringify(vorTon));
    if (extra.teil) fd.append("teil", "1");
    // Ticket 2624: nur die vom Wach-Wort geöffnete Aufnahme trägt das Feld — genau dieser Wert.
    if (extra.ausloeser === "wachwort") fd.append("ausloeser", "wachwort");
    if (liveText) fd.append("text", liveText);
    if (liveText && textQuelle) fd.append("textQuelle", textQuelle);
    /*
     * Bahn live-primaer (Prüfung 12.10.2026, Punkt B1): ein Diktat in TEILEN schickt keinen Stand. Die Mitschrift läuft
     * über alle Teile weiter, ihr Text enthielte die früheren Teile noch einmal — er passt nicht zu dieser letzten Datei
     * (der Server lehnt `vorText` mit Live-Text ohnehin ab, Grund `teile`; das hier spart nur das Feld).
     */
    if (liveText && !textQuelle && extra.liveStand && !vorlauf) fd.append("liveStand", liveStandAlsFeld(extra.liveStand));
    /*
     * 16.10.2026 (Bahn „salat-stille“, server/ptt-salat.ts): hörte die Live-Mitschrift die Aufnahme und fand KEIN Wort,
     * sagt das der Server — er verwirft dann ein erfundenes Wort aus einer leisen Aufnahme. Nur zum Verwerfen; der
     * Server prüft die Werte selbst. Ohne Stand (nativer Weg, Erneut senden, Abriss) fehlt das Feld, alles wie vorher.
     */
    if (!liveText && !textQuelle && extra.liveStand && !vorlauf && extra.liveStand.endeLetztesWortMs === null) {
      fd.append("liveLeer", liveStandAlsFeld(extra.liveStand));
    }
    if (extra.endwort) fd.append("endwort", JSON.stringify(extra.endwort));
    if (extra.vonSelbst) fd.append("vonSelbst", "1");
    if (extra.vonSelbst && extra.liveOffen) fd.append("liveOffen", "1");
    // 12.10.2026: die Fortsetzung — das Feld nur, wenn die Seite sie meldet; sonst bleibt die Anfrage, wie sie war.
    if (extra.fortsetzung) fd.append("fortsetzung", JSON.stringify(extra.fortsetzung));
    // 13.10.2026 (Tickets 3232/3230): läuft Musik, sagt es das Feld; bei Stille fehlt es (Anfrage wie bisher).
    const wiedergabe = wiedergabeJetzt();
    if (wiedergabe !== "stille") fd.append("wiedergabe", wiedergabe);
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), UPLOAD_TIMEOUT_MS);
    try {
      res = await fetch(extra.ziel ?? "/api/voice/command-stream", {
        method: "POST",
        body: fd,
        credentials: "same-origin",
        // Ticket 2796: damit nur DIESE Seite das Echo verwirft, nicht der
        // Browser am Rechner (shared/geraet-kennung.ts).
        headers: { Accept: "text/event-stream", [GERAET_KOPF]: EIGENES_GERAET },
        signal: ctrl.signal,
      });
    } catch (err) {
      lastErr = err instanceof Error ? err.message : String(err);
      res = null;
    } finally {
      clearTimeout(to);
    }
    if (res && !RETRYABLE_STATUS.has(res.status)) break;
    if (res) lastErr = `http-${res.status}`;
    if (attempt < 3) {
      console.warn(
        `[ptt] upload attempt ${attempt} failed (${lastErr}) — retry …`,
      );
      await new Promise((r) => setTimeout(r, attempt * 1500));
    }
  }
  if (!res) {
    cb.onDone(false, "", lastErr || "network");
    return;
  }
  if (!res.ok || !res.body) {
    cb.onDone(false, "", `http-${res.status}`);
    return;
  }
  /*
   * 26.09.2026 — Ab hier ist die Aufnahme beim Server: Seine Kopfzeilen
   * kommen erst, wenn er den Upload ganz gelesen hat (voice-command.ts,
   * flushHeaders nach upload.single). Der erste Text kommt erst nach der
   * Erkennung — auf ihn zu warten, hiesse die Quittung um Sekunden zu
   * verspäten.
   */
  try {
    cb.onAngenommen?.();
  } catch (e) {
    console.error("[ptt-stream] callback angenommen", e);
  }
  empfaengerWahl.verbraucht(anFeld);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let collectedText = "";
  // 2026-07-07 (Ticket 283): Render-Fehler in UI-Callbacks (WKWebView-
  // TypeErrors wie "undefined is not an object") duerfen weder den Stream
  // killen noch als falscher "Sprachnachricht-Fehler" auftauchen — und
  // onDone darf NIE doppelt feuern (Erfolgs-onDone warf -> catch rief es
  // nochmal mit ok=false).
  let doneCalled = false;
  let stalled = false;
  // Ticket 3085: der Server hat das lange Diktat schon abgelegt (Ereignis `gespeichert`).
  let textGespeichert = false;
  // Welle A (15.10.2026, lib/zug-ende.ts): dieser Strom besitzt seinen Zug — das Mikro entscheidet erst nach seinem Ende.
  // „Nur erkennen“ (ins Textfeld) ist kein Zug.
  const zugKennung = extra.ziel === "/api/voice/erkennen" ? 0 : zugBeginnt(az);
  const fireDone: typeof cb.onDone = (ok, text, error, model, gestoppt) => {
    if (doneCalled) return;
    doneCalled = true;
    // VOR onDone: der flusht die Schlange, und ihr Ende fragt, ob der Zug fertig ist.
    zugZuEnde(zugKennung);
    wache.stopp(); // `wache` steht gleich darunter; fireDone läuft erst, wenn der Strom gelesen wird
    // `gestoppt` nur, wenn es wahr ist: alle anderen Enden rufen onDone unverändert mit vier Angaben.
    if (gestoppt) cb.onDone(ok, text, error, model, true);
    else cb.onDone(ok, text, error, model);
  };
  const safe = (label: string, fn: () => void): void => {
    try {
      fn();
    } catch (e) {
      console.error("[ptt-stream] callback", label, e);
    }
  };
  /*
   * 2026-07-23 (Vorfall "SENDE ... eingefroren"): Inaktivitaets-Watchdog — 60 s
   * echte Stille = Verbindung halbtot -> abbrechen statt ewig haengen.
   *
   * 30.09.2026 (Ticket 2634): Die Wache (lib/ptt-haenger.ts) ist der Nachfolger.
   * Sie haelt die 60 s im Vordergrund, fragt nach einer Rueckkehr aus dem
   * Hintergrund sofort den Verlauf und gilt dann nur kurz (Begruendung dort).
   * Der Server pingt alle 15 s (nicht ~25 s, wie hier bisher stand).
   *
   * „nur erkennen" (`/api/voice/erkennen`) hat keinen Verlauf: dort gibt es nichts
   * zu finden, nur die kurze Frist.
   */
  const anker = ankerAus(res, Date.now());
  const wache = starteSendeWache({
    ...(extra.ziel === "/api/voice/erkennen" ? {} : { frageVerlauf: () => fragVerlauf(anker) }),
    beiAntwort: () => {
      if (doneCalled) return;
      safe("aus-verlauf", () => cb.onAusVerlauf?.());
      fireDone(true, "");
      void reader.cancel().catch(() => undefined);
    },
    beiTot: () => {
      stalled = true;
      void reader.cancel().catch(() => undefined);
    },
  });
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      wache.aktivitaet();
      buf += decoder.decode(value, { stream: true });
      // SSE-Frames sind durch `\n\n` getrennt
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let evName = "message";
        let dataLine = "";
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) evName = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLine = line.slice(5).trim();
        }
        if (!dataLine) continue;
        let payload: Record<string, unknown>;
        try {
          payload = JSON.parse(dataLine) as Record<string, unknown>;
        } catch {
          continue;
        }
        switch (evName) {
          case "transcript": {
            const roh = String(payload.transcript ?? "");
            // Ticket 2618, Übergang: ein Server vor dem Neustart kennt
            // `vorText` nicht — dann stehen die früheren Teile wenigstens in
            // der Blase und im Eingabefeld.
            const transcript =
              vorText && payload.vorTextGenommen !== true ? verbindeTeile(vorText, roh) : roh;
            safe("transcript", () =>
              cb.onTranscript(
                transcript,
                typeof payload.audio_url === "string"
                  ? payload.audio_url
                  : null,
                { endwortGeprueft: payload.endwortGeprueft === true },
              ),
            );
            break;
          }
          case "gespeichert":
            textGespeichert = true;
            break;
          case "fortsetzung":
            if (payload.gilt === true) {
              const abgebrochen = payload.abgebrochen === true;
              safe("fortsetzung", () => cb.onFortsetzung?.(abgebrochen));
            }
            break;
          case "text":
            if (typeof payload.chunk === "string") {
              collectedText += payload.chunk;
              const chunk = payload.chunk;
              safe("text", () => cb.onTextChunk(chunk));
            }
            break;
          case "flush":
            safe("flush", () => cb.onFlush?.());
            break;
          case "vorsatz":
            safe("vorsatz", () => cb.onVorsatz?.());
            break;
          // ⚠️ `tool_progress` wird hier NICHT verarbeitet: der Sprechtasten-Weg zeigt keine Zwischenstände von Werkzeugen. Der Server
          //    schickt sie mit (server/voice-command.ts), aber nur der getippte Chat (frontend/lib/chat-http.ts) zeigt sie — auch die
          //    Stufenkarte der Musik (Ticket 3074). Wer sie hier braucht, baut den Fall dazu; sie steht nicht still hinter einem Schalter.
          case "tool_call":
            safe("tool_call", () =>
              cb.onToolCall(
                String(payload.tool ?? ""),
                String(payload.callId ?? ""),
                payload.input,
              ),
            );
            break;
          case "tool_result":
            safe("tool_result", () =>
              cb.onToolResult(
                String(payload.callId ?? ""),
                String(payload.name ?? ""),
                payload.result,
                Boolean(payload.isError),
                payload.anzeige,
              ),
            );
            break;
          case "model":
            if (typeof payload.effectiveModel === "string") {
              const m = payload.effectiveModel;
              safe("model", () => cb.onModel?.(m));
            }
            break;
          case "runde":
            safe("runde", () => cb.onRunde?.(payload.wartet === true));
            break;
          case "mikro":
            safe("mikro", () => cb.onMikro?.(payload));
            break;
          case "schmied":
            // 15.10.2026: Martin hat den Schmied angesprochen — Vespera antwortet nicht, das Mikro wartet auf ihn (lib/schmied-warten.ts).
            safe("schmied", () => meldeSchmiedAngesprochen(payload));
            break;
          case "zimmermann":
          case "hueter":
            // 17.10.2026: Martin hat den Zimmermann (oder den Hüter) angesprochen — Vespera antwortet nicht, nichts wartet.
            safe(evName, () => meldeZusatzAngesprochen(evName));
            break;
          case "schmied-folgt":
            // 16.10.2026: Martin sprach beide an, der Schmied antwortet nach Vespera — das Mikro wartet dann auf ihn.
            safe("schmied-folgt", () => merkeSchmiedFolgt(zugKennung));
            break;
          case "zug":
            // Welle A: was der Zugvertrag meldet (Arbeits- oder Plauderzug, echte Rückfrage) — vor `done`.
            safe("zug", () => merkeZug(zugKennung, payload));
            break;
          case "project_suggest":
            if (payload.projectId && payload.name) {
              safe("project_suggest", () =>
                cb.onProjectSuggest?.({
                  projectId: String(payload.projectId),
                  name: String(payload.name),
                  score: Number(payload.score ?? 0),
                }),
              );
            }
            break;
          case "error":
            // 2026-07-24 (Martin: "zweimal 'wird transkribiert', nichts geht
            // raus"): Der Server schickt bei leerem/kaputtem Upload ein
            // `error`-Event und schliesst. Ohne diesen Zweig fiel es durch,
            // der Stream endete "sauber" -> fireDone(true) -> Platzhalter und
            // SENDE-Overlay blieben ewig haengen. Jetzt echter Fehlschlag.
            fireDone(
              false,
              collectedText,
              typeof payload.message === "string"
                ? payload.message
                : typeof payload.error === "string"
                  ? payload.error
                  : "Serverfehler",
            );
            return;
          case "done":
            /*
             * Ticket #1884 (2026-08-23): Der Server erkennt jetzt, wenn
             * dieselbe Aufnahme ein zweites Mal ankommt — sei es durch den
             * Auto-Retry oben oder durch "erneut senden".
             *
             * "bereitsUnterwegs" ist KEIN Fehlschlag: Der erste Versuch
             * arbeitet noch. Genau das war Martins Klage — die Meldung
             * behauptete Fehlschlag und riet zum erneuten Senden, waehrend im
             * Hintergrund laengst gehandelt wurde.
             */
            if (payload.bereitsUnterwegs) {
              fireDone(
                true,
                collectedText,
                "Wird schon bearbeitet — die Antwort kommt gleich.",
              );
              return;
            }
            // Ticket 2780: verworfen am Server — kein Zug, nichts ging hinaus.
            if (payload.verworfen && typeof payload.verworfen === "object") {
              const v = payload.verworfen as { text?: unknown; fuerSich?: unknown };
              safe("verworfen", () =>
                cb.onVerworfen?.({
                  text: typeof v.text === "string" ? v.text : "",
                  fuerSich: v.fuerSich === true,
                }),
              );
              fireDone(true, "");
              return;
            }
            // 21.10.2026: Bremse am Endtext — offener Satz nach Stille, nichts ging hinaus; der Text kommt ins Eingabefeld.
            if (payload.unvollstaendig && typeof payload.unvollstaendig === "object") {
              const u = payload.unvollstaendig as { text?: unknown };
              safe("unvollstaendig", () => cb.onUnvollstaendig?.(typeof u.text === "string" ? u.text : ""));
              fireDone(true, "");
              return;
            }
            /*
             * 19.10.2026: der Rauschfilter des Servers hat verworfen (server/schmied-rauschen.ts). Nie still: die Seite zeigt den
             * erkannten Text samt „Trotzdem senden“ (lib/rausch-karte.ts). Es geht nichts hinaus; danach `onDone(true, "")`.
             */
            if (payload.rauschen && typeof payload.rauschen === "object") {
              const r = payload.rauschen as { text?: unknown };
              const text = typeof r.text === "string" ? r.text : "";
              const hinweis = typeof payload.error === "string" ? payload.error : "";
              // Die Tondatei geht nicht verloren (wie im allgemeinen Zweig unten).
              if (typeof payload.audio_url === "string" && payload.audio_url) {
                const url = payload.audio_url;
                safe("tondatei", () => cb.onTondatei?.(url));
              }
              safe("rauschen",() => cb.onRauschen?.({ text, hinweis }));
              fireDone(true, "");
              return;
            }
            /*
             * Ticket 2624: eine fremde Stimme nach „Hey Vespera“, am Server
             * verworfen. Der benannte Grund entscheidet, nie der Text (der
             * Server meldet `ok: false`, die Seite zeigt es aber als stille
             * Zeile, nicht als Fehler und ohne „Erneut senden“).
             */
            if (payload.grund === "fremde-stimme") {
              const hinweis = typeof payload.error === "string" ? payload.error : "";
              safe("fremde-stimme", () => cb.onFremdeStimme?.(hinweis));
              fireDone(true, "");
              return;
            }
            // 22.10.2026: Silbensalat aus Geräuschen — nur ein stiller Hinweis, nie als Fehler.
            if (payload.grund === "salat") {
              const hinweis = typeof payload.error === "string" ? payload.error : "";
              // Prüfauflage 22.10.: die Tondatei geht nicht verloren (wie im allgemeinen Zweig unten) — ein Fehlgriff bleibt rettbar.
              if (typeof payload.audio_url === "string" && payload.audio_url) {
                const url = payload.audio_url;
                safe("tondatei", () => cb.onTondatei?.(url));
              }
              safe("salat", () => cb.onSalat?.(hinweis));
              fireDone(true, "");
              return;
            }
            // Ticket 2882: dieselbe Aufnahme ist schon im Zug — ruhig, nie als Fehler.
            if (payload.grund === "dublette") {
              const hinweis = typeof payload.error === "string" ? payload.error : "";
              safe("dublette", () => cb.onDublette?.(hinweis));
              fireDone(true, "");
              return;
            }
            // Ticket 2618: die abgelegte Tondatei — auch ohne Text.
            if (typeof payload.audio_url === "string" && payload.audio_url) {
              const url = payload.audio_url;
              safe("tondatei", () => cb.onTondatei?.(url));
            }
            // Ticket 3085: das Modell ist gescheitert, das lange Diktat aber gespeichert — die Seite sagt es, statt „noch einmal“.
            if (payload.gespeichert === true && payload.ok !== true && typeof payload.error === "string") {
              const satz = payload.error;
              safe("gespeichert", () => cb.onGespeichert?.(satz));
            }
            fireDone(
              Boolean(payload.ok),
              collectedText || String(payload.text ?? ""),
              typeof payload.error === "string" ? payload.error : undefined,
              typeof payload.effectiveModel === "string"
                ? payload.effectiveModel
                : undefined,
              payload.gestoppt === true,
            );
            return;
        }
      }
    }
    // Stream beendet ohne `done`-Event: nach Watchdog-Abbruch ehrlich
    // scheitern (Karte zeigt Fehler + "Erneut senden"), sonst success.
    if (stalled) {
      /*
       * ⚠️ Kein „bitte erneut senden" mehr. Wer hier ankommt, hat die
       * Aufnahme in aller Regel bereits VOLLSTAENDIG hochgeladen — der
       * Wachhund schlaegt an, weil der Strom still wird, nicht weil die
       * Arbeit ausblieb. Ein zweites Senden machte dieselbe Arbeit noch
       * einmal. Wie es dem Nutzer gesagt wird, entscheidet die Ebene
       * darueber (app.ts); hier steht nur, WAS passiert ist.
       */
      if (textGespeichert) safe("gespeichert", () => cb.onGespeichert?.(UNTERBROCHEN_TEXT_GESPEICHERT));
      fireDone(false, collectedText, "Verbindung unterbrochen");
    } else {
      fireDone(true, collectedText);
    }
  } catch (err) {
    /*
     * ⚠️ Nachprüfung 01.10.2026 (Ticket 2634): Ab hier ist die Aufnahme beim Server (onAngenommen). Ein
     * Fehler beim Lesen — auf iOS beim Aufwachen typisch („Load failed“, „The network connection was
     * lost.“) — ist darum kein Sendefehler, sondern ein abgerissener Strom: die Arbeit läuft am Server
     * weiter. Früher kam hier die rohe Fehlermeldung, die Ebene darüber bot „Erneut senden“ an, und ein Tipp
     * schickte dieselbe Aufnahme mit NEUER Kennung ein zweites Mal. Jetzt: erst eine letzte Nachfrage
     * beim Verlauf (steht die Antwort da, ist der Zug erledigt), sonst dieselbe ehrliche Auskunft wie beim
     * stillen Abriss.
     */
    console.warn("[ptt-stream] Strom nach der Annahme gerissen:", err instanceof Error ? err.message : String(err));
    if (!doneCalled && extra.ziel !== "/api/voice/erkennen") {
      let gefunden: boolean | null = null;
      try {
        gefunden = await fragVerlauf(anker);
      } catch {
        gefunden = null;
      }
      if (gefunden === true && !doneCalled) {
        safe("aus-verlauf", () => cb.onAusVerlauf?.());
        fireDone(true, "");
        return;
      }
    }
    // „nur erkennen“ hat keinen Verlauf und keine Antwort, die nachkommt: dort bleibt die rohe Meldung.
    if (textGespeichert && !doneCalled) safe("gespeichert", () => cb.onGespeichert?.(UNTERBROCHEN_TEXT_GESPEICHERT));
    fireDone(
      false,
      collectedText,
      extra.ziel === "/api/voice/erkennen" ? (err instanceof Error ? err.message : String(err)) : "Verbindung unterbrochen",
    );
  }
}
